/**
 * Bella cart drawer: delivery region, date & gift sheets.
 * Uses capture-phase delegation on #mini-cart so clicks work after every section re-render.
 */
(function () {
  const formControllers = new WeakMap();

  function getMiniCartHost() {
    return document.getElementById('mini-cart');
  }

  function getForm() {
    const host = getMiniCartHost();
    return host && host.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart');
  }

  function getCartDrawerSectionId() {
    return document.querySelector('mini-cart')?.dataset?.sectionId || 'mini-cart';
  }

  function sectionInnerFromHtml(html, selector) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector || '.shopify-section');
    return node ? node.innerHTML : '';
  }

  function updateCartIconSections(parsed) {
    if (!parsed || !parsed.sections) return;
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      const el = document.getElementById(id);
      const block = parsed.sections[id];
      if (el && block) {
        el.innerHTML = sectionInnerFromHtml(block, '.shopify-section');
      }
    });
  }

  /** Collect product ids + handles currently in the basket. */
  function getCartProductKeys(host) {
    const ids = new Set();
    const handles = new Set();
    if (!host) return { ids, handles };

    host.querySelectorAll('cart-items [data-product-id]').forEach(function (el) {
      const id = el.getAttribute('data-product-id');
      if (id) ids.add(String(id));
    });

    host.querySelectorAll('cart-items a[href*="/products/"]').forEach(function (a) {
      const href = a.getAttribute('href') || '';
      const m = href.match(/\/products\/([^/?#]+)/);
      if (m && m[1]) handles.add(decodeURIComponent(m[1]));
    });

    return { ids, handles };
  }

  function productHandleFromHref(href) {
    if (!href) return '';
    const m = String(href).match(/\/products\/([^/?#]+)/);
    return m && m[1] ? decodeURIComponent(m[1]) : '';
  }

  /** Remove upsell cards for products already in the basket. */
  function pruneUpsellProductsInCart(host) {
    host = host || getMiniCartHost();
    if (!host) return;
    const keys = getCartProductKeys(host);
    if (!keys.ids.size && !keys.handles.size) return;

    let removed = false;
    host.querySelectorAll('.bella-cart__recs-item').forEach(function (li) {
      const id = li.getAttribute('data-product-id');
      if (id && keys.ids.has(String(id))) {
        li.remove();
        removed = true;
        return;
      }
      const link = li.querySelector('a[href*="/products/"]');
      const handle = productHandleFromHref(link && link.getAttribute('href'));
      if (handle && keys.handles.has(handle)) {
        li.remove();
        removed = true;
      }
    });

    const recs = host.querySelector('cart-recommendations');
    if (recs && window.BellaCartRecs) {
      if (recs.dataset.url) {
        window.BellaCartRecs.setCached(recs.dataset.url, recs.innerHTML);
      }
      if (typeof window.BellaCartRecs.onPruned === 'function') {
        window.BellaCartRecs.onPruned(recs);
      }
    }
    return removed;
  }

  // Shared for cart-drawer.js / cart-recommendations.js
  window.BellaCartPruneUpsell = pruneUpsellProductsInCart;

  /** Full drawer HTML replace — use for add-to-cart / line-item changes, not Bella attribute patches. */
  function renderCartSections(parsed) {
    const host = getMiniCartHost();
    if (!parsed || !parsed.sections || !host) return;

    const sid = getCartDrawerSectionId();
    const miniHtml = parsed.sections[sid];
    if (!miniHtml) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Bella cart] Missing sections[' + sid + ']. Keys:', parsed.sections ? Object.keys(parsed.sections) : []);
      }
      return;
    }

    const prevRecs = host.querySelector('cart-recommendations');
    const prevTrack = prevRecs && prevRecs.querySelector('.bella-cart__recs-track');
    const prevScroll = prevTrack ? prevTrack.scrollLeft : 0;
    const prevRecsHtml = prevRecs && prevRecs.innerHTML.trim() ? prevRecs.innerHTML : '';

    host.innerHTML = sectionInnerFromHtml(miniHtml, '.shopify-section');

    const mini = document.querySelector('mini-cart');
    if (mini) mini.dataset.sectionLoaded = 'true';

    const nextRecs = host.querySelector('cart-recommendations');
    const drawerAlreadyOpen = !!(
      document.querySelector('cart-drawer details[open]')
    );

    // Prefer fresh Liquid HTML (already excludes in-cart products).
    // Only reuse previous upsell markup when the new section has no cards yet
    // (async recommendations fetch still pending).
    if (nextRecs) {
      const nextHasItems = !!nextRecs.querySelector('.bella-cart__recs-item');
      if (!nextHasItems && prevRecsHtml) {
        nextRecs.innerHTML = prevRecsHtml;
        nextRecs.dataset.loaded = 'true';
        delete nextRecs.dataset.loading;
      } else if (nextHasItems) {
        nextRecs.dataset.loaded = 'true';
        delete nextRecs.dataset.loading;
        if (window.BellaCartRecs && nextRecs.dataset.url) {
          window.BellaCartRecs.setCached(nextRecs.dataset.url, nextRecs.innerHTML);
        }
      }
      const track = nextRecs.querySelector('.bella-cart__recs-track');
      if (track && prevScroll) track.scrollLeft = prevScroll;
      if (drawerAlreadyOpen) {
        if (window.BellaCartRecs) window.BellaCartRecs.stripAnimate(nextRecs);
        else nextRecs.removeAttribute('data-animate');
      }
    }

    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    updateCartIconSections(parsed);
    pruneUpsellProductsInCart(host);
  }

  /** Suppress host MutationObserver refresh while we patch the drawer in place. */
  let bellaQuietDomDepth = 0;
  function withBellaQuietDom(fn) {
    bellaQuietDomDepth += 1;
    try {
      return fn();
    } finally {
      bellaQuietDomDepth -= 1;
    }
  }

  /** Persist delivery date / gift / region without re-rendering the drawer (avoids jitter & animation replay). */
  function syncBellaCartAfterAttributeUpdate(parsed) {
    const form = getForm();
    if (!form) return;
    if (parsed && typeof parsed.total_price === 'number') {
      form.dataset.subtotalCents = String(parsed.total_price);
    }
    const ctrl = formControllers.get(form);
    if (ctrl) {
      // Never collapse/rebuild pickers mid date-save (blink / addon refresh loop).
      ctrl.updateFooterTotals({
        preserveDeliveryPickers: !!(ctrl._dateSaveUiTimer || ctrl._dateSyncBusy || isBellaCartMergeLocked()),
      });
    }
  }

  function cartUpdateBody(extra, opts) {
    opts = opts || {};
    const payload = { ...extra };
    if (!opts.attributesOnly) {
      const sid = getCartDrawerSectionId();
      payload.sections = [sid, 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
      payload.sections_url = window.location.pathname;
    }
    return JSON.stringify(payload);
  }

  const rules = window.BellaDeliveryRules || {};
  const startOfDay = rules.startOfDay || function (d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const addDays = rules.addDays || function (d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const isoDate = rules.isoDate || function (d) {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  const parseISODate = rules.parseISODate || function (s) {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const getMinSelectableDate = function (region, context) {
    const ctx = context === 'pdp' ? 'pdp' : 'cart';
    if (rules.getMinSelectableDate) return rules.getMinSelectableDate(region, ctx);
    if (ctx === 'pdp' && rules.getPdpMinSelectableDate) return rules.getPdpMinSelectableDate(region);
    if (rules.getCartMinSelectableDate) return rules.getCartMinSelectableDate(region);
    return startOfDay(new Date());
  };
  const isBeforeMinSelectable = function (date, region, context) {
    const ctx = context === 'pdp' ? 'pdp' : 'cart';
    if (rules.isBeforeMinSelectable) return rules.isBeforeMinSelectable(date, region, ctx);
    return startOfDay(date).getTime() < getMinSelectableDate(region, ctx).getTime();
  };

  function formatDisplayDate(iso, strings) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) return '';

    const today = startOfDay(new Date());
    const tmr = startOfDay(addDays(new Date(), 1));
    const cur = startOfDay(dt);

    if (cur.getTime() === today.getTime()) return strings.today;
    if (cur.getTime() === tmr.getTime()) return strings.tomorrow;

    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  function formatPdpDateTitle(iso, strings) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) return '';

    const today = startOfDay(new Date());
    const tmr = startOfDay(addDays(new Date(), 1));
    const cur = startOfDay(dt);
    const formatted = dt.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

    if (cur.getTime() === today.getTime()) return strings.today + ', ' + formatted;
    if (cur.getTime() === tmr.getTime()) return strings.tomorrow + ', ' + formatted;
    return formatted;
  }

  function findBellaSheet(root, which) {
    if (!root || !which) return null;
    return (
      root.querySelector('[data-bella-sheet="' + which + '"]') ||
      root.querySelector('#bella-sheet-' + which)
    );
  }

  function isPdpDeliveryRoot(root) {
    return !!(root && root.hasAttribute('data-bella-pdp-delivery'));
  }

  /** Matches `checkmark-line` in snippets/icon.liquid — used for instant stepper updates. */
  var BellaStepCheckSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-checkmark-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5 11-12"></path></svg>';

  function monthLabel(d, locale) {
    return d.toLocaleDateString(locale || undefined, { month: 'long', year: 'numeric' });
  }

  function getBrowserTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  /** Long date + weekday for Shopify order "Additional details" (matches admin display). */
  function formatAdminOrderDateParts(iso) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) {
      return { date: '', day: '' };
    }
    const date = dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const day = dt.toLocaleDateString('en-US', { weekday: 'long' });
    return { date, day };
  }

  /** Parse admin "Delivery Date" (e.g. May 22, 2026) back to ISO for the calendar. */
  function parseAdminDeliveryDate(str) {
    if (!str || typeof str !== 'string') return '';
    const trimmed = str.trim();
    // Avoid `new Date(string)` UTC parsing which shifts the day in UAE (UTC+4).
    const match = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
    if (match) {
      const months = {
        january: 0,
        february: 1,
        march: 2,
        april: 3,
        may: 4,
        june: 5,
        july: 6,
        august: 7,
        september: 8,
        october: 9,
        november: 10,
        december: 11,
      };
      const mi = months[match[1].toLowerCase()];
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      if (mi != null && day && year) {
        return isoDate(new Date(year, mi, day));
      }
    }
    const dt = new Date(trimmed + 'T12:00:00');
    if (Number.isNaN(dt.getTime())) return '';
    return isoDate(dt);
  }

  const BellaLegacyAttributeKeys = [
    'delivery-region',
    'delivery-date',
    'Delivery Method',
    'Delivery area',
    'bella-gift-note',
    'Gift Note',
  ];

  /** Whole-dirham amounts omit trailing .00 (matches bella-money-amount.liquid). */
  function bellaFormatAmount(amountCents) {
    const cents = parseInt(amountCents, 10) || 0;
    if (cents % 100 === 0) {
      return String(cents / 100);
    }
    if (typeof theme !== 'undefined' && theme.Currency) {
      return theme.Currency.formatMoney(cents, '{{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  /** Subtotal / delivery / checkout: prefix + amount only (no trailing ISO like AED). */
  function bellaMoneyMarkup(amountCents, form, wrap) {
    const prefix =
      form && form.dataset && form.dataset.bellaMoneyPrefix ? form.dataset.bellaMoneyPrefix : 'Dhs.';
    const safePrefix = String(prefix)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
    const amount = bellaFormatAmount(amountCents);
    const core = '<span class="bella-money__prefix">' + safePrefix + '</span><bdi>' + amount + '</bdi>';
    if (wrap === 'core') return core;
    if (wrap === 'price') return '<span class="price bella-money">' + core + '</span>';
    return '<span class="bella-money">' + core + '</span>';
  }

  /**
   * Cart attributes for order "Additional details" — one field each; gift message uses cart.note instead.
   * @param {HTMLFormElement} form
   */
  function buildBellaCartAttributesPayload(form) {
    const region = form.dataset.deliveryRegion || 'dubai';
    const iso = form.dataset.deliveryDate || '';
    const dateFromCart = form.dataset.bellaDateSource === 'cart' && !!iso;
    const deliveryRegionLabel = region === 'other' ? 'Other Emirates' : 'Dubai';
    const parts = dateFromCart ? formatAdminOrderDateParts(iso) : { date: '', day: '' };
    const attrs = {
      'Delivery region': deliveryRegionLabel,
      'Delivery Date': parts.date,
      'Delivery Day': parts.day,
      'Customer TimeZone': getBrowserTimeZone(),
      'delivery-date': dateFromCart ? iso : '',
      'bella-date-source': dateFromCart ? 'cart' : '',
    };
    BellaLegacyAttributeKeys.forEach(function (k) {
      if (k === 'delivery-date') return;
      attrs[k] = '';
    });
    return attrs;
  }

  function getDeliveryPropLabel(form) {
    return (form && form.dataset.deliveryPropLabel) || 'Delivery Date';
  }

  function getGiftPropLabel(form) {
    return (form && form.dataset.giftPropLabel) || 'Gift note';
  }

  function getGiftPropYes(form) {
    return (form && form.dataset.giftPropYes) || 'Yes';
  }

  function getGiftPropNo(form) {
    return (form && form.dataset.giftPropNo) || 'No';
  }

  function getDeliveryToPropLabel(form) {
    return (form && form.dataset.deliveryToPropLabel) || 'Delivery To';
  }

  /** Pre-order lines always include "Delivery To" from the PDP modal. */
  function isPreorderCartLine(item, form) {
    if (!item || !item.properties) return false;
    const toLabel = getDeliveryToPropLabel(form);
    const props = item.properties;
    return !!(props[toLabel] || props['Delivery To']);
  }

  /** Product-addons app lines (bouquet extras) — never require cart date. */
  function isAddonCartLine(item) {
    if (!item || !item.properties) return false;
    const props = item.properties;
    if (props._pa_hide_in_cart === 'true') return true;
    if (props._pa_sticky_addon === 'true') return true;
    if (props._pa_parent_variant != null && String(props._pa_parent_variant) !== '') return true;
    return false;
  }

  function isAddonLineEl(li) {
    return !!(li && li.getAttribute('data-pa-addon') === 'true');
  }

  function buildCheckoutLinePropertyPatch(form, overrides) {
    overrides = overrides || {};
    const dateLabel = getDeliveryPropLabel(form);
    const giftLabel = getGiftPropLabel(form);
    const msgLabel = getGiftMessagePropLabel(form);
    const giftMode = getGiftNoteMode(form);
    const patch = {};

    if (Object.prototype.hasOwnProperty.call(overrides, 'date')) {
      patch[dateLabel] = overrides.date || '';
    } else if (form.dataset.bellaDateSource === 'cart') {
      const parts = formatAdminOrderDateParts(form.dataset.deliveryDate || '');
      patch[dateLabel] = parts.date || '';
    } else {
      // Cart-level date is only valid when chosen in the drawer date sheet.
      patch[dateLabel] = '';
    }

    if (Object.prototype.hasOwnProperty.call(overrides, 'giftYes')) {
      patch[giftLabel] = overrides.giftYes ? getGiftPropYes(form) : '';
      if (giftMode === 'per_product' && Object.prototype.hasOwnProperty.call(overrides, 'giftMessage')) {
        patch[msgLabel] = overrides.giftMessage || '';
      }
    } else if (giftMode !== 'per_product') {
      const hasGift = !!(form.dataset.bellaGiftNote && String(form.dataset.bellaGiftNote).trim());
      // Only stamp Yes when a message exists — never stamp "No" on lines.
      patch[giftLabel] = hasGift ? getGiftPropYes(form) : '';
    }

    return patch;
  }

  function applyOrderedCheckoutLineProperties(existingProps, patch, form, opts) {
    existingProps = existingProps || {};
    opts = opts || {};
    const dateLabel = getDeliveryPropLabel(form);
    const giftLabel = getGiftPropLabel(form);
    const msgLabel = getGiftMessagePropLabel(form);
    const managedLabels = [
      dateLabel,
      giftLabel,
      msgLabel,
      'Delivery Date',
      'Gift note',
      'Gift Note',
      'Gift message',
    ];
    const next = {};
    const isPreorder = !!opts.isPreorder;
    const applyToPreorder = !!opts.applyToPreorder;
    const replaceDate = !!opts.replaceDate;

    const patchedDate = patch[dateLabel] || '';
    const existingDate = existingProps[dateLabel] || existingProps['Delivery Date'] || '';

    if (isPreorder) {
      // Only overwrite preorder dates when explicitly replacing (line edit / first cart save).
      if (replaceDate && applyToPreorder && patchedDate) {
        next[dateLabel] = patchedDate;
      } else if (existingDate) {
        next[dateLabel] = existingDate;
      }
    } else if (opts.clearStandardDates && replaceDate) {
      // Drop inherited dates when cart-level date is not set.
      if (patchedDate) next[dateLabel] = patchedDate;
    } else if (replaceDate && patchedDate) {
      next[dateLabel] = patchedDate;
    } else if (existingDate) {
      // Keep per-line dates independent across region/gift/add-to-cart syncs.
      next[dateLabel] = existingDate;
    } else if (patchedDate) {
      // Fill blank normal lines from the cart date (first pick / new line).
      next[dateLabel] = patchedDate;
    }

    if (Object.prototype.hasOwnProperty.call(patch, giftLabel)) {
      if (patch[giftLabel]) next[giftLabel] = patch[giftLabel];
      // Empty string clears Gift note (do not keep Yes/No when message was removed).
    } else if (existingProps[giftLabel] || existingProps['Gift note'] || existingProps['Gift Note']) {
      next[giftLabel] =
        existingProps[giftLabel] || existingProps['Gift note'] || existingProps['Gift Note'];
    }

    if (Object.prototype.hasOwnProperty.call(patch, msgLabel)) {
      if (patch[msgLabel]) next[msgLabel] = patch[msgLabel];
    } else if (existingProps[msgLabel] || existingProps['Gift message']) {
      next[msgLabel] = existingProps[msgLabel] || existingProps['Gift message'];
    }

    Object.keys(existingProps).forEach(function (key) {
      if (managedLabels.indexOf(key) !== -1) return;
      if (existingProps[key] == null || existingProps[key] === '') return;
      next[key] = existingProps[key];
    });

    return next;
  }

  function checkoutLinePropertiesEqual(a, b) {
    a = a || {};
    b = b || {};
    const keys = new Set(Object.keys(a).concat(Object.keys(b)));
    for (const key of keys) {
      if ((a[key] || '') !== (b[key] || '')) return false;
    }
    return true;
  }

  function escapeHtmlText(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var BellaLineDatePenSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-pen" fill="none" viewBox="0 0 512 512"><path fill-rule="evenodd" clip-rule="evenodd" d="M493.25 56.26l-37.51-37.51C443.25 6.25 426.87 0 410.49 0s-32.76 6.25-45.26 18.74L12.85 371.12.15 485.34C-1.45 499.72 9.88 512 23.95 512c.89 0 1.78-.05 2.69-.15l114.14-12.61 352.48-352.48c24.99-24.99 24.99-65.51-.01-90.5zM126.09 468.68l-93.03 10.31 10.36-93.17 263.89-263.89 82.77 82.77-263.99 263.98zm344.54-344.54l-57.93 57.93-82.77-82.77 57.93-57.93c6.04-6.04 14.08-9.37 22.63-9.37 8.55 0 16.58 3.33 22.63 9.37l37.51 37.51c12.47 12.48 12.47 32.78 0 45.26z" fill="currentColor"/></svg>';
  var BellaQtyMinusSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"/></svg>';
  var BellaQtyPlusSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"/></svg>';

  function lineDateEditEnabled(form) {
    return !!(form && form.dataset.lineEditDeliveryDate === 'true');
  }

  function lineGiftEditEnabled(form) {
    return !!(form && form.dataset.lineEditGiftNote === 'true');
  }

  /** Theme setting: same | per_product */
  function getGiftNoteMode(form) {
    return form && form.dataset.giftNoteMode === 'per_product' ? 'per_product' : 'same';
  }

  function getGiftMessagePropLabel(form) {
    return (form && form.dataset.giftMessagePropLabel) || 'Gift message';
  }

  /** Theme setting: inherit | shared | per_line — normal products only. */
  function getNormalLineDateMode(form) {
    const mode = form && form.dataset.bellaNormalLineDateMode;
    if (mode === 'shared' || mode === 'per_line') return mode;
    return 'inherit';
  }

  function buildLineGiftPropInnerHtml(form, giftYes, lineKey, lineIndex, messagePreview, opts) {
    opts = opts || {};
    const giftLabel = getGiftPropLabel(form);
    const yes = getGiftPropYes(form);
    const no = getGiftPropNo(form);
    const value = giftYes ? yes : no;
    const labelHtml =
      '<span class="bella-cart__line-prop-label">' + escapeHtmlText(giftLabel) + ':</span> ';
    // Addons inherit parent/cart gift — display only, no pencil (same as date).
    const editable = opts.editable !== false && lineGiftEditEnabled(form);
    if (!editable) {
      return labelHtml + '<span class="bella-cart__line-gift-text">' + escapeHtmlText(value) + '</span>';
    }
    const aria = form.dataset.strChangeGift || 'Change gift note';
    return (
      labelHtml +
      '<button type="button" class="bella-cart__line-gift-edit" data-bella-open="gift" data-bella-line-gift-edit' +
      (lineKey ? ' data-line-key="' + escapeHtmlText(lineKey) + '"' : '') +
      (lineIndex ? ' data-line-index="' + escapeHtmlText(String(lineIndex)) + '"' : '') +
      ' aria-label="' +
      escapeHtmlText(aria) +
      '"><span class="bella-cart__line-gift-text">' +
      escapeHtmlText(value) +
      '</span>' +
      BellaLineDatePenSvg +
      '</button>'
    );
  }

  function readLineGiftMessage(li) {
    if (!li) return '';
    const fromAttr = li.querySelector('[data-bella-line-gift]');
    if (fromAttr && fromAttr.getAttribute('data-gift-message')) {
      return String(fromAttr.getAttribute('data-gift-message') || '').trim();
    }
    return '';
  }

  function paintCartLineGiftNotes(form, opts) {
    if (!form || !lineGiftEditEnabled(form)) return;
    opts = opts || {};
    const onlyLineKey = opts.lineKey ? String(opts.lineKey) : '';
    const onlyLineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
    const mode = getGiftNoteMode(form);
    const cartGift = String(form.dataset.bellaGiftNote || form.dataset.cartNote || '').trim();
    const yes = getGiftPropYes(form);

    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      const isAddon = isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true';
      // Same mode: show on every product (including addons). Per-product: skip addons.
      if (isAddon && mode === 'per_product') return;
      const lineKey = getCartLineKey(li);
      const lineIndex = getCartLineIndex(li);
      if (onlyLineIndex && lineIndex !== onlyLineIndex) return;
      if (!onlyLineIndex && onlyLineKey && lineKey !== onlyLineKey) return;

      const content = li.querySelector('.product-content');
      if (!content) return;
      let propsEl = content.querySelector('.bella-cart__line-props');
      let giftEl = propsEl
        ? propsEl.querySelector('.bella-cart__line-prop--gift, [data-bella-line-gift]')
        : null;

      let message = '';
      let giftYes = false;
      if (mode === 'per_product') {
        message =
          opts.giftMessage != null && (onlyLineKey || onlyLineIndex)
            ? String(opts.giftMessage || '').trim()
            : readLineGiftMessage(li);
        if (opts.giftYes != null && (onlyLineKey || onlyLineIndex)) {
          giftYes = !!opts.giftYes;
        } else {
          giftYes = !!message;
          const textEl = li.querySelector('.bella-cart__line-gift-text');
          if (!message && textEl && String(textEl.textContent || '').trim() === yes) giftYes = true;
        }
      } else {
        giftYes = !!cartGift;
        message = cartGift;
      }

      // Only show Gift note on lines after a message is saved — never show "No".
      if (!giftYes) {
        if (giftEl) giftEl.remove();
        if (propsEl && !propsEl.querySelector('.bella-cart__line-prop')) {
          propsEl.remove();
        }
        return;
      }

      if (!propsEl) {
        propsEl = document.createElement('div');
        propsEl.className = 'bella-cart__line-props';
        content.appendChild(propsEl);
      }

      const html = buildLineGiftPropInnerHtml(form, true, lineKey, lineIndex, message, {
        editable: !isAddon,
      });
      if (!giftEl) {
        giftEl = document.createElement('p');
        giftEl.className = 'bella-cart__line-prop bella-cart__line-prop--gift';
        giftEl.setAttribute('data-bella-line-gift', '');
        propsEl.appendChild(giftEl);
      }
      giftEl.className = 'bella-cart__line-prop bella-cart__line-prop--gift';
      giftEl.setAttribute('data-bella-line-gift', '');
      if (isAddon) giftEl.setAttribute('data-bella-line-gift-readonly', '');
      else giftEl.removeAttribute('data-bella-line-gift-readonly');
      if (message) {
        giftEl.setAttribute('title', message);
        if (mode === 'per_product') giftEl.setAttribute('data-gift-message', message);
        else giftEl.removeAttribute('data-gift-message');
      } else {
        giftEl.removeAttribute('title');
        giftEl.removeAttribute('data-gift-message');
      }
      giftEl.innerHTML = html;
    });
  }

  function cartHasAnyGiftNote(form) {
    if (!form) return false;
    if (getGiftNoteMode(form) === 'per_product') {
      let found = false;
      const yes = getGiftPropYes(form);
      form.querySelectorAll('[data-bella-line-gift]').forEach(function (el) {
        if (found) return;
        if (String(el.getAttribute('data-gift-message') || '').trim()) {
          found = true;
          return;
        }
        const t = el.querySelector('.bella-cart__line-gift-text');
        if (t && String(t.textContent || '').trim() === yes) found = true;
      });
      return found;
    }
    return !!(form.dataset.bellaGiftNote && String(form.dataset.bellaGiftNote).trim());
  }

  function buildLineDatePropInnerHtml(form, propLabel, dateText, isPreorder, lineKey, lineIndex, lineRegion) {
    const labelHtml =
      '<span class="bella-cart__line-prop-label">' + escapeHtmlText(propLabel) + ':</span> ';
    if (!dateText) return labelHtml;
    if (!lineDateEditEnabled(form)) return labelHtml + escapeHtmlText(dateText);
    const aria = form.dataset.strChangeDate || 'Change delivery date';
    return (
      labelHtml +
      '<button type="button" class="bella-cart__line-date-edit" data-bella-open="date" data-bella-line-date-edit' +
      (lineKey ? ' data-line-key="' + escapeHtmlText(lineKey) + '"' : '') +
      (lineIndex ? ' data-line-index="' + escapeHtmlText(String(lineIndex)) + '"' : '') +
      (isPreorder ? ' data-line-preorder="true"' : '') +
      (isPreorder && lineRegion ? ' data-line-region="' + escapeHtmlText(lineRegion) + '"' : '') +
      ' aria-label="' +
      escapeHtmlText(aria) +
      '"><span class="bella-cart__line-date-text">' +
      escapeHtmlText(dateText) +
      '</span>' +
      BellaLineDatePenSvg +
      '</button>'
    );
  }

  function getCartLineKey(li) {
    if (!li) return '';
    const fromLi = li.getAttribute('data-line-key');
    if (fromLi) return fromLi;
    const btn = li.querySelector('[data-bella-line-date-edit][data-line-key]');
    return btn ? btn.getAttribute('data-line-key') || '' : '';
  }

  function getCartLineIndex(liOrBtn) {
    if (!liOrBtn) return 0;
    const fromAttr = parseInt(liOrBtn.getAttribute('data-line-index') || '', 10);
    if (fromAttr > 0) return fromAttr;
    const li = liOrBtn.closest ? liOrBtn.closest('li[data-line]') : null;
    if (li) {
      const fromLi = parseInt(li.getAttribute('data-line') || '', 10);
      if (fromLi > 0) return fromLi;
    }
    return 0;
  }

  function readLineDateDisplay(li) {
    if (!li) return '';
    const textEl = li.querySelector('.bella-cart__line-date-text');
    if (textEl) return String(textEl.textContent || '').trim();
    const propLabel = getDeliveryPropLabel(getForm());
    const propsEl = li.querySelector('.bella-cart__line-props');
    const dateEl = findLineDatePropEl(propsEl, propLabel);
    if (!dateEl) return '';
    const clone = dateEl.cloneNode(true);
    const label = clone.querySelector('.bella-cart__line-prop-label');
    if (label) label.remove();
    const btn = clone.querySelector('button');
    if (btn) return String(btn.textContent || '').trim();
    return String(clone.textContent || '').trim();
  }

  function findLineDatePropEl(propsEl, propLabel) {
    if (!propsEl) return null;
    let found = null;
    propsEl.querySelectorAll('.bella-cart__line-prop').forEach(function (p) {
      const lab = p.querySelector('.bella-cart__line-prop-label');
      if (lab && lab.textContent.replace(/:\s*$/, '').trim() === propLabel) found = p;
    });
    return found;
  }

  /**
   * Show a compact loader on Delivery Date row(s) while saveDate syncs —
   * hides blink/repaint until the final paint.
   */
  function setLineDateLoading(form, busy, opts) {
    if (!form) return;
    opts = opts || {};
    const onlyLineKey = opts.lineKey ? String(opts.lineKey) : '';
    const onlyLineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
    const scoped = !!(onlyLineKey || onlyLineIndex);
    const propLabel = getDeliveryPropLabel(form);

    form.classList.toggle('is-line-dates-syncing', !!busy);

    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true') {
        // Addons follow parent — loader on parent is enough unless scoped to addon.
      }
      const lineKey = getCartLineKey(li);
      const lineIndex = getCartLineIndex(li);
      if (onlyLineIndex && lineIndex !== onlyLineIndex) return;
      if (!onlyLineIndex && onlyLineKey && lineKey !== onlyLineKey) return;
      if (!scoped && (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true')) return;
      if (!scoped && isPreorderLineEl(li, form)) return;

      const content = li.querySelector('.product-content');
      if (!content) return;
      let propsEl = content.querySelector('.bella-cart__line-props');
      let dateEl = findLineDatePropEl(propsEl, propLabel);

      if (busy) {
        if (!propsEl) {
          propsEl = document.createElement('div');
          propsEl.className = 'bella-cart__line-props';
          content.appendChild(propsEl);
        }
        if (!dateEl) {
          dateEl = document.createElement('p');
          dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
          dateEl.innerHTML =
            '<span class="bella-cart__line-prop-label">' +
            escapeHtmlText(propLabel) +
            ':</span> <span class="bella-cart__line-date-text"></span>';
          propsEl.appendChild(dateEl);
        }
        dateEl.classList.add('is-line-date-loading');
        if (!dateEl.querySelector('.bella-cart__line-date-loader')) {
          const loader = document.createElement('span');
          loader.className = 'bella-cart__line-date-loader';
          loader.setAttribute('aria-hidden', 'true');
          dateEl.appendChild(loader);
        }
      } else if (dateEl) {
        dateEl.classList.remove('is-line-date-loading');
        dateEl.querySelectorAll('.bella-cart__line-date-loader').forEach(function (el) {
          el.remove();
        });
      }
    });
  }

  /**
   * Paint Delivery Date under cart lines after the shopper picks a date.
   * Preserves pencil edit controls when the theme setting is on.
   * When opts.lineKey is set, only that line is updated (per-line edit).
   */
  function paintCartLineDeliveryDates(form, dateDisplay, opts) {
    if (!form) return;
    opts = opts || {};
    const onlyLineKey = opts.lineKey ? String(opts.lineKey) : '';
    const onlyLineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
    const scopedEdit = !!(onlyLineKey || onlyLineIndex);
    const propLabel = getDeliveryPropLabel(form);
    const toLabel = getDeliveryToPropLabel(form);
    const dateText = dateDisplay ? String(dateDisplay).trim() : '';

    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      const lineKey = getCartLineKey(li);
      const lineIndex = getCartLineIndex(li);
      if (onlyLineIndex && lineIndex !== onlyLineIndex) return;
      if (!onlyLineIndex && onlyLineKey && lineKey !== onlyLineKey) return;
      // Add-on dates are painted from the parent product, not cart-level paints.
      if (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true') return;

      const content = li.querySelector('.product-content');
      if (!content) return;

      let propsEl = content.querySelector('.bella-cart__line-props');
      const propsText = propsEl ? propsEl.textContent || '' : '';
      const isPreorder =
        propsText.indexOf(toLabel) !== -1 || !!li.querySelector('[data-line-preorder="true"]');

      // Cart / normal-product date updates must never rewrite preorder dates.
      // Only a pencil edit scoped to that preorder line may change it.
      if (isPreorder && !scopedEdit) return;

      const existingText = content.querySelector('.bella-cart__line-date-text');
      if (existingText && dateText) {
        let editBtn = existingText.closest('[data-bella-line-date-edit]');
        // Upsell quiet-append / date-loader often leaves bare date text (no pencil).
        // Rebuild with the same edit control Liquid uses for normal lines.
        if (!editBtn && lineDateEditEnabled(form)) {
          let dateEl =
            existingText.closest('.bella-cart__line-prop--date') ||
            findLineDatePropEl(propsEl, propLabel);
          if (!dateEl) {
            if (!propsEl) {
              propsEl = document.createElement('div');
              propsEl.className = 'bella-cart__line-props';
              content.appendChild(propsEl);
            }
            dateEl = document.createElement('p');
            dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
            propsEl.appendChild(dateEl);
          } else {
            dateEl.classList.add('bella-cart__line-prop--date');
          }
          dateEl.innerHTML = buildLineDatePropInnerHtml(
            form,
            propLabel,
            dateText,
            isPreorder,
            lineKey,
            lineIndex,
            isPreorder ? regionFromDeliveryTo(readLineDeliveryTo(li, form)) : ''
          );
          return;
        }
        // Global first pick fills blank lines only; pencil edit may overwrite.
        if (!scopedEdit && !opts.replaceExisting) return;
        if (String(existingText.textContent || '').trim() === dateText) {
          if (editBtn) {
            if (lineKey) editBtn.setAttribute('data-line-key', lineKey);
            if (lineIndex) editBtn.setAttribute('data-line-index', String(lineIndex));
          }
          return;
        }
        existingText.textContent = dateText;
        if (editBtn) {
          if (lineKey) editBtn.setAttribute('data-line-key', lineKey);
          if (lineIndex) editBtn.setAttribute('data-line-index', String(lineIndex));
        }
        return;
      }

      if (isPreorder) {
        if (!dateText || !propsEl) return;
        let dateEl = findLineDatePropEl(propsEl, propLabel);
        if (!dateEl) {
          dateEl = document.createElement('p');
          dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
          propsEl.appendChild(dateEl);
        } else {
          dateEl.classList.add('bella-cart__line-prop--date');
        }
        dateEl.innerHTML = buildLineDatePropInnerHtml(
          form,
          propLabel,
          dateText,
          true,
          lineKey,
          lineIndex,
          regionFromDeliveryTo(readLineDeliveryTo(li, form))
        );
        return;
      }

      if (!dateText) {
        if (!propsEl) return;
        const dateEl = findLineDatePropEl(propsEl, propLabel);
        if (dateEl) dateEl.remove();
        if (!propsEl.querySelector('.bella-cart__line-prop')) propsEl.remove();
        return;
      }

      if (!propsEl) {
        propsEl = document.createElement('div');
        propsEl.className = 'bella-cart__line-props';
        content.appendChild(propsEl);
      }

      let dateEl = findLineDatePropEl(propsEl, propLabel);
      if (!dateEl) {
        dateEl = document.createElement('p');
        dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
        propsEl.appendChild(dateEl);
      } else {
        dateEl.classList.add('bella-cart__line-prop--date');
      }
        dateEl.innerHTML = buildLineDatePropInnerHtml(
          form,
          propLabel,
          dateText,
          false,
          lineKey,
          lineIndex
        );
      });

    // Keep bouquet add-on dates locked to their parent product line.
    syncAddonDatePaintFromParents(form, {
      lineKey: onlyLineKey,
      lineIndex: onlyLineIndex,
      dateDisplay: dateText,
      scopedEdit: scopedEdit,
    });
  }

  function paintAddonLineDateDisplay(form, li, dateText) {
    if (!form || !li) return;
    const content = li.querySelector('.product-content');
    if (!content) return;
    const propLabel = getDeliveryPropLabel(form);
    let propsEl = content.querySelector('.bella-cart__line-props');
    const text = dateText ? String(dateText).trim() : '';

    if (!text) {
      if (!propsEl) return;
      const dateEl = findLineDatePropEl(propsEl, propLabel);
      if (dateEl) dateEl.remove();
      if (!propsEl.querySelector('.bella-cart__line-prop')) propsEl.remove();
      return;
    }

    if (!propsEl) {
      propsEl = document.createElement('div');
      propsEl.className = 'bella-cart__line-props';
      content.appendChild(propsEl);
    }
    let dateEl = findLineDatePropEl(propsEl, propLabel);
    if (!dateEl) {
      dateEl = document.createElement('p');
      dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
      propsEl.appendChild(dateEl);
    } else {
      dateEl.classList.add('bella-cart__line-prop--date');
    }
    // Add-ons follow parent — show date only (no pencil).
    dateEl.innerHTML =
      '<span class="bella-cart__line-prop-label">' +
      escapeHtmlText(propLabel) +
      ':</span> <span class="bella-cart__line-date-text">' +
      escapeHtmlText(text) +
      '</span>';
  }

  function paintAddonDatesMatchingParent(form, parentVariantId, dateText) {
    if (!form || !parentVariantId) return;
    const want = String(parentVariantId);
    form.querySelectorAll('cart-items li[data-pa-addon="true"]').forEach(function (li) {
      const parentVid = String(li.getAttribute('data-pa-parent-variant') || '');
      if (parentVid && parentVid === want) {
        paintAddonLineDateDisplay(form, li, dateText);
      }
    });
  }

  function findCartLineEl(form, lineKey, lineIndex) {
    if (!form) return null;
    if (lineIndex) {
      const byLine = form.querySelector('cart-items li[data-line="' + lineIndex + '"]');
      if (byLine) return byLine;
    }
    if (!lineKey) return null;
    let found = null;
    form.querySelectorAll('cart-items li[data-line-key]').forEach(function (li) {
      if (!found && li.getAttribute('data-line-key') === String(lineKey)) found = li;
    });
    return found;
  }

  function syncAddonDatePaintFromParents(form, opts) {
    if (!form) return;
    opts = opts || {};
    if (opts.scopedEdit) {
      const parentLi = findCartLineEl(form, opts.lineKey, opts.lineIndex);
      if (!parentLi || isAddonLineEl(parentLi)) return;
      const vid = parentLi.getAttribute('data-variant-id');
      const dateText = opts.dateDisplay || readLineDateDisplay(parentLi);
      if (vid && dateText) paintAddonDatesMatchingParent(form, vid, dateText);
      return;
    }
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (isAddonLineEl(li)) return;
      const vid = li.getAttribute('data-variant-id');
      if (!vid) return;
      const parentDate = readLineDateDisplay(li);
      if (parentDate) paintAddonDatesMatchingParent(form, vid, parentDate);
    });
  }

  /**
   * Copy each parent product's Delivery Date onto its product-addon lines
   * (_pa_parent_variant). Works for both pre-order and normal parents.
   * opts.paintOnly — update drawer UI only (safe after add-to-cart; avoids cart/change races).
   */
  function syncAddonDatesFromParents(form, opts) {
    if (!form) return Promise.resolve();
    opts = opts || {};
    const paintOnly = !!opts.paintOnly;
    const dateLabel = getDeliveryPropLabel(form);
    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    const changeUrl = theme.routes.cart_change_url || '/cart/change.js';
    let onlyParentVariantId = opts.parentVariantId ? String(opts.parentVariantId) : '';
    const onlyParentLineIndex = opts.parentLineIndex ? parseInt(opts.parentLineIndex, 10) || 0 : 0;
    const forcedDate = opts.date ? String(opts.date).trim() : '';

    if (!onlyParentVariantId && onlyParentLineIndex) {
      const parentLi = findCartLineEl(form, '', onlyParentLineIndex);
      if (parentLi) onlyParentVariantId = String(parentLi.getAttribute('data-variant-id') || '');
    }

    // Fast DOM-only path: never call cart/change (add-to-cart reconcile must stay read-only).
    if (paintOnly) {
      if (forcedDate && onlyParentVariantId) {
        paintAddonDatesMatchingParent(form, onlyParentVariantId, forcedDate);
        return Promise.resolve();
      }
      form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
        if (isAddonLineEl(li)) return;
        const vid = li.getAttribute('data-variant-id');
        if (!vid) return;
        if (onlyParentVariantId && String(vid) !== onlyParentVariantId) return;
        const parentDate = readLineDateDisplay(li);
        if (parentDate) paintAddonDatesMatchingParent(form, vid, parentDate);
      });
      return Promise.resolve();
    }

    if (bellaCartWritesBlocked()) return Promise.resolve();

    const run = function () {
      return fetch(cartUrl, { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;

          if (!onlyParentVariantId && onlyParentLineIndex && cart.items[onlyParentLineIndex - 1]) {
            const p = cart.items[onlyParentLineIndex - 1];
            onlyParentVariantId = String(p.variant_id || p.id || '');
          }

          const parentDates = {};
          cart.items.forEach(function (item) {
            if (!item || isAddonCartLine(item)) return;
            const vid = String(item.variant_id || item.id || '');
            if (!vid) return;
            const date =
              (item.properties && (item.properties[dateLabel] || item.properties['Delivery Date'])) || '';
            if (forcedDate && onlyParentVariantId && vid === onlyParentVariantId) {
              parentDates[vid] = forcedDate;
            } else if (date) {
              parentDates[vid] = date;
            }
          });

          if (forcedDate && onlyParentVariantId) {
            parentDates[onlyParentVariantId] = forcedDate;
            paintAddonDatesMatchingParent(form, onlyParentVariantId, forcedDate);
          } else {
            Object.keys(parentDates).forEach(function (vid) {
              if (onlyParentVariantId && vid !== onlyParentVariantId) return;
              paintAddonDatesMatchingParent(form, vid, parentDates[vid]);
            });
          }

          const updates = [];
          cart.items.forEach(function (item, idx) {
            if (!item || !isAddonCartLine(item)) return;
            if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
            const parentVid = String((item.properties && item.properties._pa_parent_variant) || '');
            if (!parentVid || !parentDates[parentVid]) return;
            if (onlyParentVariantId && parentVid !== onlyParentVariantId) return;
            const wantDate = parentDates[parentVid];
            const existing =
              (item.properties && (item.properties[dateLabel] || item.properties['Delivery Date'])) || '';
            if (String(existing) === String(wantDate)) return;
            updates.push({ item: item, line: idx + 1, date: wantDate });
          });

          if (!updates.length) return null;

          return updates.reduce(function (chain, update) {
            return chain.then(function () {
              const patch = {};
              patch[dateLabel] = update.date;
              const nextProps = applyOrderedCheckoutLineProperties(update.item.properties, patch, form, {
                isPreorder: false,
                applyToPreorder: false,
                replaceDate: true,
              });
              // Never drop Product Addons private props — that can orphan/remove the bundle.
              Object.keys(update.item.properties || {}).forEach(function (key) {
                if (key.charAt(0) === '_' && nextProps[key] == null) {
                  nextProps[key] = update.item.properties[key];
                }
              });
              if (checkoutLinePropertiesEqual(update.item.properties, nextProps)) return null;
              const qty = parseInt(update.item.quantity, 10);
              const body = {
                quantity: qty > 0 ? qty : 1,
                properties: nextProps,
              };
              // Prefer stable line-item key so index shifts cannot wipe other lines.
              if (update.item.key) body.id = update.item.key;
              else body.line = update.line;
              return fetch(changeUrl, {
                ...fetchConfig(),
                body: JSON.stringify(body),
              }).then(function (r) {
                return r.json();
              });
            });
          }, Promise.resolve());
        })
        .catch(function (e) {
          console.error('[Bella cart] sync addon dates from parents', e);
        });
    };

    bellaLinePropsSyncChain = bellaLinePropsSyncChain.then(run, run);
    return bellaLinePropsSyncChain;
  }

  let bellaLinePropsSyncChain = Promise.resolve();
  let bellaCheckoutSubmitBypass = false;
  let bellaCheckoutInFlight = false;

  function isPreorderLineEl(li, form) {
    if (!li) return false;
    if (li.querySelector('[data-line-preorder="true"]')) return true;
    const toLabel = getDeliveryToPropLabel(form);
    if (!toLabel) return false;
    let found = false;
    li.querySelectorAll('.bella-cart__line-prop-label').forEach(function (lab) {
      if (lab.textContent.replace(/:\s*$/, '').trim() === toLabel) found = true;
    });
    return found;
  }

  function readLineDeliveryTo(li, form) {
    if (!li) return '';
    const toLabel = getDeliveryToPropLabel(form);
    let found = '';
    li.querySelectorAll('.bella-cart__line-prop').forEach(function (p) {
      if (found) return;
      const lab = p.querySelector('.bella-cart__line-prop-label');
      if (!lab) return;
      if (lab.textContent.replace(/:\s*$/, '').trim() !== toLabel) return;
      const clone = p.cloneNode(true);
      const label = clone.querySelector('.bella-cart__line-prop-label');
      if (label) label.remove();
      found = String(clone.textContent || '').trim();
    });
    return found;
  }

  function regionFromDeliveryTo(text) {
    if (!text) return 'dubai';
    return String(text).toLowerCase().indexOf('other') !== -1 ? 'other' : 'dubai';
  }

  /** Cart-level date from normal lines only — never inherit a pre-order line date. */
  function resolveSelectedDeliveryDateDisplay(form) {
    if (!form) return '';
    let found = '';
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (found) return;
      if (li.getAttribute('data-pa-addon') === 'true') return;
      if (isPreorderLineEl(li, form)) return;
      const t = readLineDateDisplay(li);
      if (t) found = t;
    });
    return found;
  }

  function cartHasVisibleLineDate(form) {
    return !!resolveSelectedDeliveryDateDisplay(form);
  }

  /** Suppress refresh/reconcile briefly after ATC quiet-merge (stops date blink loops). */
  let bellaCartMergeLockUntil = 0;
  function armBellaCartMergeLock(ms) {
    const until = Date.now() + (ms || 900);
    if (until > bellaCartMergeLockUntil) bellaCartMergeLockUntil = until;
  }
  function isBellaCartMergeLocked() {
    return Date.now() < bellaCartMergeLockUntil;
  }

  /**
   * After preorder → normal ATC, block Bella cart/change + cart/update briefly.
   * Those writes raced Product Addons and wiped preorder (+ addon) lines.
   */
  let bellaCartWriteBlockUntil = 0;
  let bellaPreorderSnapshot = null;
  /** Full cart snapshot for upsell ATC — Product Addons can wipe lines AFTER cart/add returns. */
  let bellaUpsellCartSnapshot = null;
  let bellaUpsellRestoreInFlight = null;
  let bellaUpsellRebuildInFlight = false;
  /** True while upsell + handler owns the cart — blocks destructive section paints. */
  let bellaUpsellAtcInFlight = false;
  /** Set only for the single post-restore section merge during upsell ATC. */
  let bellaUpsellAllowSectionMerge = false;
  /** When true, quiet-merge always replaces line items (final restore paint). */
  let bellaUpsellForceItemsReplace = false;
  /** Minimum visible line cards we must keep while snapshot is armed. */
  let bellaUpsellExpectedMinLines = 0;
  let bellaUpsellVariantId = null;
  let bellaUpsellWatchTimer = null;
  /** Shopper intentionally removed/changed a line — never restore old snapshots. */
  let bellaUserCartEditUntil = 0;
  let bellaCartGlobalLoaderDepth = 0;

  function bellaGetWriteShield() {
    return typeof window !== 'undefined' ? window.BellaCartWriteShield : null;
  }

  function bellaBlockForeignCartWrites(ms) {
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.block === 'function') {
      shield.block(ms || 12000);
      return;
    }
    const until = Date.now() + (ms || 12000);
    if (until > bellaForeignCartBlockUntil) bellaForeignCartBlockUntil = until;
  }

  function bellaClearForeignCartWriteBlock() {
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.clear === 'function') {
      shield.clear();
    }
    bellaForeignCartBlockUntil = 0;
  }

  function bellaSetUpsellAtcActive(active) {
    bellaUpsellAtcInFlight = !!active;
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.setAtcActive === 'function') {
      shield.setAtcActive(!!active);
    }
  }

  function bellaAllowOwnCartWrite(promiseFactory) {
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.allowOwn === 'function') {
      return shield.allowOwn(promiseFactory);
    }
    bellaOwnCartWriteDepth += 1;
    return Promise.resolve()
      .then(function () {
        return promiseFactory();
      })
      .finally(function () {
        bellaOwnCartWriteDepth = Math.max(0, bellaOwnCartWriteDepth - 1);
      });
  }

  function bellaOwnCartFetch(input, init) {
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.ownFetch === 'function') {
      return shield.ownFetch(input, init);
    }
    return bellaAllowOwnCartWrite(function () {
      return fetch(input, init);
    });
  }

  /** Fallback install only if early head shield did not load. */
  let bellaForeignCartBlockUntil = 0;
  let bellaOwnCartWriteDepth = 0;

  function bellaInstallForeignCartWriteShield() {
    if (typeof window === 'undefined' || window.__bellaCartWriteShield) return;
    window.__bellaCartWriteShield = true;

    function isCartMutateUrl(url) {
      // Never block /cart/add — see bella-cart-write-shield.js.
      return /\/cart\/(change|clear|update)(\.js)?(\?|$)/i.test(String(url || ''));
    }
    function shouldBlockForeign() {
      return (
        bellaOwnCartWriteDepth < 1 &&
        (bellaUpsellAtcInFlight || bellaUpsellRebuildInFlight || Date.now() < bellaForeignCartBlockUntil)
      );
    }
    function blockedFetchResponse() {
      return new Response(JSON.stringify({ ok: true, bella_blocked: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    var rawFetch = window.fetch;
    if (typeof rawFetch === 'function') {
      window.fetch = function (input, init) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
        } catch (err) {
          url = '';
        }
        if (shouldBlockForeign() && isCartMutateUrl(url)) {
          return Promise.resolve(blockedFetchResponse());
        }
        return rawFetch.apply(this, arguments);
      };
    }

    if (typeof XMLHttpRequest !== 'undefined') {
      var rawOpen = XMLHttpRequest.prototype.open;
      var rawSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__bellaCartUrl = url;
        return rawOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var self = this;
        if (shouldBlockForeign() && isCartMutateUrl(self.__bellaCartUrl)) {
          setTimeout(function () {
            try {
              Object.defineProperty(self, 'status', { configurable: true, get: function () { return 200; } });
              Object.defineProperty(self, 'readyState', { configurable: true, get: function () { return 4; } });
              Object.defineProperty(self, 'responseText', {
                configurable: true,
                get: function () {
                  return '{"ok":true,"bella_blocked":true}';
                },
              });
              Object.defineProperty(self, 'response', {
                configurable: true,
                get: function () {
                  return self.responseText;
                },
              });
            } catch (err) {
              /* ignore */
            }
            try {
              if (typeof self.onreadystatechange === 'function') self.onreadystatechange();
            } catch (err2) {
              /* ignore */
            }
            try {
              self.dispatchEvent(new Event('readystatechange'));
              self.dispatchEvent(new Event('load'));
            } catch (err3) {
              /* ignore */
            }
          }, 0);
          return;
        }
        return rawSend.apply(this, arguments);
      };
    }
  }

  bellaInstallForeignCartWriteShield();

  function bellaEnsureCartGlobalLoaderEl() {
    // Only attach to the drawer panel (#mini-cart) — never to <details>/<cart-drawer>
    // (those position overrides broke the fixed drawer layout).
    const host = getMiniCartHost() || document.querySelector('mini-cart#mini-cart, #mini-cart');
    if (!host) return null;
    // Remove stray loaders from earlier buggy attach points.
    document.querySelectorAll('cart-drawer > .bella-cart-global-loader, details.cart-drawer-container > .bella-cart-global-loader').forEach(function (orphan) {
      if (orphan.parentElement !== host) orphan.remove();
    });
    let el = host.querySelector(':scope > .bella-cart-global-loader');
    if (!el) {
      el = document.createElement('div');
      el.className = 'bella-cart-global-loader';
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('role', 'status');
      el.innerHTML = '<span class="loading-bar" aria-hidden="true"></span>';
      host.appendChild(el);
    }
    return el;
  }

  function bellaShowCartGlobalLoader() {
    bellaCartGlobalLoaderDepth += 1;
    const el = bellaEnsureCartGlobalLoaderEl();
    if (!el) return;
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    const host = getMiniCartHost();
    if (host) {
      host.setAttribute('aria-busy', 'true');
      host.classList.add('bella-cart--global-loading');
    }
  }

  function bellaHideCartGlobalLoader(force) {
    if (force) bellaCartGlobalLoaderDepth = 0;
    else bellaCartGlobalLoaderDepth = Math.max(0, bellaCartGlobalLoaderDepth - 1);
    if (bellaCartGlobalLoaderDepth > 0) return;
    const host = getMiniCartHost() || document.querySelector('mini-cart#mini-cart, #mini-cart');
    const el = host && host.querySelector(':scope > .bella-cart-global-loader');
    if (el) {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
    if (host) {
      host.classList.remove('bella-cart--global-loading');
      host.removeAttribute('aria-busy');
    }
  }

  function bellaClearRestoreSnapshots() {
    bellaUpsellCartSnapshot = null;
    bellaPreorderSnapshot = null;
    bellaUpsellRestoreInFlight = null;
    bellaSetUpsellAtcActive(false);
    bellaUpsellAllowSectionMerge = false;
    bellaUpsellForceItemsReplace = false;
    bellaUpsellRebuildInFlight = false;
    bellaUpsellExpectedMinLines = 0;
    bellaUpsellVariantId = null;
    if (bellaUpsellWatchTimer) {
      clearInterval(bellaUpsellWatchTimer);
      bellaUpsellWatchTimer = null;
    }
    const form = getForm();
    if (form) delete form.dataset.bellaPreservePreorder;
  }

  function bellaMarkUserCartEdit(ms) {
    bellaUserCartEditUntil = Date.now() + (ms || 4000);
    bellaClearRestoreSnapshots();
  }
  window.BellaCartMarkUserEdit = bellaMarkUserCartEdit;

  function bellaIsUserCartEdit() {
    return Date.now() < bellaUserCartEditUntil;
  }

  /** Use shield allow-list / raw fetch so Product Addons cannot swallow parent deletes. */
  function bellaCartMutateFetch(url, opts) {
    const shield = bellaGetWriteShield();
    if (shield && typeof shield.ownFetch === 'function') {
      return shield.ownFetch(url, opts);
    }
    if (typeof window.__bellaRawFetch === 'function') {
      return window.__bellaRawFetch(url, opts);
    }
    return fetch(url, opts);
  }

  function bellaCollectAddonKeysForParent(cart, parentVariantId, parentKey) {
    const keys = [];
    const seen = {};
    const pvid = String(parentVariantId || '');
    function pushKey(key) {
      if (!key || key === parentKey || seen[key]) return;
      seen[key] = true;
      keys.push(key);
    }
    if (cart && Array.isArray(cart.items) && pvid) {
      cart.items.forEach(function (item) {
        if (!item || !item.key) return;
        const props = item.properties || {};
        const bound = String(props._pa_parent_variant || '');
        if (bound && bound === pvid) pushKey(item.key);
      });
    }
    // Also pick visible sticky cards tagged to this parent (DOM may have parent when cart props lag).
    if (pvid) {
      document.querySelectorAll('li[data-pa-addon="true"]').forEach(function (addonLi) {
        const parentAttr = String(addonLi.getAttribute('data-pa-parent-variant') || '');
        if (parentAttr && parentAttr === pvid) {
          pushKey(addonLi.getAttribute('data-line-key') || '');
        }
      });
    }
    return keys;
  }

  function bellaApplyCartSectionsFromState(parsedState) {
    const cartItemsEl = document.querySelector('#mini-cart cart-items, cart-drawer cart-items, cart-items');
    if (!cartItemsEl || typeof cartItemsEl.getSectionsToRender !== 'function') {
      return bellaRefreshCartDrawerSections();
    }
    if (!parsedState || !parsedState.sections) {
      return bellaRefreshCartDrawerSections();
    }
    cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    cartItemsEl.getSectionsToRender().forEach(function (section) {
      const element = document.getElementById(section.id);
      if (!element || !parsedState.sections[section.section]) return;
      const elementToReplace = element.querySelector(section.selector) || element;
      if (!elementToReplace) return;
      const html = new DOMParser()
        .parseFromString(parsedState.sections[section.section], 'text/html')
        .querySelector(section.selector);
      if (html) elementToReplace.innerHTML = html.innerHTML;
      else elementToReplace.innerHTML = parsedState.sections[section.section];
    });
    if (typeof cartItemsEl.disableLoading === 'function') cartItemsEl.disableLoading();
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: parsedState },
      })
    );
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bella-cart-remove' });
    }
    refreshBellaForm();
    return Promise.resolve();
  }

  /**
   * Remove a cart line by key. For parent products, remove Product Addons children
   * first, then the parent — one click must clear the whole bundle.
   */
  function bellaRemoveCartLine(removeBtn) {
    const li =
      (removeBtn && removeBtn.closest && removeBtn.closest('li[data-line], li[id^="CartItem-"]')) || null;
    if (!li) return;

    const lineKey = li.getAttribute('data-line-key') || '';
    const variantId = li.getAttribute('data-variant-id') || '';
    const isAddon = li.getAttribute('data-pa-addon') === 'true';
    const lineIndex = li.getAttribute('data-line') || '';

    bellaMarkUserCartEdit(12000);
    bellaBlockForeignCartWrites(12000);
    armBellaCartMergeLock(12000);

    const overlay = li.querySelector('.loading-overlay');
    if (overlay) overlay.classList.remove('hidden');
    const main = document.getElementById('main-cart-items');
    if (main) main.classList.add('cart__items--disabled');

    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    const changeUrl = theme.routes.cart_change_url || '/cart/change.js';
    const cartItemsEl = document.querySelector('#mini-cart cart-items, cart-drawer cart-items, cart-items');

    function sectionPayload() {
      const payload = {};
      if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
        payload.sections = cartItemsEl
          .getSectionsToRender()
          .map(function (s) {
            return s.section;
          })
          .filter(Boolean);
        payload.sections_url = window.location.pathname;
      }
      return payload;
    }

    function changeKey(key, quantity, withSections) {
      const body = Object.assign({ id: key, quantity: quantity }, withSections ? sectionPayload() : {});
      return bellaCartMutateFetch(changeUrl, Object.assign({}, fetchConfig(), { body: JSON.stringify(body) })).then(
        function (r) {
          return r.text();
        }
      ).then(function (text) {
        try {
          return JSON.parse(text);
        } catch (err) {
          return {};
        }
      });
    }

    function cleanupLoading() {
      document.querySelectorAll('cart-items .loading-overlay').forEach(function (el) {
        el.classList.add('hidden');
      });
      if (main) main.classList.remove('cart__items--disabled');
      if (cartItemsEl && typeof cartItemsEl.disableLoading === 'function') cartItemsEl.disableLoading();
    }

    bellaCartMutateFetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        let parentKey = lineKey;
        let parentItem = null;
        if (parentKey && Array.isArray(cart.items)) {
          parentItem =
            cart.items.find(function (it) {
              return it && it.key === parentKey;
            }) || null;
        }
        if (!parentItem && variantId && Array.isArray(cart.items)) {
          parentItem =
            cart.items.find(function (it) {
              if (!it) return false;
              if (String(it.variant_id || it.id) !== String(variantId)) return false;
              const p = it.properties || {};
              if (isAddon) return !!(p._pa_parent_variant || p._pa_sticky_addon === 'true');
              return !(p._pa_parent_variant || p._pa_sticky_addon === 'true');
            }) || null;
          if (parentItem) parentKey = parentItem.key;
        }
        if (!parentKey && lineIndex && cart.items && cart.items[parseInt(lineIndex, 10) - 1]) {
          parentItem = cart.items[parseInt(lineIndex, 10) - 1];
          parentKey = parentItem.key;
        }
        if (!parentKey) throw new Error('Could not resolve cart line key');

        const pvid = String(
          (parentItem && (parentItem.variant_id || parentItem.id)) || variantId || ''
        );
        const parentIsAddon = !!(
          isAddon ||
          (parentItem &&
            parentItem.properties &&
            (parentItem.properties._pa_parent_variant || parentItem.properties._pa_sticky_addon === 'true'))
        );

        let keys = [];
        if (!parentIsAddon) {
          keys = bellaCollectAddonKeysForParent(cart, pvid, parentKey);
          keys.forEach(function (k) {
            const addonLi = document.querySelector('li[data-line-key="' + k.replace(/"/g, '') + '"]');
            const ov = addonLi && addonLi.querySelector('.loading-overlay');
            if (ov) ov.classList.remove('hidden');
          });
        }
        // Children first, parent last — Addons often eats a parent-first remove.
        keys.push(parentKey);
        keys = keys.filter(function (k, i, arr) {
          return k && arr.indexOf(k) === i;
        });

          return keys
          .reduce(function (chain, key, idx) {
            const isLast = idx === keys.length - 1;
            return chain.then(function () {
              return changeKey(key, 0, isLast);
            });
          }, Promise.resolve())
          .then(function (lastState) {
            // If parent somehow survived (Addons race), force-remove by key once more.
            const stillHasParent =
              lastState &&
              Array.isArray(lastState.items) &&
              lastState.items.some(function (it) {
                return it && it.key === parentKey;
              });
            if (stillHasParent) {
              return changeKey(parentKey, 0, true);
            }
            return lastState;
          })
          .then(function (lastState) {
            return bellaApplyCartSectionsFromState(lastState || {}).then(function () {
              return lastState;
            });
          })
          .then(function (lastState) {
            var empty =
              lastState &&
              typeof lastState.item_count === 'number' &&
              lastState.item_count === 0;
            if (empty) {
              return maybeClearBellaStateIfCartEmpty(lastState).then(function () {
                return lastState;
              });
            }
            return lastState;
          });
      })
      .catch(function (err) {
        console.error('[Bella cart] remove line failed', err);
        // Fallback: let theme cart-items try once by key.
        if (cartItemsEl && typeof cartItemsEl.updateQuantity === 'function') {
          cartItemsEl.updateQuantity(lineIndex, 0, null, { lineKey: lineKey, lineEl: li });
          return;
        }
        cleanupLoading();
      })
      .finally(function () {
        cleanupLoading();
        // Keep foreign writes blocked briefly so Addons cannot re-sync the deleted parent.
        bellaBlockForeignCartWrites(2500);
        bellaMarkUserCartEdit(4000);
      });
  }
  window.BellaCartRemoveLine = bellaRemoveCartLine;
  function blockBellaCartWrites(ms) {
    const until = Date.now() + (ms || 3000);
    if (until > bellaCartWriteBlockUntil) bellaCartWriteBlockUntil = until;
    armBellaCartMergeLock(ms || 3000);
  }
  function bellaCartWritesBlocked() {
    return Date.now() < bellaCartWriteBlockUntil;
  }
  function bellaSnapshotCartItems(cart) {
    const snap = [];
    if (!cart || !Array.isArray(cart.items)) return snap;
    cart.items.forEach(function (item) {
      if (!item) return;
      var sellingPlanId = null;
      try {
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          sellingPlanId = item.selling_plan_allocation.selling_plan.id || null;
        }
      } catch (err) {
        sellingPlanId = null;
      }
      snap.push({
        key: item.key,
        variantId: String(item.variant_id || item.id || ''),
        quantity: parseInt(item.quantity, 10) || 1,
        properties: Object.assign({}, item.properties || {}),
        sellingPlanId: sellingPlanId,
        isPreorder: isPreorderCartLine(item, getForm()),
        isAddon: isAddonCartLine(item),
      });
    });
    return snap;
  }

  function bellaFindMissingSnapshotLines(cart, snap) {
    const missing = [];
    if (!snap || !snap.length) return missing;
    const items = (cart && cart.items) || [];
    snap.forEach(function (s) {
      if (!s || !s.variantId) return;
      const still = items.some(function (item) {
        if (!item) return false;
        if (s.key && item.key === s.key) return true;
        if (String(item.variant_id || item.id) !== String(s.variantId)) return false;
        const ip = item.properties || {};
        const sp = s.properties || {};
        if (s.isAddon || sp._pa_parent_variant || sp._pa_sticky_addon) {
          // Match addon by parent binding when present; else same variant is enough.
          var sameParent =
            String(ip._pa_parent_variant || '') === String(sp._pa_parent_variant || '');
          var sameSticky =
            String(ip._pa_sticky_addon || '') === String(sp._pa_sticky_addon || '');
          if (sp._pa_parent_variant || sp._pa_sticky_addon) {
            return sameParent && sameSticky;
          }
          return isAddonCartLine(item);
        }
        if (s.isPreorder) {
          const toLabel = getDeliveryToPropLabel(getForm());
          return !!(ip[toLabel] || ip['Delivery To']);
        }
        return true;
      });
      if (!still) missing.push(s);
    });
    return missing;
  }

  /** Properties safe to re-POST on cart/add (empty values / junk cause 422). */
  function bellaPropsForRestore(props, kind) {
    const out = {};
    if (!props) return out;
    const dateLabel = getDeliveryPropLabel(getForm()) || 'Delivery Date';
    const toLabel = getDeliveryToPropLabel(getForm()) || 'Delivery To';
    Object.keys(props).forEach(function (k) {
      if (props[k] == null) return;
      const v = String(props[k]).trim();
      if (!v) return;
      // Always keep Product Addons private keys + delivery fields.
      if (k.charAt(0) === '_') {
        out[k] = v;
        return;
      }
      if (k === dateLabel || k === 'Delivery Date' || k === toLabel || k === 'Delivery To') {
        out[k] = v;
        return;
      }
      // Preorder / addon: keep other public props (Color, etc.). Skip blank-only noise.
      if (kind === 'preorder' || kind === 'addon' || kind === 'all') {
        out[k] = v;
      }
    });
    return out;
  }

  function bellaBuildRestoreRow(s) {
    const row = {
      id: Number(s.variantId) || s.variantId,
      quantity: s.quantity || 1,
    };
    if (s.sellingPlanId) row.selling_plan = s.sellingPlanId;
    const kind = s.isAddon ? 'addon' : s.isPreorder ? 'preorder' : 'all';
    const props = bellaPropsForRestore(s.properties, kind);
    if (Object.keys(props).length) row.properties = props;
    return row;
  }

  function bellaAddRestoreRow(row) {
    return bellaOwnCartFetch(
      theme.routes.cart_add_url,
      Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(row) })
    )
      .then(function (r) {
        return r.json();
      })
      .then(function (parsed) {
        if (parsed && parsed.status === 422) {
          // Retry without selling_plan (some preorders accept properties-only).
          if (row.selling_plan) {
            const retry = Object.assign({}, row);
            delete retry.selling_plan;
            return bellaOwnCartFetch(
              theme.routes.cart_add_url,
              Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(retry) })
            ).then(function (r2) {
              return r2.json();
            }).then(function (p2) {
              return !(p2 && p2.status === 422);
            });
          }
          // Retry with delivery props only.
          if (row.properties) {
            const dateLabel = getDeliveryPropLabel(getForm()) || 'Delivery Date';
            const toLabel = getDeliveryToPropLabel(getForm()) || 'Delivery To';
            const minimal = {};
            [dateLabel, 'Delivery Date', toLabel, 'Delivery To', '_pa_parent_variant', '_pa_sticky_addon', '_pa_hide_in_cart'].forEach(
              function (k) {
                if (row.properties[k] != null && String(row.properties[k]) !== '') {
                  minimal[k] = String(row.properties[k]);
                }
              }
            );
            const retry2 = {
              id: row.id,
              quantity: row.quantity || 1,
              properties: minimal,
            };
            if (row.selling_plan) retry2.selling_plan = row.selling_plan;
            return bellaOwnCartFetch(
              theme.routes.cart_add_url,
              Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(retry2) })
            ).then(function (r3) {
              return r3.json();
            }).then(function (p3) {
              return !(p3 && p3.status === 422);
            });
          }
          return false;
        }
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function bellaRestoreMissingSnapshotLines(snap, opts) {
    opts = opts || {};
    // Never re-add lines the shopper just deleted / qty-changed.
    if (bellaIsUserCartEdit()) return Promise.resolve(false);
    if (bellaUpsellRebuildInFlight) return Promise.resolve(false);
    if (!snap || !snap.length) return Promise.resolve(false);
    // Allow a new restore after the previous one finished; only coalesce true in-flight work.
    if (bellaUpsellRestoreInFlight) return bellaUpsellRestoreInFlight;

    function orderMissing(missing) {
      return missing.slice().sort(function (a, b) {
        const rank = function (s) {
          if (s.isPreorder) return 0;
          if (s.isAddon) return 2;
          return 1;
        };
        return rank(a) - rank(b);
      });
    }

    function restoreSequential(ordered) {
      if (!ordered.length) return Promise.resolve(false);
      blockBellaCartWrites(opts.blockMs || 10000);
      bellaBlockForeignCartWrites(opts.blockMs || 10000);
      // Always sequential — bulk add.js often returns 200 while skipping preorder/addon lines.
      return ordered.reduce(function (chain, s) {
        return chain.then(function (anyOk) {
          return bellaAddRestoreRow(bellaBuildRestoreRow(s)).then(function (ok) {
            return anyOk || ok;
          });
        });
      }, Promise.resolve(false));
    }

    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    const knownCart = opts.cart;
    const start = knownCart
      ? Promise.resolve(knownCart)
      : fetch(cartUrl, { credentials: 'same-origin' }).then(function (r) {
          return r.json();
        });

    bellaUpsellRestoreInFlight = start
      .then(function (cart) {
        const missing = bellaFindMissingSnapshotLines(cart, snap);
        if (!missing.length) return false;
        function pass(remaining, triesLeft) {
          if (!remaining.length) return Promise.resolve(true);
          return restoreSequential(orderMissing(remaining)).then(function (did) {
            if (!did) return false;
            return fetch(cartUrl, { credentials: 'same-origin' })
              .then(function (r) {
                return r.json();
              })
              .then(function (after) {
                const stillMissing = bellaFindMissingSnapshotLines(after, snap);
                if (!stillMissing.length) return true;
                if (triesLeft <= 0) return true;
                return pass(stillMissing, triesLeft - 1);
              });
          });
        }
        return pass(missing, 2);
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        bellaUpsellRestoreInFlight = null;
      });
    return bellaUpsellRestoreInFlight;
  }

  function bellaUpsellCartHasAllLines(cart, snap, upsellVariantId) {
    if (!cart || !Array.isArray(cart.items)) return false;
    if (snap && snap.length && bellaFindMissingSnapshotLines(cart, snap).length) return false;
    if (upsellVariantId == null || upsellVariantId === '') return true;
    return cart.items.some(function (item) {
      return item && String(item.variant_id || item.id) === String(upsellVariantId);
    });
  }

  function bellaCountVisibleCartItems(cart) {
    var n = 0;
    if (!cart || !Array.isArray(cart.items)) return 0;
    cart.items.forEach(function (item) {
      if (!item) return;
      if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
      n += 1;
    });
    return n;
  }

  /**
   * Product Addons sometimes sets _pa_hide_in_cart after restore, which drops the line card
   * while keeping price in the subtotal. Clear hide for lines that were visible in the snapshot.
   */
  function bellaUnhideRestoredSnapshotLines(cart, snap) {
    if (!cart || !snap || !snap.length) return Promise.resolve(false);
    var changeUrl = theme.routes.cart_change_url || '/cart/change.js';
    var updates = [];
    snap.forEach(function (s) {
      if (!s || !s.variantId) return;
      var wasHidden = !!(s.properties && s.properties._pa_hide_in_cart === 'true');
      if (wasHidden) return;
      (cart.items || []).forEach(function (item) {
        if (!item || !item.key) return;
        if (String(item.variant_id || item.id) !== String(s.variantId)) return;
        var ip = item.properties || {};
        if (ip._pa_hide_in_cart !== 'true') return;
        if (s.isAddon || (s.properties && (s.properties._pa_parent_variant || s.properties._pa_sticky_addon))) {
          if (
            String(ip._pa_parent_variant || '') !== String((s.properties && s.properties._pa_parent_variant) || '') &&
            String(ip._pa_sticky_addon || '') !== String((s.properties && s.properties._pa_sticky_addon) || '')
          ) {
            // Same variant but different addon binding — still unhide if sticky/parent present.
            if (!ip._pa_parent_variant && ip._pa_sticky_addon !== 'true') return;
          }
        }
        var nextProps = Object.assign({}, ip);
        delete nextProps._pa_hide_in_cart;
        updates.push({
          id: item.key,
          quantity: item.quantity,
          properties: nextProps,
        });
      });
    });
    if (!updates.length) return Promise.resolve(false);
    blockBellaCartWrites(8000);
    return updates
      .reduce(function (chain, row) {
        return chain.then(function () {
          return bellaOwnCartFetch(changeUrl, Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(row) })).then(
            function (r) {
              return r.json();
            }
          );
        });
      }, Promise.resolve())
      .then(function () {
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function bellaCartHasAddonItems(cartOrForm) {
    if (!cartOrForm) return false;
    if (cartOrForm.items && Array.isArray(cartOrForm.items)) {
      return cartOrForm.items.some(function (item) {
        return item && isAddonCartLine(item);
      });
    }
    return !!cartOrForm.querySelector(
      'cart-items li[data-pa-addon="true"], cart-items li[data-pa-parent-variant]'
    );
  }

  /**
   * Freeze existing basket line cards so upsell/Addons cannot rewrite them.
   * Returns a handle used by bellaReinstateFrozenCartItems.
   */
  function bellaFreezeCartItemsDom() {
    var form = getForm();
    if (!form) return null;
    var list =
      form.querySelector('cart-items .bella-cart__items') ||
      form.querySelector('cart-items ul') ||
      form.querySelector('.bella-cart__items');
    if (!list) return null;
    return {
      html: list.innerHTML,
      count: list.querySelectorAll(':scope > li[data-line], :scope > li[id^="CartItem-"]').length,
    };
  }

  function bellaGetCartItemsList() {
    var form = getForm();
    if (!form) return null;
    return (
      form.querySelector('cart-items .bella-cart__items') ||
      form.querySelector('cart-items ul') ||
      form.querySelector('.bella-cart__items')
    );
  }

  /** Put frozen line cards back, then append the upsell card (if any). */
  function bellaReinstateFrozenCartItems(freeze, upsellItem, dateDisplay) {
    if (!freeze || !freeze.html) return false;
    var list = bellaGetCartItemsList();
    if (!list) return false;
    withBellaQuietDom(function () {
      list.innerHTML = freeze.html;
      if (upsellItem) bellaAppendUpsellLineToDrawer(upsellItem, { dateDisplay: dateDisplay || '' });
    });
    // Re-paint gift on all lines (frozen HTML may be stale; upsell card starts without gift).
    bellaEnsureGiftPaintOnLines(getForm());
    return true;
  }

  /** Same-mode gift message currently on the cart (drawer dataset or cart.note). */
  function readCartGiftMessage(form, cart) {
    form = form || getForm();
    var fromForm = form
      ? String(form.dataset.bellaGiftNote || form.dataset.cartNote || '').trim()
      : '';
    if (fromForm) return fromForm;
    if (cart && cart.note) return String(cart.note).trim();
    return '';
  }

  /** Paint Gift note: Yes on every line when same-mode message exists (upsell-safe). */
  function bellaEnsureGiftPaintOnLines(form) {
    form = form || getForm();
    if (!form || !lineGiftEditEnabled(form)) return;
    if (getGiftNoteMode(form) === 'per_product') return;
    paintCartLineGiftNotes(form);
  }

  /**
   * After upsell ATC: paint gift UI and stamp Gift note: Yes on lines missing it.
   * Safe to call repeatedly; no-ops when there is no saved gift message.
   */
  function bellaEnsureGiftAfterUpsellAdd(form, cart) {
    form = form || getForm();
    if (!form || !lineGiftEditEnabled(form)) return Promise.resolve();
    if (getGiftNoteMode(form) === 'per_product') return Promise.resolve();

    var msg = readCartGiftMessage(form, cart);
    if (msg) {
      form.dataset.bellaGiftNote = msg;
      form.dataset.cartNote = msg;
    }
    paintCartLineGiftNotes(form);
    if (!msg) return Promise.resolve();

    return syncCheckoutLineProperties(form, {
      giftYes: true,
      applyToAddon: true,
      applyToPreorder: true,
      allowDuringWriteBlock: true,
    }).then(function () {
      paintCartLineGiftNotes(form);
    });
  }

  /**
   * While upsell ATC is in flight, undo any DOM rewrite of existing cart lines.
   */
  function bellaArmCartItemsFreezeGuard(freeze, getUpsellItem, getDateDisplay) {
    var list = bellaGetCartItemsList();
    if (!list || !freeze || typeof MutationObserver === 'undefined') return function () {};
    var ticking = false;
    var obs = new MutationObserver(function () {
      if (!bellaUpsellAtcInFlight || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!bellaUpsellAtcInFlight) return;
        var live = bellaGetCartItemsList();
        if (!live) return;
        var liveCount = live.querySelectorAll(
          ':scope > li[data-line], :scope > li[id^="CartItem-"]'
        ).length;
        // Existing cards were replaced/removed — reinstate exact frozen HTML + upsell.
        if (liveCount < freeze.count || live.innerHTML.indexOf('data-line-key') === -1) {
          bellaReinstateFrozenCartItems(
            freeze,
            typeof getUpsellItem === 'function' ? getUpsellItem() : null,
            typeof getDateDisplay === 'function' ? getDateDisplay() : ''
          );
          return;
        }
        // List grew but first N cards no longer match freeze — reinstate.
        var probe = document.createElement('ul');
        probe.innerHTML = freeze.html;
        var frozenLis = probe.querySelectorAll(':scope > li');
        var liveLis = live.querySelectorAll(':scope > li[data-line], :scope > li[id^="CartItem-"]');
        var mismatch = false;
        for (var i = 0; i < frozenLis.length; i++) {
          var wantKey = frozenLis[i].getAttribute('data-line-key') || '';
          var wantVid = frozenLis[i].getAttribute('data-variant-id') || '';
          var got = liveLis[i];
          if (!got) {
            mismatch = true;
            break;
          }
          var gotKey = got.getAttribute('data-line-key') || '';
          var gotVid = got.getAttribute('data-variant-id') || '';
          if ((wantKey && gotKey && wantKey !== gotKey) || (wantVid && gotVid && wantVid !== gotVid)) {
            mismatch = true;
            break;
          }
        }
        if (mismatch) {
          bellaReinstateFrozenCartItems(
            freeze,
            typeof getUpsellItem === 'function' ? getUpsellItem() : null,
            typeof getDateDisplay === 'function' ? getDateDisplay() : ''
          );
        }
      });
    });
    obs.observe(list, { childList: true, subtree: true });
    // Also watch the form — Addons may replace <cart-items> entirely.
    var form = getForm();
    if (form) obs.observe(form, { childList: true, subtree: false });
    return function () {
      try {
        obs.disconnect();
      } catch (err) {
        /* ignore */
      }
    };
  }

  /**
   * Append one upsell line card into the live drawer — no section refresh.
   * Keeps existing product cards untouched.
   * Markup must match sections/bella-cart-drawer.liquid (qty + date pencil).
   */
  function bellaAppendUpsellLineToDrawer(item, opts) {
    opts = opts || {};
    var form = getForm();
    if (!form || !item) return false;
    var list =
      form.querySelector('cart-items .bella-cart__items') ||
      form.querySelector('cart-items ul') ||
      form.querySelector('.bella-cart__items');
    if (!list) return false;

    var vid = String(item.variant_id || item.id || '');
    if (vid && list.querySelector('li[data-variant-id="' + vid + '"]')) {
      return true; // already shown
    }

    var existing = list.querySelectorAll('li[data-line], li[id^="CartItem-"]').length;
    var lineIndex = existing + 1;
    var key = item.key || '';
    var title = item.product_title || item.title || 'Product';
    var vendor = item.vendor ? String(item.vendor).toUpperCase() : '';
    var unitCents =
      item.original_price != null
        ? item.original_price
        : item.price != null
          ? item.price
          : item.final_price != null
            ? item.final_price
            : 0;
    var qty = parseInt(item.quantity, 10) || 1;
    var url = item.url || '#';
    var img = item.image || (item.featured_image && item.featured_image.url) || '';
    var dateDisplay = opts.dateDisplay || '';
    var dateLabel = getDeliveryPropLabel(form) || 'Delivery Date';
    var removeUrl = key ? '/cart/change?id=' + encodeURIComponent(key) + '&quantity=0' : '#';

    var li = document.createElement('li');
    li.id = 'CartItem-' + lineIndex;
    li.setAttribute('data-line', String(lineIndex));
    if (key) li.setAttribute('data-line-key', key);
    if (item.product_id) li.setAttribute('data-product-id', String(item.product_id));
    if (vid) li.setAttribute('data-variant-id', vid);
    li.setAttribute('data-quantity', String(qty));
    li.setAttribute('data-price', String(item.original_price || item.price || unitCents));

    var imgHtml = '';
    if (img) {
      imgHtml =
        '<div><a href="' +
        escapeHtmlText(url) +
        '" class="product-image media-wrapper media-wrapper--small"><div class="media media--adapt" style="--image-ratio-percent:113%;">' +
        '<img src="' +
        escapeHtmlText(img) +
        '" alt="" width="92" height="104" loading="lazy" />' +
        '</div></a></div>';
    }

    var dateHtml = '';
    if (dateDisplay) {
      dateHtml =
        '<div class="bella-cart__line-props"><p class="bella-cart__line-prop bella-cart__line-prop--date">' +
        buildLineDatePropInnerHtml(form, dateLabel, dateDisplay, false, key, lineIndex, '') +
        '</p></div>';
    }

    var qtyId = 'Quantity-' + lineIndex;
    var qtyHtml =
      '<div class="product-quantity">' +
      '<dt>' +
      '<label for="' +
      qtyId +
      '" class="visually-hidden">Quantity</label>' +
      '<quantity-input class="quantity">' +
      '<button class="quantity__button no-js-hidden" name="minus" type="button">' +
      '<span class="visually-hidden">Decrease quantity</span>' +
      BellaQtyMinusSvg +
      '</button>' +
      '<input class="quantity__input" type="number" name="updates[]" value="' +
      qty +
      '" min="0" aria-label="Quantity" id="' +
      qtyId +
      '" data-index="' +
      lineIndex +
      '" />' +
      '<button class="quantity__button no-js-hidden" name="plus" type="button">' +
      '<span class="visually-hidden">Increase quantity</span>' +
      BellaQtyPlusSvg +
      '</button>' +
      '</quantity-input>' +
      '</dt>' +
      '<dd class="price"><price-money>' +
      bellaMoneyMarkup(unitCents, form, 'core') +
      '</price-money></dd>' +
      '</div>';

    li.innerHTML =
      '<div class="loading-overlay hidden"><div class="loading-overlay__spinner"></div></div>' +
      '<cart-remove-button id="Remove-' +
      lineIndex +
      '" data-index="' +
      lineIndex +
      '">' +
      '<a href="' +
      escapeHtmlText(removeUrl) +
      '" class="delete-product" aria-label="Remove" data-no-instant>' +
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13"></path></svg>' +
      '</a></cart-remove-button>' +
      '<div class="product-container' +
      (img ? '' : ' no-image') +
      '">' +
      imgHtml +
      '<div class="product-description"><div class="product-content">' +
      (vendor ? '<div class="bella-cart__vendor">' + escapeHtmlText(vendor) + '</div>' : '') +
      '<a href="' +
      escapeHtmlText(url) +
      '" class="link product-title">' +
      escapeHtmlText(title) +
      '</a>' +
      dateHtml +
      '</div>' +
      qtyHtml +
      '<p class="cart-item__error form__message errors" id="Line-item-error-' +
      lineIndex +
      '" role="alert"><span class="cart-item__error-text"></span></p>' +
      '</div></div>';

    withBellaQuietDom(function () {
      list.appendChild(li);
      form.classList.remove('is-empty');
    });
    // Upsell cards are built without gift markup — paint from cart.note when present.
    bellaEnsureGiftPaintOnLines(form);
    return true;
  }

  function bellaUpdateDrawerCountAndMoney(cart) {
    var form = getForm();
    if (!form) return;
    if (cart && typeof cart.total_price === 'number') {
      form.dataset.subtotalCents = String(cart.total_price);
    }
    var visible = 0;
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        if (!item) return;
        if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
        visible += item.quantity || 1;
      });
    } else {
      visible = form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').length;
    }
    var countEl = form.querySelector('.bella-cart__item-count');
    if (countEl) {
      var label = (countEl.textContent || '').replace(/^\d+\s*/, '').trim() || 'ITEM';
      countEl.textContent = visible + ' ' + label;
    }
    var ctrl = formControllers.get(form) || getCtrl(form, false);
    if (ctrl) ctrl.updateFooterTotals({ preserveDeliveryPickers: true });
  }

  /**
   * Quiet upsell: add without sections, append line in DOM, restore server wipe silently.
   * Never full-refreshes the drawer (avoids empty flash + Addons paint races).
   */
  function bellaUpsellQuietAppendAfterAdd(snapshot, upsellVariantId, addedItem, stampFn, opts) {
    opts = opts || {};
    var cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    var hasAddons = !!opts.hasAddons;
    var dateDisplay = opts.dateDisplay || '';
    function wait(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }
    function fetchCart() {
      return fetch(cartUrl, { credentials: 'same-origin' }).then(function (r) {
        return r.json();
      });
    }

    // 1) Append upsell card immediately — keep existing cards as-is.
    bellaAppendUpsellLineToDrawer(addedItem, { dateDisplay: dateDisplay });
    if (typeof stampFn === 'function') stampFn();

    // 2) Sync count/money from cart.js; restore if Addons wiped; never section-refresh.
    function syncAndGuard(attempt) {
      attempt = attempt || 0;
      return fetchCart().then(function (cart) {
        bellaUpdateDrawerCountAndMoney(cart);
        if (bellaUpsellCartHasAllLines(cart, snapshot, upsellVariantId)) {
          return cart;
        }
        return bellaRestoreMissingSnapshotLines(snapshot, { blockMs: 12000, cart: cart }).then(function () {
          return fetchCart().then(function (after) {
            bellaUpdateDrawerCountAndMoney(after);
            // Re-append upsell if restore somehow dropped the DOM card.
            if (addedItem) bellaAppendUpsellLineToDrawer(addedItem, { dateDisplay: dateDisplay });
            if (typeof stampFn === 'function') stampFn();
            if (
              !bellaUpsellCartHasAllLines(after, snapshot, upsellVariantId) &&
              attempt < (hasAddons ? 4 : 2)
            ) {
              return wait(hasAddons ? 350 : 200).then(function () {
                return syncAndGuard(attempt + 1);
              });
            }
            return after;
          });
        });
      });
    }

    return syncAndGuard(0)
      .then(function (cart) {
        // Late Addons wipe guard — server only, no drawer refresh.
        return wait(hasAddons ? 400 : 200)
          .then(fetchCart)
          .then(function (late) {
            if (bellaUpsellCartHasAllLines(late, snapshot, upsellVariantId)) {
              bellaUpdateDrawerCountAndMoney(late);
              return late;
            }
            return bellaUpsellGuardRestore({ cart: late, paintIfRestored: false }).then(function () {
              return fetchCart().then(function (after) {
                bellaUpdateDrawerCountAndMoney(after);
                if (addedItem) bellaAppendUpsellLineToDrawer(addedItem, { dateDisplay: dateDisplay });
                if (typeof stampFn === 'function') stampFn();
                return after;
              });
            });
          });
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * If Product Addons wiped lines after restore, put them back.
   * Safe to call during bellaUpsellAtcInFlight (that used to early-return and miss the wipe).
   */
  function bellaUpsellGuardRestore(opts) {
    opts = opts || {};
    if (bellaIsUserCartEdit()) return Promise.resolve(false);
    var snap = bellaUpsellCartSnapshot;
    if (!snap || !snap.length) return Promise.resolve(false);
    var cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    var known = opts.cart;
    var start = known
      ? Promise.resolve(known)
      : fetch(cartUrl, { credentials: 'same-origin' }).then(function (r) {
          return r.json();
        });
    return start.then(function (cart) {
      if (bellaUpsellCartHasAllLines(cart, snap, bellaUpsellVariantId)) {
        return false;
      }
      return bellaRestoreMissingSnapshotLines(snap, { blockMs: 12000, cart: cart }).then(function (did) {
        if (!did && !(opts.paintIfRestored)) return false;
        return fetch(cartUrl, { credentials: 'same-origin' })
          .then(function (r) {
            return r.json();
          })
          .then(function (after) {
            if (!bellaUpsellCartHasAllLines(after, snap, bellaUpsellVariantId)) return !!did;
            if (!opts.paintIfRestored) return true;
            // Quiet path: update money/count only — never full section refresh.
            bellaUpdateDrawerCountAndMoney(after);
            return true;
          });
      });
    });
  }

  /**
   * After upsell add: restore until stable, paint once, then watch for late Addons wipes.
   */
  function bellaUpsellFinalizeAfterAdd(snapshot, upsellVariantId, stampFn, opts) {
    opts = opts || {};
    var cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    var hasAddons = !!opts.hasAddons;
    var paintGen = 0;
    bellaUpsellVariantId = upsellVariantId;
    bellaUpsellExpectedMinLines = Math.max(
      bellaUpsellExpectedMinLines,
      (snapshot || []).filter(function (s) {
        return !(s.properties && s.properties._pa_hide_in_cart === 'true');
      }).length + 1
    );
    function wait(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }
    function fetchCart() {
      return fetch(cartUrl, { credentials: 'same-origin' }).then(function (r) {
        return r.json();
      });
    }
    function domVisibleCount() {
      var form = getForm();
      if (!form) return 0;
      return form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').length;
    }
    function drawerMatchesCart(cart) {
      if (!cart) return false;
      if (!bellaUpsellCartHasAllLines(cart, snapshot, upsellVariantId)) return false;
      var expect = bellaCountVisibleCartItems(cart);
      if (expect < 1) return false;
      return domVisibleCount() >= expect;
    }
    function ensureComplete(cart) {
      if (bellaUpsellCartHasAllLines(cart, snapshot, upsellVariantId)) {
        return Promise.resolve({ cart: cart, ok: true });
      }
      return bellaRestoreMissingSnapshotLines(snapshot, { blockMs: 12000, cart: cart }).then(function () {
        return fetchCart().then(function (cart2) {
          return {
            cart: cart2,
            ok: bellaUpsellCartHasAllLines(cart2, snapshot, upsellVariantId),
          };
        });
      });
    }
    function settleStable() {
      var gap = hasAddons ? 300 : 160;
      var maxAttempts = hasAddons ? 8 : 4;
      var okStreak = 0;
      var attempt = 0;
      function step(cart) {
        attempt += 1;
        return ensureComplete(cart).then(function (res) {
          if (res && res.ok) {
            okStreak += 1;
            if (okStreak >= 2) return res.cart;
            return wait(gap).then(fetchCart).then(step);
          }
          okStreak = 0;
          if (attempt >= maxAttempts) return (res && res.cart) || cart;
          return wait(gap).then(fetchCart).then(step);
        });
      }
      return fetchCart().then(step);
    }
    function paintFinal(cart) {
      if (!cart || !bellaUpsellCartHasAllLines(cart, snapshot, upsellVariantId)) {
        return Promise.resolve(cart);
      }
      if (bellaCountVisibleCartItems(cart) < 1) return Promise.resolve(cart);
      var gen = ++paintGen;
      bellaUpsellAllowSectionMerge = true;
      bellaUpsellForceItemsReplace = true;
      return bellaRefreshCartDrawerSections()
        .then(function () {
          if (gen !== paintGen) return cart;
          if (typeof stampFn === 'function') stampFn();
          bellaUpsellAllowSectionMerge = false;
          bellaUpsellForceItemsReplace = false;
          return cart;
        })
        .catch(function () {
          if (gen === paintGen) {
            bellaUpsellAllowSectionMerge = false;
            bellaUpsellForceItemsReplace = false;
          }
          return cart;
        });
    }
    /** Keep restoring after paint — Addons often wipes 0.5–2s later. */
    function postPaintWatchdog() {
      var deadline = Date.now() + (hasAddons ? 3500 : 1800);
      var okStreak = 0;
      function tick() {
        return fetchCart().then(function (cart) {
          if (bellaUpsellCartHasAllLines(cart, snapshot, upsellVariantId)) {
            okStreak += 1;
            if (okStreak >= 2 && domVisibleCount() >= bellaCountVisibleCartItems(cart)) {
              return cart;
            }
            if (domVisibleCount() < bellaCountVisibleCartItems(cart)) {
              return paintFinal(cart).then(function () {
                return Date.now() < deadline ? wait(300).then(tick) : cart;
              });
            }
            if (Date.now() >= deadline) return cart;
            return wait(300).then(tick);
          }
          okStreak = 0;
          return ensureComplete(cart).then(function (res) {
            var next = (res && res.cart) || cart;
            return paintFinal(next).then(function () {
              if (Date.now() >= deadline) return next;
              return wait(250).then(tick);
            });
          });
        });
      }
      return wait(hasAddons ? 200 : 120).then(tick);
    }

    return settleStable()
      .then(function (cart) {
        return paintFinal(cart);
      })
      .then(function () {
        return postPaintWatchdog();
      })
      .catch(function () {
        bellaUpsellAllowSectionMerge = false;
        bellaUpsellForceItemsReplace = false;
      });
  }

  function bellaSanitizeLineProperties(props) {
    var out = {};
    if (!props) return out;
    Object.keys(props).forEach(function (k) {
      if (props[k] == null) return;
      out[k] = String(props[k]);
    });
    return out;
  }
  function hasPreorderLineEl(form) {
    if (!form) return false;
    let found = false;
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (found) return;
      if (isPreorderLineEl(li, form)) found = true;
    });
    return found;
  }
  function cartLooksPreorderOnly(form) {
    if (!form) return false;
    if ((form.dataset.bellaCartMode || '') === 'preorder-only') return true;
    return hasPreorderLineEl(form) && !hasStandardLineEl(form);
  }
  /** Call before ATC when cart still has preorder lines — enables write-block + restore. */
  function armPreorderPreserveForAtc(form) {
    form = form || getForm();
    if (!form) return Promise.resolve(false);
    if (!hasPreorderLineEl(form) && (form.dataset.bellaCartMode || '') !== 'preorder-only') {
      return Promise.resolve(false);
    }
    form.dataset.bellaPreservePreorder = '1';
    blockBellaCartWrites(4000);
    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    return fetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return true;
        const snap = [];
        cart.items.forEach(function (item) {
          if (!item) return;
          var sellingPlanId = null;
          try {
            if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
              sellingPlanId = item.selling_plan_allocation.selling_plan.id || null;
            }
          } catch (err) {
            sellingPlanId = null;
          }
          if (isAddonCartLine(item)) {
            snap.push({
              key: item.key,
              variantId: String(item.variant_id || item.id || ''),
              quantity: parseInt(item.quantity, 10) || 1,
              properties: Object.assign({}, item.properties || {}),
              sellingPlanId: sellingPlanId,
              isPreorder: false,
              isAddon: true,
            });
            return;
          }
          if (!isPreorderCartLine(item, form)) return;
          snap.push({
            key: item.key,
            variantId: String(item.variant_id || item.id || ''),
            quantity: parseInt(item.quantity, 10) || 1,
            properties: Object.assign({}, item.properties || {}),
            sellingPlanId: sellingPlanId,
            isPreorder: true,
            isAddon: false,
          });
        });
        if (snap.length) bellaPreorderSnapshot = snap;
        // Also keep a full-cart restore snapshot — Addons can wipe after mixed ATC.
        bellaUpsellCartSnapshot = bellaSnapshotCartItems(cart);
        return true;
      })
      .catch(function () {
        return true;
      });
  }
  window.BellaCartArmPreorderPreserve = function () {
    return armPreorderPreserveForAtc(getForm());
  };

  /** If ATC wiped preorder lines server-side, re-add them once with their properties. */
  function bellaRestoreWipedPreorderLines() {
    if (bellaIsUserCartEdit()) return Promise.resolve(false);
    if (bellaUpsellRebuildInFlight) return Promise.resolve(false);
    // Prefer full-cart snapshot when available (covers addons + preorder parents).
    if (bellaUpsellCartSnapshot && bellaUpsellCartSnapshot.length) {
      return bellaRestoreMissingSnapshotLines(bellaUpsellCartSnapshot, { blockMs: 5000 });
    }
    if (!bellaPreorderSnapshot || !bellaPreorderSnapshot.length) return Promise.resolve(false);
    const snap = bellaPreorderSnapshot.slice();
    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    return fetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return false;
        const missing = [];
        snap.forEach(function (s) {
          // Restore preorder parents + addon lines that vanished with them.
          if (!s.isPreorder && !s.isAddon) return;
          const still = cart.items.some(function (item) {
            if (!item) return false;
            if (s.key && item.key === s.key) return true;
            if (String(item.variant_id || item.id) !== String(s.variantId)) return false;
            if (s.isAddon) {
              const ip = item.properties || {};
              const sp = s.properties || {};
              return (
                String(ip._pa_parent_variant || '') === String(sp._pa_parent_variant || '') &&
                String(ip._pa_sticky_addon || '') === String(sp._pa_sticky_addon || '')
              );
            }
            return isPreorderCartLine(item, getForm());
          });
          if (!still) missing.push(s);
        });
        if (!missing.length) {
          bellaPreorderSnapshot = null;
          const form = getForm();
          if (form) delete form.dataset.bellaPreservePreorder;
          return false;
        }
        blockBellaCartWrites(8000);
        const ordered = missing.slice().sort(function (a, b) {
          if (a.isPreorder && !b.isPreorder) return -1;
          if (!a.isPreorder && b.isPreorder) return 1;
          if (a.isAddon && !b.isAddon) return 1;
          if (!a.isAddon && b.isAddon) return -1;
          return 0;
        });
        return ordered
          .reduce(function (chain, s) {
            return chain.then(function (anyOk) {
              return bellaAddRestoreRow(bellaBuildRestoreRow(s)).then(function (ok) {
                return anyOk || ok;
              });
            });
          }, Promise.resolve(false))
          .then(function (anyOk) {
            bellaPreorderSnapshot = null;
            const form = getForm();
            if (form) delete form.dataset.bellaPreservePreorder;
            if (anyOk) bellaRefreshCartDrawerSections();
            return anyOk;
          });
      })
      .catch(function () {
        return false;
      });
  }

  /** True while a cart date is being saved — skip reconcile/clear that causes the blink. */
  function isBellaDateSaveInFlight(form) {
    if (!form) return false;
    if (isBellaCartMergeLocked()) return true;
    const ctrl = formControllers.get(form);
    if (!ctrl) return false;
    return !!(ctrl._dateSyncBusy || ctrl._dateSaveUiTimer || ctrl._dateSyncPromise);
  }

  /** Every visible standard (non-preorder, non-addon) line must have its own Delivery Date. */
  function allStandardLinesHaveDates(form) {
    if (!form) return false;
    let hasStandard = false;
    let missing = false;
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true') return;
      if (isPreorderLineEl(li, form)) return;
      hasStandard = true;
      if (!readLineDateDisplay(li)) missing = true;
    });
    if (!hasStandard) return true;
    return !missing;
  }

  function hasStandardLineEl(form) {
    if (!form) return false;
    let found = false;
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (found) return;
      if (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true') return;
      if (isPreorderLineEl(li, form)) return;
      found = true;
    });
    return found;
  }

  function buildEmptyBellaAttributes() {
    const empty = {
      'Delivery region': '',
      'Delivery Date': '',
      'Delivery Day': '',
      'Customer TimeZone': '',
      'bella-date-source': '',
      'delivery-date': '',
    };
    BellaLegacyAttributeKeys.forEach(function (k) {
      empty[k] = '';
    });
    return empty;
  }

  function resetBellaFormDeliveryDom(form) {
    if (!form) return;
    form.dataset.deliveryDate = '';
    form.dataset.deliveryDateDisplay = '';
    form.dataset.bellaDateSource = '';
    form.dataset.deliveryRegion = form.dataset.deliveryRegion || 'dubai';
    form.dataset.bellaGiftNote = '';
    form.dataset.cartNote = '';
    const ctrl = formControllers.get(form);
    if (ctrl) {
      ctrl.calSelected = null;
      try {
        ctrl.setRegionUI(form.dataset.deliveryRegion || 'dubai');
      } catch (err) {
        /* ignore */
      }
      ctrl.updateFooterTotals();
      ctrl.updateStepper();
    }
  }

  /** After empty-cart clear, block date rehydrate from stale attrs on the next ATC. */
  let bellaDeliveryClearedUntil = 0;
  function bellaMarkDeliveryCleared(ms) {
    const until = Date.now() + (ms || 12000);
    if (until > bellaDeliveryClearedUntil) bellaDeliveryClearedUntil = until;
  }
  function bellaDeliveryWasJustCleared() {
    return Date.now() < bellaDeliveryClearedUntil;
  }

  /** Clear delivery date / gift from cart attributes + note (survives empty cart otherwise). */
  let bellaClearDeliveryInFlight = null;
  let bellaClearDeliveryCooldownUntil = 0;
  let bellaClearDeliveryLastAt = 0;

  /**
   * Clear cart delivery attrs/note when basket is empty.
   * opts.force — bypass write-block / cooldown (needed after shopper empties cart during delete locks).
   */
  function clearBellaCartDeliveryState(opts) {
    opts = opts || {};
    const force = !!opts.force;
    const liveForm = getForm();
    // Never wipe a date the shopper just saved (reconcile race caused the blink).
    if (!force && isBellaDateSaveInFlight(liveForm)) return Promise.resolve();
    // Preorder → normal ATC: never clear attrs (cart/update retriggers Addons wipe loop).
    if (!force && bellaCartWritesBlocked()) return Promise.resolve();
    if (!force && liveForm && liveForm.dataset.bellaPreservePreorder === '1') return Promise.resolve();
    // Hard stop: Product Addons publishes cart:updated after every cart/update — without
    // a cooldown we loop cart/update → addons → cart:updated → clear → 429.
    const now = Date.now();
    if (!force && now < bellaClearDeliveryCooldownUntil) return Promise.resolve();
    if (bellaClearDeliveryInFlight) return bellaClearDeliveryInFlight;

    // Never clear while the drawer still shows line items (false empty from addons).
    if (
      liveForm &&
      liveForm.querySelector(
        'cart-items li[data-line], cart-items li[id^="CartItem-"], .bella-cart__items li'
      )
    ) {
      return Promise.resolve();
    }

    bellaClearDeliveryCooldownUntil = now + (force ? 1500 : 4000);
    bellaClearDeliveryLastAt = now;
    armBellaCartMergeLock(force ? 1500 : 4000);

    // Reset DOM immediately so a fast re-ATC cannot inherit the old date from dataset.
    resetBellaFormDeliveryDom(liveForm);
    bellaMarkDeliveryCleared(12000);
    document.querySelectorAll('[data-bella-pdp-delivery]').forEach(function (root) {
      root.dataset.deliveryDate = '';
      root.dataset.deliveryDateDisplay = '';
      root.dataset.bellaDateSource = '';
      root.dataset.bellaGiftNote = '';
    });

    const empty = buildEmptyBellaAttributes();
    const body = cartUpdateBody({ attributes: empty, note: '' }, { attributesOnly: true });
    // Must use allow-listed fetch — foreign write shield blocks /cart/update during delete.
    const doFetch =
      typeof bellaOwnCartFetch === 'function'
        ? bellaOwnCartFetch
        : typeof bellaCartMutateFetch === 'function'
          ? bellaCartMutateFetch
          : fetch;
    bellaClearDeliveryInFlight = doFetch(theme.routes.cart_update_url, { ...fetchConfig(), body })
      .then(function (r) {
        return r.json();
      })
      .then(function (parsed) {
        if (parsed && parsed.bella_blocked) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[Bella cart] clear delivery state blocked');
          }
          return parsed;
        }
        if (parsed && parsed.errors) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[Bella cart] clear delivery state', parsed.errors);
          }
          return parsed;
        }
        // Bail if a date save started while this clear was in flight (unless forced empty clear).
        if (!force && isBellaDateSaveInFlight(getForm())) return parsed;
        // Re-check emptiness — addons may have refilled the cart during our update.
        const formNow = getForm();
        if (
          formNow &&
          formNow.querySelector(
            'cart-items li[data-line], cart-items li[id^="CartItem-"], .bella-cart__items li'
          )
        ) {
          return parsed;
        }
        if (parsed && typeof parsed.item_count === 'number' && parsed.item_count > 0) {
          return parsed;
        }
        resetBellaFormDeliveryDom(getForm());
        document.querySelectorAll('[data-bella-pdp-delivery]').forEach(function (root) {
          root.dataset.deliveryDate = '';
          root.dataset.deliveryDateDisplay = '';
          root.dataset.bellaDateSource = '';
          root.dataset.bellaGiftNote = '';
          const ctrl = getCtrl(root, false);
          if (ctrl) {
            ctrl.calSelected = null;
            ctrl.setRegionUI('dubai');
            ctrl.updateFooterTotals();
          }
        });
        return parsed;
      })
      .finally(function () {
        bellaClearDeliveryInFlight = null;
        armBellaCartMergeLock(1500);
      });

    return bellaClearDeliveryInFlight;
  }

  function maybeClearBellaStateIfCartEmpty(cartLike) {
    // Ignore empty snapshots mid Product-Addons mutation (they publish incomplete carts).
    if (bellaUpsellRebuildInFlight || bellaUpsellCartSnapshot) return Promise.resolve(false);

    // cart.js sometimes passes a JSON string in cart:updated detail.
    if (typeof cartLike === 'string') {
      try {
        cartLike = JSON.parse(cartLike);
      } catch (err) {
        cartLike = null;
      }
    }

    if (!cartLike && !bellaIsUserCartEdit()) return Promise.resolve(false);

    const form = getForm();
    const hasDomLines = !!(
      form &&
      form.querySelector(
        'cart-items li[data-line], cart-items li[id^="CartItem-"], .bella-cart__items li'
      )
    );
    if (hasDomLines) return Promise.resolve(false);

    let count = -1;
    if (cartLike && typeof cartLike.item_count === 'number') count = cartLike.item_count;
    else if (cartLike && Array.isArray(cartLike.items)) count = cartLike.items.length;
    // After shopper delete, DOM may already be empty while form.is-empty lags — still clear.
    if (count < 0 && bellaIsUserCartEdit() && form && form.classList.contains('is-empty')) {
      count = 0;
    }
    if (count < 0 && bellaIsUserCartEdit() && !hasDomLines) {
      count = 0;
    }
    // Missing count (e.g. { source: '…' }) — do not treat as empty unless user emptied cart.
    if (count < 0) return Promise.resolve(false);
    if (count !== 0) return Promise.resolve(false);

    // Confirm with cart.js once — never clear from a transient empty payload alone.
    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    const confirmFetch =
      typeof bellaCartMutateFetch === 'function' ? bellaCartMutateFetch : fetch;
    return confirmFetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number' || cart.item_count > 0) {
          return false;
        }
        return clearBellaCartDeliveryState({ force: true }).then(function () {
          const live = getForm();
          if (live) {
            live.classList.add('is-empty');
            resetBellaFormDeliveryDom(live);
            const ctrl = formControllers.get(live) || getCtrl(live, false);
            if (ctrl) {
              try {
                ctrl.updateStepper();
                ctrl.updateFooterTotals();
              } catch (err) {
                /* ignore */
              }
            }
          }
          return true;
        });
      })
      .catch(function () {
        return false;
      });
  }

  /**
   * If cart attributes say a date is set but no line shows one, attributes are stale
   * (e.g. emptied cart then re-added). Clear attrs so the shopper must pick again.
   */
  function reconcileStaleBellaDeliveryState(form) {
    if (!form) return Promise.resolve();
    if (isBellaDateSaveInFlight(form)) return Promise.resolve();
    if (form.classList.contains('is-empty') || bellaDeliveryWasJustCleared()) {
      return clearBellaCartDeliveryState({ force: true });
    }
    const mode = form.dataset.bellaCartMode || 'standard';
    if (mode === 'preorder-only') return Promise.resolve();
    const source = form.dataset.bellaDateSource || '';
    const hasAttrDate = source === 'cart' && !!(form.dataset.deliveryDate || form.dataset.deliveryDateDisplay);
    if (!hasAttrDate) return Promise.resolve();
    if (cartHasVisibleLineDate(form) && allStandardLinesHaveDates(form)) return Promise.resolve();
    // No visible line dates but attrs still claim a cart date → stale after empty-cart.
    // Do not re-stamp; clear so the date step opens again.
    if (!cartHasVisibleLineDate(form)) {
      return clearBellaCartDeliveryState({ force: true });
    }
    return applySelectedDateToBlankLines(form, { paintOnly: true });
  }

  /**
   * Fill normal lines that have no Delivery Date yet (e.g. upsell just added).
   * Only when the shopper already chose a cart-level date — never copy a pre-order date.
   * opts.paintOnly — DOM only (no cart/change); use after ATC to avoid blink loops.
   */
  function applySelectedDateToBlankLines(form, opts) {
    if (!form) return Promise.resolve();
    if (bellaDeliveryWasJustCleared()) return Promise.resolve();
    if (form.dataset.bellaDateSource !== 'cart') return Promise.resolve();
    opts = opts || {};

    const lineDateMode = getNormalLineDateMode(form);

    let dateDisplay = String(form.dataset.deliveryDateDisplay || '').trim();
    if (!dateDisplay && form.dataset.deliveryDate) {
      const parts = formatAdminOrderDateParts(form.dataset.deliveryDate);
      dateDisplay = parts.date || '';
    }
    if (!dateDisplay) dateDisplay = resolveSelectedDeliveryDateDisplay(form);
    if (!dateDisplay) return Promise.resolve();

    if (!form.dataset.deliveryDateDisplay) {
      form.dataset.deliveryDateDisplay = dateDisplay;
    }

    // All modes: once a cart date exists, new normal lines get it (no "pick date again").
    // shared / forceReplace: overwrite every normal line. inherit / per_line: fill blanks only.
    const replaceExisting = lineDateMode === 'shared' || opts.forceReplace === true;
    paintCartLineDeliveryDates(form, dateDisplay, { replaceExisting: replaceExisting });
    syncAddonDatePaintFromParents(form, { dateDisplay: dateDisplay });

    if (opts.paintOnly) {
      const ctrl = formControllers.get(form);
      if (ctrl) ctrl.updateFooterTotals({ preserveDeliveryPickers: !!opts.preserveDeliveryPickers });
      return Promise.resolve();
    }

    // shared: force every normal line in cart props; inherit/per_line: fill blanks in cart props.
    const replaceDate = lineDateMode === 'shared' || opts.forceReplace === true;
    return syncCheckoutLineProperties(form, { date: dateDisplay, replaceDate: replaceDate }).then(function () {
      paintCartLineDeliveryDates(form, dateDisplay, { replaceExisting: replaceExisting });
      syncAddonDatePaintFromParents(form, { dateDisplay: dateDisplay });
      const ctrl = formControllers.get(form);
      if (ctrl) ctrl.updateFooterTotals();
    });
  }

  /**
   * When there is no cart-chosen date, strip Delivery Date from standard lines
   * (they may have inherited a pre-order date from an earlier buggy sync).
   * opts.paintOnly — DOM only (default for refresh paths; cart/change loops with addons).
   */
  function clearInheritedStandardLineDates(form, opts) {
    if (!form) return Promise.resolve();
    if (isBellaDateSaveInFlight(form)) return Promise.resolve();
    if (form.dataset.bellaDateSource === 'cart') return Promise.resolve();
    if ((form.dataset.bellaCartMode || '') === 'preorder-only') return Promise.resolve();
    opts = opts || {};

    const propLabel = getDeliveryPropLabel(form);
    let touched = false;
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      // Strip orphan dates from standard + addon lines when shopper hasn't picked cart date.
      // (Addons sometimes keep a stale Delivery Date and fight the date sheet into a loop.)
      if (isPreorderLineEl(li, form)) return;
      if (!readLineDateDisplay(li)) return;
      touched = true;
      const propsEl = li.querySelector('.bella-cart__line-props');
      const dateEl = findLineDatePropEl(propsEl, propLabel);
      if (dateEl) dateEl.remove();
      if (propsEl && !propsEl.querySelector('.bella-cart__line-prop')) propsEl.remove();
    });
    if (!touched) return Promise.resolve();

    // Refresh / addon carts: never cart/change here — that races Product Addons into a loop.
    if (opts.paintOnly !== false) {
      const ctrl = formControllers.get(form);
      if (ctrl) ctrl.updateFooterTotals({ preserveDeliveryPickers: true });
      return Promise.resolve();
    }

    return syncCheckoutLineProperties(form, {
      date: '',
      replaceDate: true,
      clearStandardDates: true,
    }).then(function () {
      const ctrl = formControllers.get(form);
      if (ctrl) ctrl.updateFooterTotals();
    });
  }

  function reconcileLineDatesAfterCartChange(form) {
    // Always paint-only after cart mutations. Writing line props here re-enters
    // Product Addons / section refresh and loops the drawer after date select.
    return clearInheritedStandardLineDates(form, { paintOnly: true })
      .then(function () {
        return applySelectedDateToBlankLines(form, { paintOnly: true });
      })
      .then(function () {
        return syncAddonDatesFromParents(form, { paintOnly: true });
      });
  }

  /**
   * Checkout only shows line item properties under each product.
   * Prefer lineIndex (stable) over lineKey (changes when properties update).
   */
  function syncCheckoutLineProperties(form, overrides) {
    if (!form) return Promise.resolve();
    // Block property writes during preorder→normal ATC (stale cart/change wiped preorder lines).
    // Date saves must still run (shopper picking delivery after upsell).
    if (
      bellaCartWritesBlocked() &&
      !(overrides && (overrides.allowDuringPreorderPreserve || overrides.allowDuringWriteBlock))
    ) {
      return Promise.resolve();
    }

    const run = function () {
      const patch = buildCheckoutLinePropertyPatch(form, overrides);
      const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
      const changeUrl = theme.routes.cart_change_url || '/cart/change.js';
      const onlyLineKey = overrides && overrides.lineKey ? String(overrides.lineKey) : '';
      const onlyLineIndex =
        overrides && overrides.lineIndex ? parseInt(overrides.lineIndex, 10) || 0 : 0;
      const replaceDate = !!(overrides && overrides.replaceDate);
      const applyToPreorder = !!(overrides && overrides.applyToPreorder);
      const scoped = !!(onlyLineIndex || onlyLineKey);

      return fetch(cartUrl, { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;

          let targets = [];
          if (onlyLineIndex > 0 && cart.items[onlyLineIndex - 1]) {
            targets = [{ item: cart.items[onlyLineIndex - 1], line: onlyLineIndex }];
          } else {
            const giftPropUpdate =
              !!(overrides && Object.prototype.hasOwnProperty.call(overrides, 'giftYes'));
            cart.items.forEach(function (item, idx) {
              if (!item) return;
              if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
              // Never stamp cart-level Delivery Date onto product-addon lines — except same-mode gift.
              if (
                !scoped &&
                isAddonCartLine(item) &&
                !(overrides && overrides.applyToAddon) &&
                !giftPropUpdate
              ) {
                return;
              }
              // Skip preorder for date-only syncs; gift updates must still reach them.
              if (!scoped && isPreorderCartLine(item, form) && !applyToPreorder && !giftPropUpdate) {
                return;
              }
              if (onlyLineKey && item.key !== onlyLineKey) return;
              targets.push({ item: item, line: idx + 1 });
            });
          }

          return targets.reduce(function (chain, target) {
            return chain.then(function () {
              const item = target.item;
              const nextProps = applyOrderedCheckoutLineProperties(item.properties, patch, form, {
                isPreorder: isPreorderCartLine(item, form),
                applyToPreorder: applyToPreorder || scoped,
                replaceDate: replaceDate || scoped,
                clearStandardDates: !!(overrides && overrides.clearStandardDates),
              });
              if (checkoutLinePropertiesEqual(item.properties, nextProps)) return null;

              // Never drop Product Addons private props — that can orphan/remove the bundle.
              Object.keys(item.properties || {}).forEach(function (key) {
                if (key.charAt(0) === '_' && nextProps[key] == null) {
                  nextProps[key] = item.properties[key];
                }
              });

              const qty = parseInt(item.quantity, 10);
              const body = {
                quantity: qty > 0 ? qty : 1,
                properties: nextProps,
              };
              // Prefer stable line-item key so index shifts cannot wipe other lines.
              if (item.key) body.id = item.key;
              else body.line = target.line;

              return bellaOwnCartFetch(changeUrl, {
                ...fetchConfig(),
                body: JSON.stringify(body),
              }).then(function (r) {
                return r.json();
              }).then(function (updatedCart) {
                if (!updatedCart || !Array.isArray(updatedCart.items)) return updatedCart;
                const updatedItem =
                  (item.key &&
                    updatedCart.items.find(function (it) {
                      return it && it.key === item.key;
                    })) ||
                  updatedCart.items[target.line - 1];
                if (updatedItem && updatedItem.key) {
                  const li =
                    form.querySelector('cart-items li[data-line-key="' + item.key + '"]') ||
                    form.querySelector('cart-items li[data-line="' + target.line + '"]');
                  if (li) {
                    li.setAttribute('data-line-key', updatedItem.key);
                    const newLine =
                      updatedCart.items.findIndex(function (it) {
                        return it && it.key === updatedItem.key;
                      }) + 1;
                    li.querySelectorAll('[data-bella-line-date-edit], [data-bella-line-gift-edit]').forEach(function (btn) {
                      btn.setAttribute('data-line-key', updatedItem.key);
                      if (newLine > 0) btn.setAttribute('data-line-index', String(newLine));
                    });
                  }
                }
                return updatedCart;
              });
            });
          }, Promise.resolve());
        })
        .catch(function (e) {
          console.error('[Bella cart] sync checkout line properties', e);
        });
    };

    bellaLinePropsSyncChain = bellaLinePropsSyncChain.then(run, run);
    return bellaLinePropsSyncChain;
  }

  /**
   * Push each line's visible Delivery Date into real cart line properties
   * so checkout matches what the shopper sees in the drawer.
   */
  function syncVisibleLineDatesToCart(form) {
    if (!form) return Promise.resolve();

    const run = function () {
      const dateLabel = getDeliveryPropLabel(form);
      const giftLabel = getGiftPropLabel(form);
      const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
      const changeUrl = theme.routes.cart_change_url || '/cart/change.js';
      const hasGift = !!(form.dataset.bellaGiftNote && String(form.dataset.bellaGiftNote).trim());
      const giftValue = hasGift ? getGiftPropYes(form) : '';

      const lineUpdates = [];
      form.querySelectorAll('cart-items li[data-line]').forEach(function (li) {
        if (li.getAttribute('data-pa-addon') === 'true') return;
        const line = getCartLineIndex(li);
        if (!line) return;
        const dateDisplay = readLineDateDisplay(li);
        lineUpdates.push({ line: line, dateDisplay: dateDisplay });
      });

      if (!lineUpdates.length) return Promise.resolve();

      return fetch(cartUrl, { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) return null;

          return lineUpdates.reduce(function (chain, update) {
            return chain.then(function () {
              const item = cart.items[update.line - 1];
              if (!item) return null;
              if (item.properties && item.properties._pa_hide_in_cart === 'true') return null;

              const patch = {};
              patch[dateLabel] = update.dateDisplay || '';
              if (getGiftNoteMode(form) !== 'per_product') {
                patch[giftLabel] = giftValue;
              }

              const lineIsPreorder = isPreorderCartLine(item, form);
              const nextProps = applyOrderedCheckoutLineProperties(item.properties, patch, form, {
                isPreorder: lineIsPreorder,
                // Cart-wide DOM sync must not overwrite preorder dates (only pencil edit may).
                applyToPreorder: false,
                replaceDate: !lineIsPreorder,
              });
              // If the line has no visible date, still keep any existing date rather than clearing.
              if (!update.dateDisplay || lineIsPreorder) {
                const existingDate =
                  (item.properties && (item.properties[dateLabel] || item.properties['Delivery Date'])) ||
                  '';
                if (existingDate) nextProps[dateLabel] = existingDate;
                else if (!lineIsPreorder) delete nextProps[dateLabel];
              }
              if (checkoutLinePropertiesEqual(item.properties, nextProps)) return null;

              Object.keys(item.properties || {}).forEach(function (key) {
                if (key.charAt(0) === '_' && nextProps[key] == null) {
                  nextProps[key] = item.properties[key];
                }
              });

              const qty = parseInt(item.quantity, 10);
              const body = {
                quantity: qty > 0 ? qty : 1,
                properties: nextProps,
              };
              if (item.key) body.id = item.key;
              else body.line = update.line;

              return bellaOwnCartFetch(changeUrl, {
                ...fetchConfig(),
                body: JSON.stringify(body),
              })
                .then(function (r) {
                  return r.json();
                })
                .then(function (updatedCart) {
                  if (updatedCart && Array.isArray(updatedCart.items)) {
                    cart.items = updatedCart.items;
                    const updatedItem =
                      (item.key &&
                        updatedCart.items.find(function (it) {
                          return it && it.key === item.key;
                        })) ||
                      updatedCart.items[update.line - 1];
                    if (updatedItem && updatedItem.key) {
                      const li =
                        form.querySelector('cart-items li[data-line-key="' + item.key + '"]') ||
                        form.querySelector('cart-items li[data-line="' + update.line + '"]');
                      if (li) {
                        li.setAttribute('data-line-key', updatedItem.key);
                        li.querySelectorAll('[data-bella-line-date-edit]').forEach(function (btn) {
                          btn.setAttribute('data-line-key', updatedItem.key);
                          btn.setAttribute('data-line-index', String(update.line));
                        });
                      }
                    }
                  }
                  return updatedCart;
                });
            });
          }, Promise.resolve());
        })
        .catch(function (e) {
          console.error('[Bella cart] sync visible line dates', e);
        });
    };

    bellaLinePropsSyncChain = bellaLinePropsSyncChain.then(run, run);
    return bellaLinePropsSyncChain;
  }

  /** @deprecated use syncCheckoutLineProperties */
  function syncDeliveryDateToLineItems(form, dateDisplay) {
    return syncCheckoutLineProperties(form, { date: dateDisplay || '' });
  }

  class BellaCartController {
    constructor(form) {
      this.form = form;
      this.readConfig();
      this.onKey = this.onKey.bind(this);
      this._sheetOpenTimer = null;
      this._savedMainScrollTop = null;
      this._cartUpdateAbort = null;
      this._editLineKey = '';
      this._editLineIndex = 0;
      this._editLineDateDisplay = '';
      this._editLinePreorder = false;
      this._editLineRegion = '';
      this._dateSaveUiTimer = null;
      this._dateSyncBusy = false;
      this._dateSyncPromise = null;
      this._checkoutLabelBeforeSync = '';
    }

    readConfig() {
      const ds = this.form.dataset;
      this.strings = {
        today: ds.strToday || 'Today',
        tomorrow: ds.strTomorrow || 'Tomorrow',
        selectDate: ds.strSelectDate || 'Tap to choose a date',
        giftPlaceholder: ds.strGiftPlaceholder || 'Add a message (optional)',
        checkout: ds.strCheckout || 'Checkout',
        selectDateCta: ds.strSelectDateCta || 'Select a delivery date to checkout',
      };
    }

    syncFromDom() {
      this.readConfig();
      if (!this.form.dataset.deliveryDate && this.form.dataset.deliveryDateDisplay) {
        const iso = parseAdminDeliveryDate(this.form.dataset.deliveryDateDisplay);
        if (iso) this.form.dataset.deliveryDate = iso;
      }
      if (this.form.dataset.cartNote !== undefined) {
        this.form.dataset.bellaGiftNote = this.form.dataset.cartNote || '';
      }
      this.initCalendar();
      this.initGiftField();
      this.updateFooterTotals();
    }

    onKey(e) {
      if (e.code === 'Escape') {
        this.form.querySelectorAll('.bella-sheet.is-open').forEach((s) => this.closeSheet(s.id));
      }
    }

    openSheet(which, opts) {
      opts = opts || {};
      if (this._sheetOpenTimer) {
        clearTimeout(this._sheetOpenTimer);
        this._sheetOpenTimer = null;
      }
      if (which === 'date') {
        this._editLineKey = opts.lineKey ? String(opts.lineKey) : '';
        this._editLineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
        this._editLineDateDisplay = opts.lineDateDisplay ? String(opts.lineDateDisplay) : '';
        this._editLinePreorder = !!opts.linePreorder;
        this._editLineRegion = opts.lineRegion ? String(opts.lineRegion) : '';
        this._editGiftLineKey = '';
        this._editGiftLineIndex = 0;
      } else if (which === 'gift') {
        this._editGiftLineKey = opts.lineKey ? String(opts.lineKey) : '';
        this._editGiftLineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
        this._editLineKey = '';
        this._editLineIndex = 0;
        this._editLineDateDisplay = '';
        this._editLinePreorder = false;
        this._editLineRegion = '';
      } else {
        this._editLineKey = '';
        this._editLineIndex = 0;
        this._editLineDateDisplay = '';
        this._editLinePreorder = false;
        this._editLineRegion = '';
        this._editGiftLineKey = '';
        this._editGiftLineIndex = 0;
      }
      // Defer past the opening pointer/click so the same gesture cannot hit the sheet backdrop.
      this._sheetOpenTimer = setTimeout(() => {
        this._sheetOpenTimer = null;
        const sheet = findBellaSheet(this.form, which);
        if (!sheet) return;
        const main = this.form.querySelector('.mini-cart__main');
        this._savedMainScrollTop = main ? main.scrollTop : null;
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', this.onKey);
        if (which === 'date') {
          let seedIso = '';
          if (this._editLineDateDisplay) {
            seedIso = parseAdminDeliveryDate(this._editLineDateDisplay) || '';
          }
          if (!seedIso) seedIso = this.form.dataset.deliveryDate || '';
          if (seedIso) this.calSelected = parseISODate(seedIso);
          this.renderCalendar();
        }
        if (which === 'gift') {
          const ta = this.form.querySelector('[data-bella-gift-input]');
          if (ta) {
            let seed = '';
            if (getGiftNoteMode(this.form) === 'per_product' && (this._editGiftLineKey || this._editGiftLineIndex)) {
              const li =
                (this._editGiftLineKey &&
                  this.form.querySelector(
                    'cart-items li[data-line-key="' + this._editGiftLineKey.replace(/"/g, '') + '"]'
                  )) ||
                (this._editGiftLineIndex &&
                  this.form.querySelector('cart-items li[data-line="' + this._editGiftLineIndex + '"]'));
              seed = readLineGiftMessage(li) || '';
            } else {
              seed = this.form.dataset.bellaGiftNote || this.form.dataset.cartNote || '';
            }
            ta.value = seed;
            this.syncGiftCharCount();
            requestAnimationFrame(() => {
              try {
                ta.focus({ preventScroll: true });
              } catch (err) {
                ta.focus();
              }
            });
          }
        }
      }, 60);
    }

    closeSheet(idOrWhich, opts) {
      opts = opts || {};
      if (this._sheetOpenTimer) {
        clearTimeout(this._sheetOpenTimer);
        this._sheetOpenTimer = null;
      }
      let sheet = null;
      let which = null;
      if (idOrWhich) {
        if (typeof idOrWhich === 'string' && (idOrWhich === 'date' || idOrWhich === 'gift' || idOrWhich === 'recs')) {
          which = idOrWhich;
          sheet = findBellaSheet(this.form, which);
        } else if (typeof idOrWhich === 'string') {
          sheet = this.form.querySelector('#' + idOrWhich);
          which = sheet ? sheet.getAttribute('data-bella-sheet') : idOrWhich.replace('bella-sheet-', '');
          if (!sheet && which) sheet = findBellaSheet(this.form, which);
        }
      }
      if (sheet) {
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
      }
      const stillOpen = this.form.querySelector('.bella-sheet.is-open');
      if (!stillOpen) {
        document.removeEventListener('keydown', this.onKey);
        if (!opts.skipScrollRestore) {
          const main = this.form.querySelector('.mini-cart__main');
          if (main && this._savedMainScrollTop != null) {
            const top = this._savedMainScrollTop;
            requestAnimationFrame(() => {
              main.scrollTop = top;
            });
          }
          const editLineKey = this._editLineKey || '';
          const opener =
            which === 'gift'
              ? this.form.querySelector('[data-bella-open="gift"]')
              : which === 'date' && editLineKey
                ? this.form.querySelector(
                    '[data-bella-line-date-edit][data-line-key="' + editLineKey.replace(/"/g, '') + '"]'
                  )
                : which === 'date'
                  ? this.form.querySelector('[data-bella-open="date"]')
                  : null;
          if (opener) {
            requestAnimationFrame(() => {
              try {
                opener.focus({ preventScroll: true });
              } catch (err) {
                opener.focus();
              }
            });
          }
        }
        this._savedMainScrollTop = null;
        if (!opts.keepEditLine) {
          this._editLineKey = '';
          this._editLineIndex = 0;
          this._editLineDateDisplay = '';
          this._editLinePreorder = false;
          this._editLineRegion = '';
        }
      }
    }

    setRegionUI(region) {
      this.form.querySelectorAll('[data-bella-region]').forEach((b) => {
        b.classList.toggle('is-selected', b.getAttribute('data-bella-region') === region);
      });
      const segment = this.form.querySelector('[data-bella-segment]');
      if (segment) {
        segment.classList.toggle('is-region-other', region === 'other');
      }
      const dubaiBox = this.form.querySelector('[data-bella-info="dubai"]');
      const otherBox = this.form.querySelector('[data-bella-info="other"]');
      if (dubaiBox && otherBox) {
        dubaiBox.hidden = region !== 'dubai';
        otherBox.hidden = region !== 'other';
      }
      this.form.dataset.deliveryRegion = region;
      this.updateFooterTotals();
    }

    setDateSyncBusy(busy) {
      this._dateSyncBusy = !!busy;
      const btn = this.form.querySelector('[data-bella-checkout]');
      if (!btn) return;
      if (busy) {
        // Quiet lock only — no "Updating…" flash (that blinked on first date select).
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
        // Never leave Checkout stuck disabled while the sheet-close timer runs.
        if (this._dateSaveUiTimer) {
          if (this.cartDeliveryReady()) btn.disabled = false;
          return;
        }
        this.updateFooterTotals({ preserveDeliveryPickers: false });
      }
    }

    waitForDateSync() {
      if (!this._dateSyncBusy || !this._dateSyncPromise) return Promise.resolve();
      return Promise.resolve(this._dateSyncPromise).catch(function () {});
    }

    persistAttributes(attrs, opts) {
      opts = opts || {};
      if (this._cartUpdateAbort) {
        try {
          this._cartUpdateAbort.abort();
        } catch (err) {
          /* ignore */
        }
      }
      this._cartUpdateAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const signal = this._cartUpdateAbort ? this._cartUpdateAbort.signal : undefined;

      const fullAttrs = buildBellaCartAttributesPayload(this.form);
      const body = cartUpdateBody({ attributes: fullAttrs }, { attributesOnly: true });
      const fetchOpts = { ...fetchConfig(), body };
      if (signal) fetchOpts.signal = signal;

      const run = bellaOwnCartFetch(theme.routes.cart_update_url, fetchOpts)
        .then((r) => r.json())
        .then((parsed) => {
          if (signal && signal.aborted) return;
          if (parsed && parsed.errors) {
            console.error('[Bella cart] cart/update', parsed.errors);
            if (opts.revertOnError) {
              const r = opts.revertOnError;
              if (Object.prototype.hasOwnProperty.call(r, 'deliveryDate')) {
                this.form.dataset.deliveryDate = r.deliveryDate;
              }
              if (Object.prototype.hasOwnProperty.call(r, 'bellaDateSource')) {
                this.form.dataset.bellaDateSource = r.bellaDateSource;
              }
              const revertParts = formatAdminOrderDateParts(this.form.dataset.deliveryDate || '');
              this.form.dataset.deliveryDateDisplay =
                this.form.dataset.bellaDateSource === 'cart' ? revertParts.date || '' : '';
              const revertLineKey = opts.lineKey ? String(opts.lineKey) : '';
              if (revertLineKey && Object.prototype.hasOwnProperty.call(r, 'lineDateDisplay')) {
                paintCartLineDeliveryDates(this.form, r.lineDateDisplay || '', { lineKey: revertLineKey });
              } else {
                paintCartLineDeliveryDates(
                  this.form,
                  this.form.dataset.bellaDateSource === 'cart' ? revertParts.date || '' : ''
                );
              }
              this.updateFooterTotals();
              this.initCalendar();
            }
            return;
          }
          if (!opts.alreadyClosed && opts.closeSheet) {
            this.closeSheet(opts.closeSheet, { skipScrollRestore: true });
          }
          syncBellaCartAfterAttributeUpdate(parsed);
          // Line properties are what checkout shows under each product name.
          const dateDisplay = fullAttrs['Delivery Date'] || '';
          const lineKey = opts.lineKey ? String(opts.lineKey) : '';
          const lineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
          const replaceDate = opts.replaceDate === true;
          const applyToPreorder = opts.applyToPreorder === true || !!(lineKey || lineIndex);
          const paintOpts = lineIndex
            ? { lineKey: lineKey, lineIndex: lineIndex }
            : lineKey
              ? { lineKey: lineKey }
              : {};
          // Prefer syncing the visible line dates (source of truth for independent edits).
          if (opts.syncFromDom) {
            return syncVisibleLineDatesToCart(this.form).then(() => {
              this.updateFooterTotals({ preserveDeliveryPickers: !!this._dateSaveUiTimer });
            });
          }
          if (opts.skipLineSync) return;
          return syncCheckoutLineProperties(this.form, {
            date: dateDisplay,
            lineKey: lineKey,
            lineIndex: lineIndex,
            replaceDate: replaceDate || !!(lineKey || lineIndex),
            applyToPreorder: applyToPreorder,
            allowDuringWriteBlock: true,
          }).then(() => {
            if (!opts.skipPaintAfterSync) {
              paintCartLineDeliveryDates(this.form, dateDisplay, paintOpts);
            }
            this.updateFooterTotals({ preserveDeliveryPickers: !!this._dateSaveUiTimer });
          });
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error('[Bella cart] fetch', e);
          if (opts.revertOnError) {
            const r = opts.revertOnError;
            if (Object.prototype.hasOwnProperty.call(r, 'deliveryDate')) {
              this.form.dataset.deliveryDate = r.deliveryDate;
            }
            if (Object.prototype.hasOwnProperty.call(r, 'bellaDateSource')) {
              this.form.dataset.bellaDateSource = r.bellaDateSource;
            }
            const revertParts = formatAdminOrderDateParts(this.form.dataset.deliveryDate || '');
            this.form.dataset.deliveryDateDisplay =
              this.form.dataset.bellaDateSource === 'cart' ? revertParts.date || '' : '';
            const revertLineKey = opts.lineKey ? String(opts.lineKey) : '';
            if (revertLineKey && Object.prototype.hasOwnProperty.call(r, 'lineDateDisplay')) {
              paintCartLineDeliveryDates(this.form, r.lineDateDisplay || '', { lineKey: revertLineKey });
            } else {
              paintCartLineDeliveryDates(
                this.form,
                this.form.dataset.bellaDateSource === 'cart' ? revertParts.date || '' : ''
              );
            }
            this.updateFooterTotals();
            this.initCalendar();
          }
        });

      return run;
    }

    persistCartNote(note, opts) {
      opts = opts || {};
      if (this._cartUpdateAbort) {
        try {
          this._cartUpdateAbort.abort();
        } catch (err) {
          /* ignore */
        }
      }
      this._cartUpdateAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const signal = this._cartUpdateAbort ? this._cartUpdateAbort.signal : undefined;
      const body = cartUpdateBody({ note: note }, { attributesOnly: true });
      const fetchOpts = { ...fetchConfig(), body };
      if (signal) fetchOpts.signal = signal;
      const hasGift = !!(note && String(note).trim());

      fetch(theme.routes.cart_update_url, fetchOpts)
        .then((r) => r.json())
        .then((parsed) => {
          if (signal && signal.aborted) return;
          if (parsed && parsed.errors) {
            console.error('[Bella cart] cart/update note', parsed.errors);
            if (opts.revertOnError !== undefined) {
              this.form.dataset.bellaGiftNote = opts.revertOnError;
              this.updateFooterTotals();
              this.initGiftField();
            }
            return;
          }
          if (parsed.note !== undefined) {
            this.form.dataset.bellaGiftNote = parsed.note || '';
          }
          this.updateFooterTotals();
          this.initGiftField();
          syncBellaCartAfterAttributeUpdate(parsed);
          // Checkout shows Yes under each product; full message stays on cart.note.
          const liveForm = this.form;
          return syncCheckoutLineProperties(liveForm, {
            giftYes: hasGift,
            applyToAddon: true,
            applyToPreorder: true,
            allowDuringWriteBlock: true,
          }).then(function () {
            paintCartLineGiftNotes(liveForm);
          });
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error('[Bella cart] fetch note', e);
          if (opts.revertOnError !== undefined) {
            this.form.dataset.bellaGiftNote = opts.revertOnError;
            this.updateFooterTotals();
            this.initGiftField();
          }
        });
    }

    beginDateSync(promise) {
      const self = this;
      const tracked = Promise.resolve(promise).catch(function () {});
      this._dateSyncPromise = tracked;
      this.setDateSyncBusy(true);
      return tracked.finally(function () {
        if (self._dateSyncPromise === tracked) {
          self._dateSyncPromise = null;
          setLineDateLoading(self.form, false);
          self.setDateSyncBusy(false);
        }
      });
    }

    saveDate() {
      if (!this.calSelected) return;
      const editLineKey = this._editLineKey ? String(this._editLineKey) : '';
      const editLineIndex = this._editLineIndex ? parseInt(this._editLineIndex, 10) || 0 : 0;
      const prevLineDate = this._editLineDateDisplay || '';
      const isLineEdit = !!(editLineIndex || editLineKey);
      const isPreorderEdit = isLineEdit && !!this._editLinePreorder;
      const region =
        isPreorderEdit && this._editLineRegion
          ? this._editLineRegion
          : this.form.dataset.deliveryRegion || 'dubai';
      const dateContext = isPreorderEdit ? 'pdp' : 'cart';
      if (isBeforeMinSelectable(this.calSelected, region, dateContext)) return;
      const iso = isoDate(this.calSelected);
      const parts = formatAdminOrderDateParts(iso);
      const self = this;

      const finishUiAfterSheet = function () {
        if (self._dateSaveUiTimer) {
          clearTimeout(self._dateSaveUiTimer);
          self._dateSaveUiTimer = null;
        }
        // After sheet close animation, collapse pickers without a mid-animation jump.
        self._dateSaveUiTimer = setTimeout(function () {
          self._dateSaveUiTimer = null;
          withBellaQuietDom(function () {
            self.updateFooterTotals();
          });
        }, 320);
      };

      // Pre-order pencil edit: update that line only — never promote its date to cart-level.
      if (isPreorderEdit) {
        const parentLi = findCartLineEl(this.form, editLineKey, editLineIndex);
        const parentVid = parentLi ? String(parentLi.getAttribute('data-variant-id') || '') : '';
        const loadOpts = { lineKey: editLineKey, lineIndex: editLineIndex };
        armBellaCartMergeLock(2500);
        withBellaQuietDom(function () {
          // Loader first — paint date only after sync finishes (no blink).
          setLineDateLoading(self.form, true, loadOpts);
          self.closeSheet('date', { skipScrollRestore: true, keepEditLine: true });
          self.updateFooterTotals({ preserveDeliveryPickers: true });
        });
        finishUiAfterSheet();
        this.beginDateSync(
          syncCheckoutLineProperties(this.form, {
            date: parts.date || '',
            lineKey: editLineKey,
            lineIndex: editLineIndex,
            replaceDate: true,
            applyToPreorder: true,
            allowDuringWriteBlock: true,
          })
            .then(function () {
              return syncAddonDatesFromParents(self.form, {
                parentLineIndex: editLineIndex,
                parentVariantId: parentVid,
                date: parts.date || '',
                paintOnly: true,
              });
            })
            .then(function () {
              armBellaCartMergeLock(1500);
              withBellaQuietDom(function () {
                paintCartLineDeliveryDates(self.form, parts.date || '', loadOpts);
                setLineDateLoading(self.form, false, loadOpts);
                self.updateFooterTotals({ preserveDeliveryPickers: !!self._dateSaveUiTimer });
              });
            })
        );
        this._editLineKey = '';
        this._editLineIndex = 0;
        this._editLineDateDisplay = '';
        this._editLinePreorder = false;
        this._editLineRegion = '';
        return;
      }

      const prev = this.form.dataset.deliveryDate || '';
      const prevSource = this.form.dataset.bellaDateSource || '';
      const lineDateMode = getNormalLineDateMode(this.form);
      // shared: pencil edit updates every normal line. Otherwise one line (or all on first pick).
      const syncAllNormal = !isLineEdit || lineDateMode === 'shared';
      const parentLi = isLineEdit ? findCartLineEl(this.form, editLineKey, editLineIndex) : null;
      const parentVid = parentLi ? String(parentLi.getAttribute('data-variant-id') || '') : '';
      const loadOpts = syncAllNormal
        ? {}
        : { lineKey: editLineKey, lineIndex: editLineIndex };
      this.form.dataset.deliveryDate = iso;
      this.form.dataset.deliveryDateDisplay = parts.date || '';
      this.form.dataset.bellaDateSource = 'cart';
      // Block refresh/reconcile while line props sync — Product Addons + cart:updated caused a loop.
      armBellaCartMergeLock(3000);
      // First cart date: collapse pickers once after sheet close (avoid mid-save footer blink).
      const isFirstCartDate = !isLineEdit || syncAllNormal;
      withBellaQuietDom(function () {
        // Show loader on date rows; paint final date only after sync (stops blink/repaint).
        setLineDateLoading(self.form, true, loadOpts);
        self.closeSheet('date', { skipScrollRestore: true, keepEditLine: true });
        if (!isFirstCartDate) {
          self.updateFooterTotals({ preserveDeliveryPickers: true });
        }
      });
      finishUiAfterSheet();
      this.beginDateSync(
        this.persistAttributes(
          {},
          {
            alreadyClosed: true,
            lineKey: syncAllNormal ? '' : editLineKey,
            lineIndex: syncAllNormal ? 0 : editLineIndex,
            replaceDate: true,
            applyToPreorder: false,
            syncFromDom: false,
            skipPaintAfterSync: true,
            revertOnError: {
              deliveryDate: prev,
              bellaDateSource: prevSource,
              lineDateDisplay: prevLineDate,
            },
          }
        ).then(function () {
          self.form.dataset.bellaDateSource = 'cart';
          self.form.dataset.deliveryDate = iso;
          self.form.dataset.deliveryDateDisplay = parts.date || '';
          armBellaCartMergeLock(2000);
          var addonOpts = syncAllNormal
            ? { paintOnly: true }
            : {
                parentLineIndex: editLineIndex,
                parentVariantId: parentVid,
                date: parts.date || '',
                paintOnly: true,
              };
          return syncAddonDatesFromParents(self.form, addonOpts).then(function () {
            armBellaCartMergeLock(800);
            withBellaQuietDom(function () {
              paintCartLineDeliveryDates(
                self.form,
                parts.date || '',
                syncAllNormal
                  ? { replaceExisting: true }
                  : { lineKey: editLineKey, lineIndex: editLineIndex, replaceExisting: true }
              );
              syncAddonDatePaintFromParents(self.form, { dateDisplay: parts.date || '' });
              bellaEnsureStandardLineDatePencils(self.form);
              setLineDateLoading(self.form, false, loadOpts);
              if (!self._dateSaveUiTimer) self.updateFooterTotals();
            });
          });
        })
      );
      this._editLineKey = '';
      this._editLineIndex = 0;
      this._editLineDateDisplay = '';
      this._editLinePreorder = false;
      this._editLineRegion = '';
    }

    saveGift() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const val = ta ? ta.value.trim() : '';
      const giftMode = getGiftNoteMode(this.form);
      const lineKey = this._editGiftLineKey ? String(this._editGiftLineKey) : '';
      const lineIndex = this._editGiftLineIndex ? parseInt(this._editGiftLineIndex, 10) || 0 : 0;
      this.closeSheet('gift', { skipScrollRestore: true });
      this._editGiftLineKey = '';
      this._editGiftLineIndex = 0;

      if (giftMode === 'per_product') {
        if (lineKey || lineIndex) {
          return this.persistPerProductGift(val, { lineKey: lineKey, lineIndex: lineIndex });
        }
        return this.persistPerProductGiftToBlankLines(val);
      }

      const prev = this.form.dataset.bellaGiftNote || '';
      this.form.dataset.bellaGiftNote = val;
      this.form.dataset.cartNote = val;
      paintCartLineGiftNotes(this.form);
      this.updateFooterTotals();
      this.persistCartNote(val, { revertOnError: prev });
    }

    persistPerProductGift(message, opts) {
      opts = opts || {};
      const form = this.form;
      const lineKey = opts.lineKey ? String(opts.lineKey) : '';
      const lineIndex = opts.lineIndex ? parseInt(opts.lineIndex, 10) || 0 : 0;
      const hasGift = !!(message && String(message).trim());
      const msg = hasGift ? String(message).trim() : '';
      paintCartLineGiftNotes(form, {
        lineKey: lineKey,
        lineIndex: lineIndex,
        giftYes: hasGift,
        giftMessage: msg,
      });
      this.updateFooterTotals();

      return syncCheckoutLineProperties(form, {
        giftYes: hasGift,
        giftMessage: msg,
        lineKey: lineKey,
        lineIndex: lineIndex,
        allowDuringWriteBlock: true,
      }).then(function () {
        // Clear shared cart.note so ops don't see a conflicting single note.
        const body = cartUpdateBody({ note: '' }, { attributesOnly: true });
        return bellaOwnCartFetch(theme.routes.cart_update_url, { ...fetchConfig(), body }).then(function (r) {
          return r.json();
        }).then(function (parsed) {
          form.dataset.bellaGiftNote = '';
          form.dataset.cartNote = '';
          syncBellaCartAfterAttributeUpdate(parsed);
          paintCartLineGiftNotes(form);
          const ctrl = formControllers.get(form);
          if (ctrl) ctrl.updateFooterTotals();
        });
      });
    }

    persistPerProductGiftToBlankLines(message) {
      const form = this.form;
      const hasGift = !!(message && String(message).trim());
      const msg = hasGift ? String(message).trim() : '';
      if (!hasGift) {
        this.updateFooterTotals();
        return Promise.resolve();
      }

      const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
      const changeUrl = theme.routes.cart_change_url || '/cart/change.js';
      const giftLabel = getGiftPropLabel(form);
      const msgLabel = getGiftMessagePropLabel(form);
      const yes = getGiftPropYes(form);
      const self = this;

      return bellaOwnCartFetch(cartUrl, { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) return null;
          const targets = [];
          cart.items.forEach(function (item, idx) {
            if (!item || !item.key) return;
            if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
            if (isAddonCartLine(item)) return;
            const existingMsg = String(
              (item.properties && (item.properties[msgLabel] || item.properties['Gift message'])) || ''
            ).trim();
            if (existingMsg) return;
            targets.push({ item: item, line: idx + 1 });
          });
          if (!targets.length) {
            paintCartLineGiftNotes(form);
            return null;
          }
          return targets
            .reduce(function (chain, target) {
              return chain.then(function () {
                const nextProps = Object.assign({}, target.item.properties || {});
                nextProps[giftLabel] = yes;
                nextProps[msgLabel] = msg;
                Object.keys(target.item.properties || {}).forEach(function (key) {
                  if (key.charAt(0) === '_' && nextProps[key] == null) {
                    nextProps[key] = target.item.properties[key];
                  }
                });
                const qty = parseInt(target.item.quantity, 10);
                const body = {
                  id: target.item.key,
                  quantity: qty > 0 ? qty : 1,
                  properties: nextProps,
                };
                return bellaOwnCartFetch(changeUrl, {
                  ...fetchConfig(),
                  body: JSON.stringify(body),
                }).then(function (r) {
                  return r.json();
                });
              });
            }, Promise.resolve())
            .then(function () {
              form.dataset.bellaGiftNote = '';
              form.dataset.cartNote = '';
              const noteBody = cartUpdateBody({ note: '' }, { attributesOnly: true });
              return bellaOwnCartFetch(theme.routes.cart_update_url, {
                ...fetchConfig(),
                body: noteBody,
              }).then(function (r) {
                return r.json();
              });
            })
            .then(function () {
              // Seed DOM messages for lines we just filled.
              form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
                if (isAddonLineEl(li)) return;
                if (readLineGiftMessage(li)) return;
                const giftEl = li.querySelector('[data-bella-line-gift]');
                if (giftEl) giftEl.setAttribute('data-gift-message', msg);
              });
              paintCartLineGiftNotes(form);
              self.updateFooterTotals();
            });
        });
    }

    initGiftField() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const saved = this.form.dataset.bellaGiftNote || '';
      if (ta && !(this._editGiftLineKey || this._editGiftLineIndex)) ta.value = saved;
      this.syncGiftCharCount();
    }

    syncGiftCharCount() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const countEl = this.form.querySelector('[data-bella-gift-count]');
      if (!ta || !countEl) return;
      const max = parseInt(ta.getAttribute('maxlength') || '500', 10) || 500;
      const n = ta.value.length;
      countEl.textContent = n + '/' + max;
    }

    initCalendar() {
      this.calView = new Date();
      const mode = this.form.dataset.bellaCartMode || 'standard';
      const source = this.form.dataset.bellaDateSource || '';
      const iso = mode !== 'preorder-only' && source !== 'cart' ? '' : this.form.dataset.deliveryDate;
      const region = this.form.dataset.deliveryRegion || 'dubai';
      if (iso) {
        const p = parseISODate(iso);
        if (p) {
          const cur = startOfDay(p);
          // Never wipe a live cart date during ATC/merge refresh — that caused pick-again + blink.
          if (
            isBeforeMinSelectable(cur, region, 'cart') &&
            !isBellaDateSaveInFlight(this.form) &&
            !isBellaCartMergeLocked()
          ) {
            this.form.dataset.deliveryDate = '';
            this.form.dataset.bellaDateSource = '';
            this.calSelected = null;
            this.calView = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          } else {
            this.calSelected = p;
            this.calView = new Date(p.getFullYear(), p.getMonth(), 1);
          }
        }
      } else {
        this.calSelected = null;
        this.calView = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      }
    }

    renderCalendar() {
      const grid = this.form.querySelector('[data-bella-cal-grid]');
      const monthEl = this.form.querySelector('[data-bella-cal-month]');
      if (!grid || !monthEl) return;

      const view = this.calView;
      monthEl.textContent = monthLabel(view, document.documentElement.lang);

      const y = view.getFullYear();
      const m = view.getMonth();
      const firstDow = new Date(y, m, 1).getDay();
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      const today = startOfDay(new Date());
      const isPreorderEdit = !!this._editLinePreorder;
      const region =
        isPreorderEdit && this._editLineRegion
          ? this._editLineRegion
          : this.form.dataset.deliveryRegion || 'dubai';
      const dateContext = isPreorderEdit ? 'pdp' : 'cart';
      const minSelectable = getMinSelectableDate(region, dateContext);

      let html = '';
      for (let i = 0; i < firstDow; i++) {
        html += '<span class="bella-cal__pad"></span>';
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = new Date(y, m, d);
        const cur = startOfDay(cell);
        const isPast = cur.getTime() < minSelectable.getTime();
        const isToday = cur.getTime() === today.getTime();
        const isSel =
          this.calSelected && startOfDay(this.calSelected).getTime() === cur.getTime();
        let cls = 'bella-cal__day';
        if (isToday) cls += ' is-today';
        if (isPast) cls += ' is-muted';
        if (isSel) cls += ' is-selected';
        const disabled = isPast ? ' disabled' : '';
        html += '<button type="button" class="' + cls + '" data-cal-day="' + d + '"' + disabled + '>' + d + '</button>';
      }
      grid.innerHTML = html;

      const self = this;
      grid.querySelectorAll('[data-cal-day]').forEach((btn) => {
        btn.addEventListener(
          'click',
          (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (btn.disabled) return;
            const d = parseInt(btn.getAttribute('data-cal-day'), 10);
            self.calSelected = new Date(y, m, d);
            self.renderCalendar();
          },
          true
        );
      });

      const saveBtn = this.form.querySelector('[data-bella-save-date]');
      if (saveBtn) saveBtn.disabled = !this.calSelected;
    }

    calStepMonths(delta) {
      this.calView.setMonth(this.calView.getMonth() + delta);
      this.renderCalendar();
    }

    cartDeliveryReady() {
      const mode = this.form.dataset.bellaCartMode || 'standard';
      if (mode === 'preorder-only') return true;
      // Pre-order + product-addons only (no normal products) — no cart date required.
      if (!hasStandardLineEl(this.form)) return true;
      if (this.form.dataset.bellaDateSource !== 'cart') return false;
      if (!(this.form.dataset.deliveryDate && this.form.dataset.deliveryDate.length)) return false;
      // Stale cart attributes can survive an empty basket — require dates on all standard lines.
      return allStandardLinesHaveDates(this.form);
    }

    updateStepper() {
      const hasDate = this.cartDeliveryReady();
      const hasGift = cartHasAnyGiftNote(this.form);
      const preorderOnly =
        (this.form.dataset.bellaCartMode || 'standard') === 'preorder-only' || !hasStandardLineEl(this.form);
      const stepper = this.form.querySelector('[data-bella-stepper]');
      if (!stepper) return;

      if (stepper.classList.contains('bella-cart__stepper--preorder-only') !== preorderOnly) {
        stepper.classList.toggle('bella-cart__stepper--preorder-only', preorderOnly);
      }
      const fill = hasDate ? '1' : '0.5';
      if (stepper.style.getPropertyValue('--bella-stepper-line-fill') !== fill) {
        stepper.style.setProperty('--bella-stepper-line-fill', fill);
      }

      const s1 = stepper.querySelector('[data-step="1"]');
      const s2 = stepper.querySelector('[data-step="2"]');
      const s3 = stepper.querySelector('[data-step="3"]');

      if (s1) {
        s1.classList.add('is-done');
        s1.classList.remove('is-active');
      }
      if (s2) {
        const s2Done = hasDate && !preorderOnly;
        const s2Active = !preorderOnly && !hasDate;
        if (s2.classList.contains('is-done') !== s2Done) s2.classList.toggle('is-done', s2Done);
        if (s2.classList.contains('is-active') !== s2Active) s2.classList.toggle('is-active', s2Active);
        if (!preorderOnly) {
          if (!s2.hasAttribute('data-bella-open')) {
            s2.setAttribute('data-bella-open', 'date');
            s2.setAttribute('role', 'button');
            s2.setAttribute('tabindex', '0');
          }
        } else {
          s2.removeAttribute('data-bella-open');
          s2.removeAttribute('role');
          s2.removeAttribute('tabindex');
        }
      }
      if (s3) {
        const s3Done = hasGift;
        const s3Active = hasDate && !hasGift;
        if (s3.classList.contains('is-done') !== s3Done) s3.classList.toggle('is-done', s3Done);
        if (s3.classList.contains('is-active') !== s3Active) s3.classList.toggle('is-active', s3Active);
      }

      const c2 = s2 && s2.querySelector('.bella-cart__step-circle');
      if (c2) {
        const wantCheck = hasDate && !preorderOnly;
        const hasCheck = !!c2.querySelector('.icon-checkmark-line');
        if (wantCheck && !hasCheck) c2.innerHTML = BellaStepCheckSvg;
        else if (!wantCheck && (hasCheck || c2.textContent.trim() !== '2')) c2.textContent = '2';
      }
      const c3 = s3 && s3.querySelector('.bella-cart__step-circle');
      if (c3) {
        const hasCheck = !!c3.querySelector('.icon-checkmark-line');
        if (hasGift && !hasCheck) c3.innerHTML = BellaStepCheckSvg;
        else if (!hasGift && (hasCheck || c3.textContent.trim() !== '3')) c3.textContent = '3';
      }
    }

    updateFooterTotals(opts) {
      opts = opts || {};
      const preserveDeliveryPickers = !!opts.preserveDeliveryPickers || !!this._dateSaveUiTimer;
      const subCents = parseInt(this.form.dataset.subtotalCents || '0', 10) || 0;
      const region = this.form.dataset.deliveryRegion || 'dubai';
      const minFree = parseInt(this.form.dataset.freeShipMinCents || '0', 10) || 0;
      const dubaiFee = parseInt(this.form.dataset.dubaiFeeCents || '0', 10) || 0;
      const otherFee = parseInt(this.form.dataset.otherFeeCents || '0', 10) || 0;
      const preorderOnly =
        (this.form.dataset.bellaCartMode || 'standard') === 'preorder-only' || !hasStandardLineEl(this.form);
      const hasDate = this.cartDeliveryReady();

      let deliveryCents = 0;
      if (hasDate && !preorderOnly) {
        if (region === 'other') {
          deliveryCents = otherFee;
        } else if (minFree > 0 && subCents >= minFree) {
          deliveryCents = 0;
        } else {
          deliveryCents = dubaiFee;
        }
      }

      const delEl = this.form.querySelector('[data-bella-delivery-display]');
      const valuesEl = this.form.querySelector('.bella-cart__summary-values');
      const pickDate = this.form.dataset.strPickDate || ' + Pick a date';
      const plusFree = this.form.dataset.strPlusFree || ' + Free';
      const onItems = 'On items';

      if (delEl) {
        delEl.classList.toggle('is-free', hasDate && deliveryCents === 0 && !preorderOnly);
        if (valuesEl) valuesEl.classList.toggle('bella-cart__summary-values--muted', !hasDate);
        if (preorderOnly) {
          delEl.textContent = onItems;
        } else if (!hasDate) {
          delEl.textContent = pickDate;
        } else if (deliveryCents === 0) {
          delEl.textContent = plusFree;
        } else {
          delEl.innerHTML = '+ ' + bellaMoneyMarkup(deliveryCents, this.form, 'block');
        }
      }

      const subtotalEl = this.form.querySelector('#mini-cart-subtotal');
      if (subtotalEl) {
        subtotalEl.className = 'bella-money bella-cart__summary-amount';
        subtotalEl.id = 'mini-cart-subtotal';
        subtotalEl.innerHTML = bellaMoneyMarkup(subCents, this.form, 'core');
      }

      const btn = this.form.querySelector('[data-bella-checkout]');
      if (btn && !this._dateSyncBusy) {
        const labelEl = btn.querySelector('[data-bella-checkout-label]');
        if (!hasDate) {
          btn.disabled = true;
          btn.classList.remove('is-date-syncing');
          btn.removeAttribute('aria-busy');
          if (labelEl) labelEl.textContent = this.strings.selectDateCta;
        } else {
          btn.disabled = false;
          if (labelEl) labelEl.textContent = this.strings.checkout;
        }
      }

      const rowSub = this.form.querySelector('[data-bella-date-row-sub]');
      const rowTitle = this.form.querySelector('[data-bella-date-row-title]');
      const dateRow = this.form.querySelector('[data-bella-date-row]');
      const giftRow = this.form.querySelector('[data-bella-gift-row]');
      const deliveryCard = this.form.querySelector('[data-bella-delivery-card]');
      const iso = this.form.dataset.deliveryDate;
      const isPdp = isPdpDeliveryRoot(this.form);
      const changeDateLabel = this.form.dataset.strChangeDate || 'Change delivery date';
      const deliveryDateLabel = this.form.dataset.strSelectDateLabel || 'Delivery date';
      const hasGift = cartHasAnyGiftNote(this.form);

      if (rowTitle && isPdp) {
        rowTitle.textContent = iso ? formatPdpDateTitle(iso, this.strings) : deliveryDateLabel;
      }

      if (rowSub) {
        if (isPdp) {
          rowSub.textContent = iso ? changeDateLabel : this.strings.selectDate;
        } else {
          rowSub.textContent = iso ? formatDisplayDate(iso, this.strings) : this.strings.selectDate;
        }
        rowSub.classList.toggle('is-accent', !!iso);
      }

      // Hide pickers once completed. Preserve while date sheet is closing to avoid drawer blink.
      const extras = this.form.querySelector('.bella-cart__extras');
      if (extras) {
        extras.classList.toggle('bella-cart__extras--preorder-only', preorderOnly);
      }
      if (dateRow && !isPdp && !preserveDeliveryPickers) {
        const hideDate = preorderOnly || this.cartDeliveryReady();
        if (dateRow.hidden !== hideDate) {
          dateRow.hidden = hideDate;
          dateRow.toggleAttribute('hidden', hideDate);
        }
      }
      if (deliveryCard && !isPdp && !preserveDeliveryPickers) {
        const hideRegion = preorderOnly || this.cartDeliveryReady();
        if (deliveryCard.hidden !== hideRegion) {
          deliveryCard.hidden = hideRegion;
          deliveryCard.toggleAttribute('hidden', hideRegion);
        }
      }

      const giftSub = this.form.querySelector('[data-bella-gift-row-sub]');
      if (giftSub) {
        const g = this.form.dataset.bellaGiftNote || '';
        const nextGift = g || this.strings.giftPlaceholder;
        if (giftSub.textContent !== nextGift) giftSub.textContent = nextGift;
        giftSub.classList.toggle('is-accent', hasGift || !!g);
      }
      if (giftRow && !isPdp && !preserveDeliveryPickers) {
        if (giftRow.hidden !== hasGift) {
          giftRow.hidden = hasGift;
          giftRow.toggleAttribute('hidden', hasGift);
        }
      }

      const shipRoot = this.form.querySelector('.free-shipping');
      if (shipRoot && minFree > 0) {
        const pct = Math.min(100, Math.round((subCents / minFree) * 100));
        const bar = shipRoot.querySelector('.free-shipping__progress');
        if (bar) {
          bar.style.setProperty('--progress', pct + '%');
          if (pct >= 100) {
            bar.classList.add('free-shipping__progress--success');
          } else {
            bar.classList.remove('free-shipping__progress--success');
          }
        }
      }

      this.updateStepper();
    }
  }

  function isNavigationReload() {
    try {
      if (typeof performance !== 'undefined' && performance.getEntriesByType) {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'reload') return true;
      }
      if (
        typeof performance !== 'undefined' &&
        performance.navigation &&
        performance.navigation.type === 1
      ) {
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function clearBellaCartAttributesOnReload() {
    if (!isNavigationReload()) return Promise.resolve();
    return clearBellaCartDeliveryState().then(function (parsed) {
      const form = getForm();
      if (form) {
        return syncCheckoutLineProperties(form, { date: '', giftYes: false }).then(function () {
          return parsed;
        });
      }
      return parsed;
    });
  }

  function getCtrl(form, forceSync) {
    let c = formControllers.get(form);
    if (!c) {
      c = new BellaCartController(form);
      formControllers.set(form, c);
      c.syncFromDom();
    } else if (forceSync) {
      c.syncFromDom();
    }
    return c;
  }

  function onBellaDeliveryClick(e, root) {
    if (!root || !root.contains(e.target)) return;

    const openBtn = e.target.closest('[data-bella-open]');
    if (openBtn) {
      e.preventDefault();
      e.stopPropagation();
      const which = openBtn.getAttribute('data-bella-open');
      const lineKey = openBtn.getAttribute('data-line-key') || '';
      const lineIndex = getCartLineIndex(openBtn);
      let lineDateDisplay = '';
      let linePreorder = openBtn.getAttribute('data-line-preorder') === 'true';
      let lineRegion = openBtn.getAttribute('data-line-region') || '';
      if (which === 'date' && (lineKey || lineIndex)) {
        const textEl = openBtn.querySelector('.bella-cart__line-date-text');
        lineDateDisplay = textEl ? String(textEl.textContent || '').trim() : '';
        const li = openBtn.closest('li[data-line], li[id^="CartItem-"]');
        if (!linePreorder && li) linePreorder = isPreorderLineEl(li, root);
        if (!lineRegion && linePreorder && li) {
          lineRegion = regionFromDeliveryTo(readLineDeliveryTo(li, root));
        }
      }
      getCtrl(root, false).openSheet(which, {
        lineKey: lineKey,
        lineIndex: lineIndex,
        lineDateDisplay: lineDateDisplay,
        linePreorder: linePreorder,
        lineRegion: lineRegion,
      });
      return;
    }

    const regionBtn = e.target.closest('[data-bella-region]');
    if (regionBtn && regionBtn.matches('button')) {
      e.preventDefault();
      e.stopPropagation();
      const region = regionBtn.getAttribute('data-bella-region');
      const current = root.dataset.deliveryRegion || 'dubai';
      if (region === current) return;

      const ctrl = getCtrl(root, false);
      ctrl.setRegionUI(region);
      if (root.dataset.deliveryDate) {
        const p = parseISODate(root.dataset.deliveryDate);
        if (p && isBeforeMinSelectable(p, region)) {
          root.dataset.deliveryDate = '';
          root.dataset.bellaDateSource = '';
          ctrl.calSelected = null;
          ctrl.initCalendar();
          ctrl.updateFooterTotals();
        }
      }
      const dateSheet = findBellaSheet(root, 'date');
      if (dateSheet && dateSheet.classList.contains('is-open')) {
        ctrl.renderCalendar();
      }
      ctrl.persistAttributes({});
      return;
    }

    const closeEl = e.target.closest('[data-bella-close]');
    if (closeEl) {
      const sheet = closeEl.closest('.bella-sheet');
      if (sheet && root.contains(sheet)) {
        e.preventDefault();
        e.stopPropagation();
        const which = sheet.getAttribute('data-bella-sheet');
        getCtrl(root, false).closeSheet(which || sheet.id);
      }
      return;
    }

    const openSheet = root.querySelector('.bella-sheet.is-open');

    if (openSheet) {
      const panel = openSheet.querySelector('.bella-sheet__panel');
      const clickedInsidePanel = panel && panel.contains(e.target);
      const clickedOpenButton = e.target.closest('[data-bella-open]');

      if (!clickedInsidePanel && !clickedOpenButton) {
        e.preventDefault();
        e.stopPropagation();
        const which = openSheet.getAttribute('data-bella-sheet');
        getCtrl(root, false).closeSheet(which || openSheet.id);
        return;
      }
    }

    const saveDateBtn = e.target.closest('[data-bella-save-date]');
    if (saveDateBtn) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).saveDate();
      return;
    }

    const saveGiftBtn = e.target.closest('[data-bella-save-gift]');
    if (saveGiftBtn) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).saveGift();
      return;
    }

    const prev = e.target.closest('[data-bella-cal-prev]');
    if (prev) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).calStepMonths(-1);
      return;
    }

    const next = e.target.closest('[data-bella-cal-next]');
    if (next) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).calStepMonths(1);
      return;
    }
  }

  var bellaRecsState = {
    product: null,
    selected: {}
  };

  function bellaFormatMoney(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        var fmt =
          (theme && theme.shopSettings && theme.shopSettings.moneyFormat) ||
          (window.theme && theme.moneyFormat) ||
          '{{amount}}';
        return Shopify.formatMoney(cents, fmt);
      }
    } catch (err) {}
    var n = Number(cents || 0) / 100;
    return 'Dhs. ' + n.toFixed(2);
  }

  function bellaRecsOptionName(opt, idx) {
    if (opt && typeof opt === 'object') return opt.name || ('Option ' + (idx + 1));
    return String(opt || 'Option ' + (idx + 1));
  }

  function bellaRecsOptionValues(product, opt, idx) {
    if (opt && typeof opt === 'object' && Array.isArray(opt.values) && opt.values.length) {
      return opt.values.slice();
    }
    var values = [];
    var key = 'option' + (idx + 1);
    (product.variants || []).forEach(function (v) {
      var val = v[key];
      if (val && values.indexOf(val) === -1) values.push(val);
    });
    return values;
  }

  function bellaRecsFindVariant(product, selected) {
    if (!product || !product.variants) return null;
    return product.variants.find(function (v) {
      return (product.options || []).every(function (opt, idx) {
        var name = bellaRecsOptionName(opt, idx);
        var key = 'option' + (idx + 1);
        return !selected[name] || v[key] === selected[name];
      });
    });
  }

  function bellaRecsRenderOptions(product) {
    var body = document.querySelector('[data-bella-recs-body]');
    if (!body || !product) return;

    var html = '';
    (product.options || []).forEach(function (opt, idx) {
      var optName = bellaRecsOptionName(opt, idx);
      var values = bellaRecsOptionValues(product, opt, idx);
      if (!values.length) return;
      if (!bellaRecsState.selected[optName]) {
        bellaRecsState.selected[optName] = values[0];
      }
      html +=
        '<fieldset class="bella-recs-variant__option" data-option-name="' +
        optName.replace(/"/g, '&quot;') +
        '">';
      html += '<legend class="bella-recs-variant__label">' + optName + '</legend>';
      html += '<div class="bella-recs-variant__pills">';
      values.forEach(function (val) {
        var active = bellaRecsState.selected[optName] === val ? ' is-active' : '';
        html +=
          '<button type="button" class="bella-recs-variant__pill' +
          active +
          '" data-bella-recs-option data-option-name="' +
          optName.replace(/"/g, '&quot;') +
          '" data-option-value="' +
          String(val).replace(/"/g, '&quot;') +
          '">' +
          val +
          '</button>';
      });
      html += '</div></fieldset>';
    });

    var variant = bellaRecsFindVariant(product, bellaRecsState.selected);
    html +=
      '<p class="bella-recs-variant__price bella-money" data-bella-recs-price>' +
      (variant ? bellaFormatMoney(variant.price) : '') +
      '</p>';

    body.innerHTML = html;
    bellaRecsSyncAddButton();
  }

  function bellaRecsLabels() {
    var sheet = document.getElementById('bella-sheet-recs');
    return {
      title: (sheet && sheet.getAttribute('data-recs-title')) || 'Choose options',
      add: (sheet && sheet.getAttribute('data-recs-add-label')) || 'Add to cart',
      soldout: (sheet && sheet.getAttribute('data-recs-soldout-label')) || 'Sold out',
      loading: (sheet && sheet.getAttribute('data-recs-loading-label')) || 'Loading…'
    };
  }

  function bellaRecsSyncAddButton() {
    var btn = document.querySelector('[data-bella-recs-add]');
    if (!btn) return;
    var labels = bellaRecsLabels();
    var variant = bellaRecsFindVariant(bellaRecsState.product, bellaRecsState.selected);
    var priceEl = document.querySelector('[data-bella-recs-price]');
    if (priceEl && variant) priceEl.textContent = bellaFormatMoney(variant.price);

    if (!variant || !variant.available) {
      btn.disabled = true;
      btn.textContent = variant ? labels.soldout : 'Unavailable';
      return;
    }
    btn.disabled = false;
    btn.textContent = labels.add + ' · ' + bellaFormatMoney(variant.price);
    btn.dataset.variantId = String(variant.id);
  }

  function bellaRecsOpen(handle, title) {
    var form = getForm();
    if (!form || !handle) return;
    var labels = bellaRecsLabels();
    var titleEl = form.querySelector('[data-bella-recs-title]');
    var body = form.querySelector('[data-bella-recs-body]');
    if (titleEl) titleEl.textContent = title || labels.title;
    if (body) body.innerHTML = '<p class="bella-recs-variant__loading">' + labels.loading + '</p>';
    var addBtn = form.querySelector('[data-bella-recs-add]');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = labels.add;
      delete addBtn.dataset.variantId;
    }

    bellaRecsState = { product: null, selected: {} };
    getCtrl(form, false).openSheet('recs');

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Product fetch failed');
        return r.json();
      })
      .then(function (product) {
        bellaRecsState.product = product;
        bellaRecsState.selected = {};
        if (titleEl && product.title) titleEl.textContent = product.title;
        bellaRecsRenderOptions(product);
      })
      .catch(function (err) {
        console.error('[Bella cart] recs variant', err);
        if (body) body.innerHTML = '<p class="bella-recs-variant__error">Unable to load options.</p>';
      });
  }

  function bellaQuietMergeCartFromSections(parsed) {
    var host = getMiniCartHost();
    var form = getForm();
    if (!host || !form || !parsed || !parsed.sections) return false;

    var sid = getCartDrawerSectionId();
    var miniHtml = parsed.sections[sid];
    if (!miniHtml) return false;

    var doc = new DOMParser().parseFromString(miniHtml, 'text/html');
    var newForm =
      doc.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart') ||
      doc.querySelector('form#cart');
    if (!newForm) return false;

    // Preserve live cart date across ATC — Liquid often omits it when the new line
    // still has no Delivery Date property (that wipe caused date blink).
    var liveDateSource = form.dataset.bellaDateSource || '';
    var liveDeliveryDate = form.dataset.deliveryDate || '';
    var liveDeliveryDisplay = form.dataset.deliveryDateDisplay || '';
    var liveGift = form.dataset.bellaGiftNote || form.dataset.cartNote || '';
    var liveRegion = form.dataset.deliveryRegion || '';
    var keepLiveDateUi = liveDateSource === 'cart' && !!liveDeliveryDate;

    // Snapshot preorder before DOM replace (PDP/upsell ATC into a preorder cart).
    if (
      !bellaPreorderSnapshot &&
      (hasPreorderLineEl(form) || (form.dataset.bellaCartMode || '') === 'preorder-only')
    ) {
      armPreorderPreserveForAtc(form);
    }

    // Block refresh/reconcile loops while we merge + stamp.
    armBellaCartMergeLock(1200);

    // During upsell ATC, skip merge until finalize allows it.
    // Return true (not false) so cart-drawer.js does not fall through and paint wiped HTML.
    if (bellaUpsellAtcInFlight && !bellaUpsellAllowSectionMerge) return true;

    // Never paint empty / wiped section HTML while we still intend to restore.
    var snapLen = (bellaUpsellCartSnapshot && bellaUpsellCartSnapshot.length) || 0;
    var expectMin = Math.max(snapLen, bellaUpsellExpectedMinLines || 0);
    var newLineCountPreview = newForm.querySelectorAll(
      'cart-items li[data-line], cart-items li[id^="CartItem-"]'
    ).length;
    var newIsEmpty = newForm.classList.contains('is-empty') || newLineCountPreview === 0;
    if (expectMin > 0 && (newIsEmpty || newLineCountPreview < expectMin) && !bellaIsUserCartEdit()) {
      // Keep live DOM; kick a restore if server cart is short.
      bellaUpsellGuardRestore({ paintIfRestored: false }).catch(function () {});
      return true;
    }

    return withBellaQuietDom(function () {
    var countSel = 'li[data-line], li[id^="CartItem-"]';
    var newItems = newForm.querySelector('cart-items');
    var oldItems = form.querySelector('cart-items');
    var skipItemsReplace = false;
    if (newItems && oldItems && oldItems.parentNode) {
      var oldLineCount = oldItems.querySelectorAll(countSel).length;
      var newLineCount = newItems.querySelectorAll(countSel).length;
      // Refuse shorter HTML even on "force" paint unless it at least matches expected min.
      var minLines = Math.max(snapLen, bellaUpsellExpectedMinLines || 0);
      var tooShortVsSnap = minLines > 0 && newLineCount < minLines && !bellaIsUserCartEdit();
      var protectShorter =
        tooShortVsSnap ||
        (!bellaUpsellForceItemsReplace &&
          bellaUpsellAtcInFlight &&
          !bellaIsUserCartEdit() &&
          !!(bellaUpsellCartSnapshot || bellaPreorderSnapshot) &&
          newLineCount < oldLineCount);
      if (protectShorter && newLineCount < Math.max(oldLineCount, minLines || 0)) {
        skipItemsReplace = true;
        setTimeout(function () {
          if (bellaIsUserCartEdit()) return;
          var snap = bellaUpsellCartSnapshot || bellaPreorderSnapshot;
          bellaRestoreMissingSnapshotLines(snap, { blockMs: 4000 }).then(function (restored) {
            function paintAfterRestore() {
              bellaUpsellAllowSectionMerge = true;
              bellaUpsellForceItemsReplace = true;
              return bellaRefreshCartDrawerSections().finally(function () {
                bellaUpsellAllowSectionMerge = false;
                bellaUpsellForceItemsReplace = false;
              });
            }
            if (restored) return paintAfterRestore();
            return bellaRestoreWipedPreorderLines().then(function () {
              return paintAfterRestore();
            });
          });
        }, 400);
      } else {
        var importedItems = document.importNode(newItems, true);
        var drawerAlreadyOpen = !!document.querySelector('cart-drawer details[open]');
        if (drawerAlreadyOpen) {
          importedItems.querySelectorAll('[data-animate]').forEach(function (el) {
            el.removeAttribute('data-animate');
            Array.from(el.attributes).forEach(function (attr) {
              if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
            });
          });
        }
        oldItems.replaceWith(importedItems);
      }
    }

    // Keep live recommendations + open sheets; only patch cart chrome.
    // When protecting shorter line lists, do NOT update count/footer (avoids 3 ITEM / 2 cards).
    if (!skipItemsReplace) {
      var newCount = newForm.querySelector('.bella-cart__item-count');
      var oldCount = form.querySelector('.bella-cart__item-count');
      if (newCount && oldCount) oldCount.textContent = newCount.textContent;
    }

    var newFooter = newForm.querySelector('.mini-cart__footer.bella-cart__footer, .bella-cart__footer');
    var oldFooter = form.querySelector('.mini-cart__footer.bella-cart__footer, .bella-cart__footer');
    if (!skipItemsReplace && newFooter && oldFooter) {
      oldFooter.querySelectorAll('[data-animate]').forEach(function () {});
      var tmp = document.createElement('div');
      tmp.innerHTML = newFooter.innerHTML;
      if (document.querySelector('cart-drawer details[open]')) {
        tmp.querySelectorAll('[data-animate]').forEach(function (el) {
          el.removeAttribute('data-animate');
          Array.from(el.attributes).forEach(function (attr) {
            if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
          });
        });
      }
      oldFooter.innerHTML = tmp.innerHTML;
    }

    // Stepper + delivery extras must refresh when mode changes (e.g. preorder-only → mixed).
    // When a cart date is already chosen, keep live stepper/extras — Liquid markup shows
    // "Tap to choose a date" and caused a continuous blink with stamp/hide.
    var newStepper = newForm.querySelector('[data-bella-stepper]');
    var oldStepper = form.querySelector('[data-bella-stepper]');
    if (!keepLiveDateUi) {
      if (newStepper && oldStepper && oldStepper.parentNode) {
        oldStepper.replaceWith(document.importNode(newStepper, true));
      } else if (newStepper && !oldStepper) {
        var header = form.querySelector('.mini-cart__header');
        if (header && header.parentNode) {
          header.parentNode.insertBefore(document.importNode(newStepper, true), header.nextSibling);
        }
      }
    }

    var newExtras = newForm.querySelector('.bella-cart__extras');
    var oldExtras = form.querySelector('.bella-cart__extras');
    if (!keepLiveDateUi) {
      if (newExtras && oldExtras && oldExtras.parentNode) {
        var importedExtras = document.importNode(newExtras, true);
        if (document.querySelector('cart-drawer details[open]')) {
          importedExtras.querySelectorAll('[data-animate]').forEach(function (el) {
            el.removeAttribute('data-animate');
            Array.from(el.attributes).forEach(function (attr) {
              if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
            });
          });
        }
        oldExtras.replaceWith(importedExtras);
      } else if (newExtras && !oldExtras) {
        var mainForExtras = form.querySelector('.mini-cart__main');
        if (mainForExtras) {
          var recsEl = mainForExtras.querySelector('cart-recommendations');
          if (recsEl) mainForExtras.insertBefore(document.importNode(newExtras, true), recsEl);
          else mainForExtras.appendChild(document.importNode(newExtras, true));
        }
      } else if (!newExtras && oldExtras) {
        oldExtras.remove();
      }
    }

    // Free-shipping / app bars that sit above footer inside main (if present in new markup)
    var newMain = newForm.querySelector('.mini-cart__main');
    var oldMain = form.querySelector('.mini-cart__main');
    if (newMain && oldMain) {
      var shippingSelectors = [
        '.decoded-llp-free-shipping',
        '[class*="free-shipping"]',
        '.bella-cart__shipping-bar'
      ];
      shippingSelectors.forEach(function (sel) {
        var neu = newMain.querySelector(sel);
        var cur = oldMain.querySelector(sel);
        if (neu && cur && cur.parentNode) {
          cur.replaceWith(document.importNode(neu, true));
        } else if (neu && !cur) {
          var recs = oldMain.querySelector('cart-recommendations');
          if (recs) oldMain.insertBefore(document.importNode(neu, true), recs);
          else oldMain.appendChild(document.importNode(neu, true));
        }
      });
    }

    [
      'data-subtotal-cents',
      'data-free-ship-min-cents',
      'data-dubai-fee-cents',
      'data-other-fee-cents',
      'data-bella-cart-mode',
      'data-delivery-to-prop-label',
      'data-line-edit-delivery-date',
      'data-bella-normal-line-date-mode',
      'data-str-change-date'
    ].forEach(function (attr) {
      if (newForm.hasAttribute(attr)) form.setAttribute(attr, newForm.getAttribute(attr));
      else form.removeAttribute(attr);
    });

    // Never copy empty date attrs from Liquid over a live cart date (ATC missing line props).
    if (!(liveDateSource === 'cart' && liveDeliveryDate)) {
      ['data-bella-date-source', 'data-delivery-date', 'data-delivery-date-display'].forEach(function (attr) {
        if (newForm.hasAttribute(attr)) form.setAttribute(attr, newForm.getAttribute(attr));
        else form.removeAttribute(attr);
      });
    }
    ['data-cart-note', 'data-bella-gift-note'].forEach(function (attr) {
      if (newForm.hasAttribute(attr)) form.setAttribute(attr, newForm.getAttribute(attr));
    });

    // Re-apply live cart date after attr copy (section HTML often drops it after ATC).
    if (liveDateSource === 'cart' && liveDeliveryDate) {
      form.dataset.bellaDateSource = 'cart';
      form.dataset.deliveryDate = liveDeliveryDate;
      form.dataset.deliveryDateDisplay =
        liveDeliveryDisplay || form.dataset.deliveryDateDisplay || '';
    } else {
      const mode = form.dataset.bellaCartMode || 'standard';
      if (mode !== 'preorder-only' && form.dataset.bellaDateSource !== 'cart') {
        form.dataset.deliveryDate = '';
        form.dataset.deliveryDateDisplay = '';
        form.dataset.bellaDateSource = '';
      } else if (mode !== 'preorder-only' && newForm.dataset.deliveryDate) {
        form.dataset.deliveryDate = newForm.dataset.deliveryDate;
        if (newForm.dataset.deliveryDateDisplay) {
          form.dataset.deliveryDateDisplay = newForm.dataset.deliveryDateDisplay;
        }
      }
    }
    if (liveGift) {
      form.dataset.bellaGiftNote = liveGift;
      form.dataset.cartNote = liveGift;
    }
    if (liveRegion) form.dataset.deliveryRegion = liveRegion;

    form.classList.toggle('is-empty', newForm.classList.contains('is-empty'));

    // Paint cart date onto new undated lines immediately (before unlock / refresh).
    if (form.dataset.bellaDateSource === 'cart' && form.dataset.deliveryDateDisplay) {
      paintCartLineDeliveryDates(form, form.dataset.deliveryDateDisplay, { replaceExisting: true });
      syncAddonDatePaintFromParents(form, { dateDisplay: form.dataset.deliveryDateDisplay });
    } else if (form.dataset.bellaDateSource === 'cart' && form.dataset.deliveryDate) {
      var paintParts = formatAdminOrderDateParts(form.dataset.deliveryDate);
      if (paintParts.date) {
        form.dataset.deliveryDateDisplay = paintParts.date;
        paintCartLineDeliveryDates(form, paintParts.date, { replaceExisting: true });
        syncAddonDatePaintFromParents(form, { dateDisplay: paintParts.date });
      }
    }
    paintCartLineGiftNotes(form);

    updateCartIconSections(parsed);
    pruneUpsellProductsInCart(host);

    var mini = document.querySelector('mini-cart');
    if (mini) mini.dataset.sectionLoaded = 'true';

    var ctrl = getCtrl(form, false);
    if (ctrl) {
      ctrl.readConfig();
      ctrl.updateFooterTotals({ preserveDeliveryPickers: false });
    }

    if (form.classList.contains('is-empty')) {
      var stillHasLines = !!form.querySelector(
        'cart-items li[data-line], cart-items li[id^="CartItem-"], .bella-cart__items li'
      );
      if (!stillHasLines) clearBellaCartDeliveryState().catch(function () {});
      return true;
    }

    // Persist blank-line dates to cart once (paint already done). Skip if merge lock refresh.
    // IMPORTANT: paintOnly after ATC — cart/change here was wiping other line items.
    if (form.dataset.bellaDateSource === 'cart' && form.dataset.deliveryDate) {
      applySelectedDateToBlankLines(form, { paintOnly: true, forceReplace: true })
        .then(function () {
          armBellaCartMergeLock(400);
          var c = formControllers.get(getForm());
          if (c) c.updateFooterTotals();
        })
        .catch(function () {});
    }

    return true;
    });
  }

  /**
   * Quiet-appended upsells can end up with Delivery Date text and no pencil
   * (date-loader HTML). Upgrade any standard line that is missing the edit control.
   */
  function bellaEnsureStandardLineDatePencils(form) {
    if (!form || !lineDateEditEnabled(form)) return;
    const propLabel = getDeliveryPropLabel(form);
    form.querySelectorAll('cart-items li[data-line], cart-items li[id^="CartItem-"]').forEach(function (li) {
      if (isAddonLineEl(li) || li.getAttribute('data-pa-addon') === 'true') return;
      if (isPreorderLineEl(li, form)) return;
      const dateText = readLineDateDisplay(li);
      if (!dateText) return;
      if (li.querySelector('[data-bella-line-date-edit]')) {
        const btn = li.querySelector('[data-bella-line-date-edit]');
        const lineKey = getCartLineKey(li);
        const lineIndex = getCartLineIndex(li);
        if (btn && lineKey) btn.setAttribute('data-line-key', lineKey);
        if (btn && lineIndex) btn.setAttribute('data-line-index', String(lineIndex));
        return;
      }
      const content = li.querySelector('.product-content');
      if (!content) return;
      let propsEl = content.querySelector('.bella-cart__line-props');
      if (!propsEl) {
        propsEl = document.createElement('div');
        propsEl.className = 'bella-cart__line-props';
        content.appendChild(propsEl);
      }
      let dateEl = findLineDatePropEl(propsEl, propLabel);
      if (!dateEl) {
        dateEl = document.createElement('p');
        dateEl.className = 'bella-cart__line-prop bella-cart__line-prop--date';
        propsEl.appendChild(dateEl);
      } else {
        dateEl.classList.add('bella-cart__line-prop--date');
      }
      dateEl.innerHTML = buildLineDatePropInnerHtml(
        form,
        propLabel,
        dateText,
        false,
        getCartLineKey(li),
        getCartLineIndex(li),
        ''
      );
    });
  }

  /**
   * Quiet upsell can turn a preorder-only basket into mixed without a section refresh.
   * Liquid omits the Dubai/date rows in preorder-only — inject them so the shopper
   * gets the same full delivery widget as a normal cart (not an auto-opened modal).
   */
  function bellaResolveCartModeFromDom(form) {
    if (!form) return 'standard';
    var hasPre = hasPreorderLineEl(form);
    var hasStd = hasStandardLineEl(form);
    if (hasPre && hasStd) return 'mixed';
    if (hasPre && !hasStd) return 'preorder-only';
    return 'standard';
  }

  function bellaBuildDeliveryWidgetsHtml(form) {
    var region = (form && form.dataset.deliveryRegion) || 'dubai';
    var isOther = region === 'other';
    var tapChoose =
      (form && form.dataset.strSelectDate) || 'Tap to choose a date';
    var dateTitle =
      (form && form.dataset.strSelectDateLabel) || 'Delivery date';
    var calSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-calendar" fill="none" viewBox="0 0 18 19"><path fill-rule="evenodd" clip-rule="evenodd" d="M4 .75a.75.75 0 01.75.75v.75h7.5V1.5a.75.75 0 011.5 0v.75h.75A2.25 2.25 0 0116.5 4.5v11.25a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 15.75V4.5A2.25 2.25 0 013.75 2.25h.75V1.5A.75.75 0 014 .75zM3 6v9.75c0 .414.336.75.75.75h10.5a.75.75 0 00.75-.75V6H3z" fill="currentColor"/></svg>';
    var chevronSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-caret icon-chevron" fill="none" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 010 .708l-4 4a.5.5 0 01-.708 0l-4-4a.5.5 0 11.708-.708L5 4.293 8.646.646a.5.5 0 01.708 0z" fill="currentColor"/></svg>';
    var truckSvg =
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h11v9H3zM14 9h4l3 3v3h-7zM7 18a2 2 0 100-4 2 2 0 000 4zM17 18a2 2 0 100-4 2 2 0 000 4z"></path></svg>';

    return (
      '<div class="bella-cart__card bella-cart__delivery-card" data-bella-delivery-card>' +
      '<div class="bella-cart__card-head">' +
      truckSvg +
      '<span>Delivery To</span></div>' +
      '<div class="bella-cart__segment' +
      (isOther ? ' is-region-other' : '') +
      '" role="group" data-bella-segment>' +
      '<span class="bella-cart__segment-slider" aria-hidden="true"></span>' +
      '<button type="button" class="bella-cart__segment-btn' +
      (!isOther ? ' is-selected' : '') +
      '" data-bella-region="dubai">Dubai</button>' +
      '<button type="button" class="bella-cart__segment-btn' +
      (isOther ? ' is-selected' : '') +
      '" data-bella-region="other">Other Emirates</button>' +
      '</div>' +
      '<div class="bella-cart__info-box" data-bella-info="dubai"' +
      (isOther ? ' hidden' : '') +
      '><span class="bella-cart__info-dot" aria-hidden="true"></span><div>Same-day &amp; next-day delivery available in Dubai.</div></div>' +
      '<div class="bella-cart__info-box" data-bella-info="other"' +
      (isOther ? '' : ' hidden') +
      '><span class="bella-cart__info-dot" aria-hidden="true"></span><div>Delivery to other emirates available on selected dates.</div></div>' +
      '</div>' +
      '<button type="button" class="bella-cart__row" data-bella-open="date" data-bella-date-row aria-haspopup="dialog">' +
      '<span class="bella-cart__row-icon">' +
      calSvg +
      '</span>' +
      '<span class="bella-cart__row-body">' +
      '<span class="bella-cart__row-title">' +
      escapeHtmlText(dateTitle) +
      '</span>' +
      '<span class="bella-cart__row-sub" data-bella-date-row-sub>' +
      escapeHtmlText(tapChoose) +
      '</span>' +
      '</span>' +
      chevronSvg +
      '</button>'
    );
  }

  function bellaEnsureMixedCartDeliveryUi(form) {
    if (!form) return;
    var mode = bellaResolveCartModeFromDom(form);
    form.dataset.bellaCartMode = mode;

    var extras = form.querySelector('.bella-cart__extras');
    if (extras) {
      extras.classList.toggle('bella-cart__extras--preorder-only', mode === 'preorder-only');
    }

    if (mode === 'preorder-only') return;

    // Delivery widgets are omitted from Liquid when the basket started preorder-only.
    if (!form.querySelector('[data-bella-date-row]') && extras) {
      var giftRow = extras.querySelector('[data-bella-gift-row]');
      var holder = document.createElement('div');
      holder.innerHTML = bellaBuildDeliveryWidgetsHtml(form);
      var node;
      while ((node = holder.firstChild)) {
        if (giftRow) extras.insertBefore(node, giftRow);
        else extras.appendChild(node);
      }
    } else {
      var dateRow = form.querySelector('[data-bella-date-row]');
      var deliveryCard = form.querySelector('[data-bella-delivery-card]');
      if (dateRow) {
        dateRow.hidden = false;
        dateRow.removeAttribute('hidden');
      }
      if (deliveryCard) {
        deliveryCard.hidden = false;
        deliveryCard.removeAttribute('hidden');
      }
    }

    var stepper = form.querySelector('[data-bella-stepper]');
    if (stepper) {
      stepper.classList.remove('bella-cart__stepper--preorder-only');
      var s2 = stepper.querySelector('[data-step="2"]');
      if (s2) {
        s2.setAttribute('data-bella-open', 'date');
        s2.setAttribute('role', 'button');
        s2.setAttribute('tabindex', '0');
      }
    }
  }

  /**
   * After quiet upsell add: inherit date from normal products only.
   * If none, show the full cart delivery widget (region + date row) — never auto-open the modal.
   */
  function bellaPromptUpsellDateIfNeeded(form, inheritedDate) {
    if (!form) return;
    var ctrl = getCtrl(form, false);
    if (!ctrl) return;

    withBellaQuietDom(function () {
      bellaEnsureMixedCartDeliveryUi(form);

      if (inheritedDate) {
        var iso = parseAdminDeliveryDate(inheritedDate) || '';
        if (iso) {
          form.dataset.deliveryDate = iso;
          form.dataset.deliveryDateDisplay = inheritedDate;
          form.dataset.bellaDateSource = 'cart';
        }
        paintCartLineDeliveryDates(form, inheritedDate, { replaceExisting: false });
      }

      bellaEnsureStandardLineDatePencils(form);
      ctrl.updateStepper();
      ctrl.updateFooterTotals();
    });
  }

  /**
   * Upsell +: add only + append one DOM card. No section refresh.
   * Inherit Delivery Date from normal products only — never from preorder.
   * If no normal date exists, show the cart date picker after add.
   */
  function bellaRecsAddToCart(variantId, triggerBtn) {
    if (variantId == null || variantId === '') return;
    var vid = Number(variantId);
    if (!vid || !isFinite(vid)) vid = variantId;
    if (!vid || vid === '0') return;

    if (document.body.classList.contains('template-cart') || !(theme && theme.shopSettings && theme.shopSettings.cartDrawer)) {
      Shopify.postLink(theme.routes.cart_add_url, {
        parameters: { id: vid, quantity: 1 },
      });
      return;
    }

    if (triggerBtn) {
      if (triggerBtn.getAttribute('aria-busy') === 'true') return;
      triggerBtn.setAttribute('disabled', true);
      triggerBtn.setAttribute('aria-busy', 'true');
      triggerBtn.classList.add('loading');
    }

    var cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    var liveForm = getForm();
    var dateLabel = getDeliveryPropLabel(liveForm) || 'Delivery Date';
    var toLabel = getDeliveryToPropLabel(liveForm) || 'Delivery To';

    function readDefaultDateFromDom() {
      if (!liveForm) return '';
      // Cart-level date only when sourced from normal products — never preorder.
      if (liveForm.dataset.bellaDateSource === 'cart') {
        var cartDisplay = String(liveForm.dataset.deliveryDateDisplay || '').trim();
        if (cartDisplay) return cartDisplay;
        if (liveForm.dataset.deliveryDate) {
          var parts = formatAdminOrderDateParts(liveForm.dataset.deliveryDate);
          if (parts && parts.date) return parts.date;
        }
      }
      return resolveSelectedDeliveryDateDisplay(liveForm) || '';
    }

    function readDefaultDateFromCart(cart) {
      if (liveForm && liveForm.dataset.bellaDateSource === 'cart' && cart && cart.attributes) {
        var attrDisplay = String(cart.attributes['Delivery Date'] || '').trim();
        if (attrDisplay) return attrDisplay;
      }
      if (!cart || !Array.isArray(cart.items)) return '';
      var normalDate = '';
      cart.items.forEach(function (item) {
        if (normalDate || !item || !item.properties) return;
        if (item.properties._pa_parent_variant || item.properties._pa_sticky_addon === 'true') return;
        var isPre = !!(item.properties[toLabel] || item.properties['Delivery To']);
        if (isPre) return;
        var d = item.properties[dateLabel] || item.properties['Delivery Date'] || '';
        if (d) normalDate = String(d);
      });
      return normalDate;
    }

    function doneBtn() {
      if (!triggerBtn) return;
      triggerBtn.classList.remove('loading');
      triggerBtn.removeAttribute('disabled');
      triggerBtn.removeAttribute('aria-busy');
      if (triggerBtn.hasAttribute('data-bella-recs-add')) bellaRecsSyncAddButton();
    }

    function finishUpsellUi() {
      var form = getForm();
      if (form) {
        var sheetCtrl = getCtrl(form, false);
        if (sheetCtrl && typeof sheetCtrl.closeSheet === 'function') {
          sheetCtrl.closeSheet('recs', { skipScrollRestore: true });
        }
      }
      var recCard = triggerBtn && triggerBtn.closest('.bella-cart__recs-item');
      if (recCard) recCard.remove();
      if (window.BellaCartRecs && typeof window.BellaCartRecs.clearCache === 'function') {
        window.BellaCartRecs.clearCache();
      } else if (window.BellaCartRecs && window.BellaCartRecs.cache) {
        try {
          window.BellaCartRecs.cache.clear();
        } catch (err) {
          /* ignore */
        }
      }
    }

    // Block Addons cart mutations + section refresh while we append.
    bellaSetUpsellAtcActive(true);
    bellaBlockForeignCartWrites(15000);
    armBellaCartMergeLock(8000);
    blockBellaCartWrites(8000);

    var dateDisplay = readDefaultDateFromDom();
    var freeze = bellaFreezeCartItemsDom();
    var addedItemRef = null;
    var stopFreezeGuard = bellaArmCartItemsFreezeGuard(
      freeze,
      function () {
        return addedItemRef;
      },
      function () {
        return dateDisplay;
      }
    );

    fetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (beforeCart) {
        if (!dateDisplay) dateDisplay = readDefaultDateFromCart(beforeCart);
        bellaUpsellCartSnapshot = bellaSnapshotCartItems(beforeCart);
        bellaUpsellVariantId = vid;
        bellaUpsellExpectedMinLines =
          (bellaUpsellCartSnapshot || []).filter(function (s) {
            return !(s.properties && s.properties._pa_hide_in_cart === 'true');
          }).length + 1;
        if (bellaUpsellCartSnapshot.length) {
          bellaPreorderSnapshot = bellaUpsellCartSnapshot.filter(function (s) {
            return s.isPreorder || s.isAddon;
          });
          if (liveForm) liveForm.dataset.bellaPreservePreorder = '1';
        }

        var addPayload = { id: vid, quantity: 1 };
        var giftMsg = readCartGiftMessage(liveForm, beforeCart);
        if (giftMsg && liveForm) {
          liveForm.dataset.bellaGiftNote = giftMsg;
          liveForm.dataset.cartNote = giftMsg;
        }
        if (dateDisplay || (giftMsg && getGiftNoteMode(liveForm) !== 'per_product')) {
          addPayload.properties = {};
          if (dateDisplay) addPayload.properties[dateLabel] = dateDisplay;
          if (giftMsg && getGiftNoteMode(liveForm) !== 'per_product') {
            addPayload.properties[getGiftPropLabel(liveForm)] = getGiftPropYes(liveForm);
          }
        }

        return bellaOwnCartFetch(
          theme.routes.cart_add_url,
          Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(addPayload) })
        )
          .then(function (r) {
            return r.json();
          })
          .then(function (parsedState) {
            if (parsedState && parsedState.status === 422) {
              var retry = { id: vid, quantity: 1 };
              if (dateDisplay || (giftMsg && getGiftNoteMode(liveForm) !== 'per_product')) {
                retry.properties = {};
                if (dateDisplay) retry.properties[dateLabel] = dateDisplay;
                if (giftMsg && getGiftNoteMode(liveForm) !== 'per_product') {
                  retry.properties[getGiftPropLabel(liveForm)] = getGiftPropYes(liveForm);
                }
              }
              return bellaOwnCartFetch(
                theme.routes.cart_add_url,
                Object.assign({}, fetchConfig('javascript'), { body: JSON.stringify(retry) })
              ).then(function (r2) {
                return r2.json();
              });
            }
            return parsedState;
          })
          .then(function (parsedState) {
            if (parsedState && parsedState.status === 422) {
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:error', {
                  detail: { errorMessage: parsedState.description },
                })
              );
              return null;
            }

            // Keep blocking Addons briefly after our add succeeds (late wipe).
            bellaBlockForeignCartWrites(4000);

            finishUpsellUi();
            addedItemRef = parsedState;

            // Keep exact existing cards, then append upsell only.
            bellaReinstateFrozenCartItems(freeze, parsedState, dateDisplay);
            // Fallback if freeze was empty (first item): just append.
            if (!freeze || !freeze.count) {
              bellaAppendUpsellLineToDrawer(parsedState, { dateDisplay: dateDisplay });
            }

            return fetch(cartUrl, { credentials: 'same-origin' })
              .then(function (r) {
                return r.json();
              })
              .then(function (afterCart) {
                bellaUpdateDrawerCountAndMoney(afterCart);
                // Server-only restore if Addons wiped lines — never rewrite existing DOM cards.
                if (
                  bellaUpsellCartSnapshot &&
                  bellaUpsellCartSnapshot.length &&
                  !bellaUpsellCartHasAllLines(afterCart, bellaUpsellCartSnapshot, vid)
                ) {
                  return bellaRestoreMissingSnapshotLines(bellaUpsellCartSnapshot, {
                    blockMs: 10000,
                    cart: afterCart,
                  }).then(function () {
                    return fetch(cartUrl, { credentials: 'same-origin' })
                      .then(function (r2) {
                        return r2.json();
                      })
                      .then(function (finalCart) {
                        bellaReinstateFrozenCartItems(freeze, addedItemRef, dateDisplay);
                        bellaUpdateDrawerCountAndMoney(finalCart);
                        return bellaEnsureGiftAfterUpsellAdd(getForm(), finalCart).then(function () {
                          return finalCart;
                        });
                      });
                  });
                }
                // Re-assert frozen DOM in case Addons mutated it during fetch.
                bellaReinstateFrozenCartItems(freeze, addedItemRef, dateDisplay);
                return bellaEnsureGiftAfterUpsellAdd(getForm(), afterCart).then(function () {
                  return afterCart;
                });
              });
          });
      })
      .catch(function (e) {
        console.error('[Bella cart] upsell quiet add failed', e);
      })
      .finally(function () {
        doneBtn();
        if (typeof stopFreezeGuard === 'function') stopFreezeGuard();
        // One last reinstate so late Addons mutations don't stick.
        if (addedItemRef) bellaReinstateFrozenCartItems(freeze, addedItemRef, dateDisplay);

        // Unlock cart writes so the shopper can save a delivery date right away.
        bellaCartWriteBlockUntil = 0;
        bellaClearForeignCartWriteBlock();
        bellaSetUpsellAtcActive(false);
        // Brief foreign-only shield (Bella date saves use allowOwn).
        bellaBlockForeignCartWrites(2500);
        armBellaCartMergeLock(1500);

        if (addedItemRef) {
          bellaEnsureGiftAfterUpsellAdd(getForm());
          bellaPromptUpsellDateIfNeeded(getForm(), dateDisplay);
        }

        setTimeout(function () {
          bellaUpsellCartSnapshot = null;
          bellaPreorderSnapshot = null;
          bellaUpsellExpectedMinLines = 0;
          bellaUpsellVariantId = null;
          var f = getForm();
          if (f) delete f.dataset.bellaPreservePreorder;
        }, 4000);
      });
  }

  /** Section-rendering refresh after ATC when add.js omitted sections. */
  function bellaRefreshCartDrawerSections() {
    var sid = getCartDrawerSectionId();
    var url =
      window.location.pathname +
      (window.location.search ? window.location.search + '&' : '?') +
      'sections=' +
      encodeURIComponent([sid, 'cart-icon-bubble', 'mobile-cart-icon-bubble'].join(','));
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        if (!sections) return;
        var parsed = { sections: sections };
        // Quiet merge only — never miniCart.renderContents (that re-opens the drawer).
        if (bellaQuietMergeCartFromSections(parsed)) {
          pruneUpsellProductsInCart(getMiniCartHost());
          return;
        }
        // Fallback: patch #mini-cart HTML without calling open().
        // Never apply during upsell restore (avoids empty flash).
        if (bellaUpsellAtcInFlight || bellaUpsellRebuildInFlight) return;
        var host = getMiniCartHost();
        var html = sections[sid];
        if (host && html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var sectionEl = doc.querySelector('.shopify-section') || doc.body;
          // Refuse empty section while we still have a restore snapshot.
          if (
            bellaUpsellCartSnapshot &&
            bellaUpsellCartSnapshot.length &&
            sectionEl &&
            !sectionEl.querySelector('cart-items li[data-line], cart-items li[id^="CartItem-"]')
          ) {
            return;
          }
          if (sectionEl) {
            withBellaQuietDom(function () {
              host.innerHTML = sectionEl.innerHTML;
            });
          }
        }
        updateCartIconSections(parsed);
        pruneUpsellProductsInCart(getMiniCartHost());
        // Section HTML replace can drop the loader node — put it back if still restoring.
        if (bellaUpsellAtcInFlight && bellaCartGlobalLoaderDepth > 0) {
          bellaEnsureCartGlobalLoaderEl();
          const loader = getMiniCartHost() && getMiniCartHost().querySelector(':scope > .bella-cart-global-loader');
          if (loader) {
            loader.removeAttribute('hidden');
            loader.setAttribute('aria-hidden', 'false');
          }
        }
      })
      .catch(function (e) {
        console.error('[Bella cart] section refresh failed', e);
      });
  }

  /** If drawer lines don't match cart.js after upsell, refresh section HTML once. */
  let bellaVerifyUpsellInFlight = false;
  function bellaVerifyCartAfterUpsellAdd() {
    if (bellaVerifyUpsellInFlight) return Promise.resolve();
    bellaVerifyUpsellInFlight = true;
    var cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    return fetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        var form = getForm();
        if (!form) return;
        var visible = form.querySelectorAll(
          'cart-items li[data-line], cart-items li[id^="CartItem-"]'
        ).length;
        // Hidden addon lines may differ; compare against non-hidden cart items.
        var cartVisible = 0;
        (cart.items || []).forEach(function (item) {
          if (!item) return;
          if (item.properties && item.properties._pa_hide_in_cart === 'true') return;
          cartVisible += 1;
        });
        if (cart.item_count < 1) return;
        if (visible < cartVisible) {
          armBellaCartMergeLock(2000);
          return bellaRefreshCartDrawerSections();
        }
      })
      .catch(function () {})
      .finally(function () {
        bellaVerifyUpsellInFlight = false;
      });
  }

  function onMiniCartClick(e) {
    const host = getMiniCartHost();
    if (!host || e.currentTarget !== host) return;

    // Trash handled on window capture (see ensureBellaCartRemoveCapture) so Product Addons cannot race.
    if (e.target.closest('cart-remove-button, .delete-product')) return;

    if (bellaCheckoutSubmitBypass || bellaCheckoutInFlight) return;

    const checkoutBtn = e.target.closest('[data-bella-checkout]');
    if (checkoutBtn && !checkoutBtn.disabled) {
      e.preventDefault();
      e.stopPropagation();
      handleBellaCheckout(checkoutBtn);
      return;
    }

    const recsOpen = e.target.closest('[data-bella-recs-variant-open]');
    if (recsOpen) {
      e.preventDefault();
      e.stopPropagation();
      bellaRecsOpen(recsOpen.getAttribute('data-product-handle'), recsOpen.getAttribute('data-product-title'));
      return;
    }

    // Single-variant upsell + — Bella-owned path only (never theme <add-to-cart>).
    const recsQuickAdd = e.target.closest(
      '[data-bella-recs-quick-add], .bella-cart__recs-action add-to-cart, add-to-cart.bella-cart__recs-add'
    );
    if (recsQuickAdd) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      bellaRecsAddToCart(recsQuickAdd.getAttribute('data-variant-id'), recsQuickAdd);
      return;
    }

    const recsPill = e.target.closest('[data-bella-recs-option]');
    if (recsPill) {
      e.preventDefault();
      e.stopPropagation();
      var optName = recsPill.getAttribute('data-option-name');
      var optVal = recsPill.getAttribute('data-option-value');
      if (optName) bellaRecsState.selected[optName] = optVal;
      if (bellaRecsState.product) bellaRecsRenderOptions(bellaRecsState.product);
      return;
    }

    const recsAdd = e.target.closest('[data-bella-recs-add]');
    if (recsAdd) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      var sheetVid = recsAdd.dataset.variantId;
      if (!sheetVid && bellaRecsState.product) {
        var sheetVariant = bellaRecsFindVariant(bellaRecsState.product, bellaRecsState.selected);
        if (sheetVariant) sheetVid = String(sheetVariant.id);
      }
      bellaRecsAddToCart(sheetVid, recsAdd);
      return;
    }

    const form = getForm();
    if (!form) return;
    onBellaDeliveryClick(e, form);
  }

  function handleBellaCheckout(btn) {
    const form = getForm();
    if (!form || !btn || btn.disabled || bellaCheckoutInFlight) return;

    const ctrl = getCtrl(form, false);
    // Dead first click when CTA looks ready but delivery isn't — open the date row instead.
    if (ctrl && typeof ctrl.cartDeliveryReady === 'function' && !ctrl.cartDeliveryReady()) {
      const dateRow = form.querySelector('[data-bella-date-row]');
      if (dateRow) {
        dateRow.hidden = false;
        dateRow.removeAttribute('hidden');
      }
      if (typeof ctrl.openSheet === 'function') ctrl.openSheet('date', {});
      if (typeof ctrl.updateFooterTotals === 'function') ctrl.updateFooterTotals();
      return;
    }

    bellaCheckoutInFlight = true;
    const labelEl = btn.querySelector('[data-bella-checkout-label]');
    const prevLabel = labelEl ? labelEl.textContent : '';
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    // Block drawer re-render from aborting the checkout navigation after line sync.
    armBellaCartMergeLock(10000);

    const wait = ctrl && typeof ctrl.waitForDateSync === 'function' ? ctrl.waitForDateSync() : Promise.resolve();

    wait
      .then(function () {
        return syncVisibleLineDatesToCart(form);
      })
      .then(function () {
        // Write addon dates once before leaving — paint-only would leave checkout stale.
        return syncAddonDatesFromParents(form);
      })
      .then(function () {
        armBellaCartMergeLock(8000);
        bellaCheckoutSubmitBypass = true;
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        if (labelEl) labelEl.textContent = prevLabel || (ctrl && ctrl.strings && ctrl.strings.checkout) || 'Checkout';

        // Native submit with this button as submitter (includes name=checkout).
        // Avoid btn.click() — capture handler + re-render made the first attempt feel dead.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(btn);
        } else {
          var hidden = form.querySelector('input[data-bella-checkout-fallback]');
          if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'checkout';
            hidden.value = '';
            hidden.setAttribute('data-bella-checkout-fallback', '1');
            form.appendChild(hidden);
          }
          form.submit();
        }

        // Keep guards until navigation starts; clear if we stay on-page (validation error).
        setTimeout(function () {
          bellaCheckoutSubmitBypass = false;
          bellaCheckoutInFlight = false;
        }, 1500);
      })
      .catch(function (e) {
        console.error('[Bella cart] checkout sync', e);
        bellaCheckoutSubmitBypass = false;
        bellaCheckoutInFlight = false;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        if (labelEl) labelEl.textContent = prevLabel || (ctrl && ctrl.strings && ctrl.strings.checkout) || 'Checkout';
      });
  }

  function onBellaDeliveryInput(e, root) {
    if (!root || !root.contains(e.target)) return;
    if (e.target.matches('[data-bella-gift-input]')) {
      getCtrl(root, false).syncGiftCharCount();
    }
  }

  function onMiniCartInput(e) {
    const host = getMiniCartHost();
    if (!host || e.currentTarget !== host) return;
    const form = getForm();
    if (!form) return;
    if (
      e.target &&
      (e.target.name === 'updates[]' ||
        e.target.classList.contains('quantity__input') ||
        e.target.closest('quantity-input, gift-quantity'))
    ) {
      bellaMarkUserCartEdit(5000);
    }
    onBellaDeliveryInput(e, form);
  }

  /**
   * Window capture runs before document/app listeners (Product Addons). Own the trash
   * click so Addons cannot turn a parent delete into "remove addon only".
   */
  function onBellaCartRemoveWindowCapture(e) {
    if (!e || !e.target || typeof e.target.closest !== 'function') return;
    const host = getMiniCartHost();
    if (!host) return;
    const removeBtn = e.target.closest('cart-remove-button, .delete-product');
    if (!removeBtn || !host.contains(removeBtn)) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    bellaRemoveCartLine(removeBtn);
  }

  function ensureBellaCartRemoveCapture() {
    if (window.__bellaCartRemoveCaptureBound) return;
    window.__bellaCartRemoveCaptureBound = true;
    window.addEventListener('click', onBellaCartRemoveWindowCapture, true);
  }

  function ensureHostDelegation() {
    ensureBellaCartRemoveCapture();
    const host = getMiniCartHost();
    if (!host || host.dataset.bellaDelegated === '1') return;
    host.dataset.bellaDelegated = '1';
    host.addEventListener('click', onMiniCartClick, true);
    host.addEventListener('input', onMiniCartInput, false);
    host.addEventListener('change', onMiniCartInput, false);
  }

  function initPdpDeliveryRoots() {
    document.querySelectorAll('[data-bella-pdp-delivery]').forEach(function (root) {
      if (root.dataset.bellaDelegated === '1') return;
      root.dataset.bellaDelegated = '1';
      root.addEventListener(
        'click',
        function (e) {
          onBellaDeliveryClick(e, root);
        },
        true
      );
      root.addEventListener(
        'input',
        function (e) {
          onBellaDeliveryInput(e, root);
        },
        false
      );
      getCtrl(root, true);
    });
  }

  function refreshBellaForm() {
    const form = getForm();
    if (bellaUpsellAtcInFlight) {
      pruneUpsellProductsInCart(getMiniCartHost());
      return;
    }
    if (form && (isBellaDateSaveInFlight(form) || isBellaCartMergeLocked())) {
      // Soft update only — full reconcile/clear mid-save/merge blinks the date away.
      const ctrl = formControllers.get(form);
      if (ctrl) ctrl.updateFooterTotals({ preserveDeliveryPickers: true });
      pruneUpsellProductsInCart(getMiniCartHost());
      return;
    }
    if (form) {
      getCtrl(form, true);
      const hasLines = !!form.querySelector(
        'cart-items li[data-line], cart-items li[id^="CartItem-"], .bella-cart__items li'
      );
      if (form.classList.contains('is-empty') && !hasLines) {
        clearBellaCartDeliveryState().catch(function () {});
      } else if (hasLines || !form.classList.contains('is-empty')) {
        reconcileStaleBellaDeliveryState(form)
          .then(function () {
            return reconcileLineDatesAfterCartChange(getForm());
          })
          .then(function () {
            paintCartLineGiftNotes(getForm());
          })
          .catch(function () {});
      }
    }
    initPdpDeliveryRoots();
    pruneUpsellProductsInCart(getMiniCartHost());
  }

  function boot() {
    ensureBellaCartRemoveCapture();
    ensureHostDelegation();
    // Before PDP / card ATC: snapshot preorder+addon lines so a normal add cannot wipe them.
    // Arm on pointerdown WITHOUT preventDefault — intercepting submit/click forced 2–3 ATC attempts.
    if (!document.documentElement.dataset.bellaPdpAtcArmed) {
      document.documentElement.dataset.bellaPdpAtcArmed = '1';
      var armPreserveSoon = function (cartForm) {
        if (!cartForm) return;
        if (!hasPreorderLineEl(cartForm) && (cartForm.dataset.bellaCartMode || '') !== 'preorder-only') {
          return;
        }
        armPreorderPreserveForAtc(cartForm).catch(function () {});
      };
      document.addEventListener(
        'pointerdown',
        function (e) {
          if (!e || !e.target || typeof e.target.closest !== 'function') return;
          var btn = e.target.closest(
            'add-to-cart, [name="add"], .product-form__submit, button[type="submit"][name="add"]'
          );
          if (!btn) return;
          if (btn.closest('.bella-cart__recs-action') || btn.hasAttribute('data-bella-recs-quick-add')) return;
          armPreserveSoon(getForm());
        },
        true
      );
      document.addEventListener(
        'submit',
        function (e) {
          var form = e.target;
          if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
          armPreserveSoon(getForm());
        },
        true
      );
    }
    clearBellaCartAttributesOnReload()
      .catch(function () {})
      .finally(function () {
        refreshBellaForm();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /**
   * After PDP/upsell ATC section merge: keep cart date + stamp every normal line.
   * Exposed for cart-drawer.js quietMerge / renderContents.
   */
  let bellaEnsureDateInFlight = null;
  function bellaEnsureCartDateAfterMerge(opts) {
    opts = opts || {};
    const form = getForm();
    if (!form || form.classList.contains('is-empty')) return Promise.resolve(false);
    armBellaCartMergeLock(opts.lockMs || 1500);

    const applyLive = function () {
      const live = getForm();
      if (!live || live.dataset.bellaDateSource !== 'cart') return false;
      let display = String(live.dataset.deliveryDateDisplay || '').trim();
      if (!display && live.dataset.deliveryDate) {
        const parts = formatAdminOrderDateParts(live.dataset.deliveryDate);
        display = parts.date || '';
        if (display) live.dataset.deliveryDateDisplay = display;
      }
      if (!display) return false;
      // Once cart date exists, newly added normal products inherit it (all modes).
      paintCartLineDeliveryDates(live, display, { replaceExisting: true });
      syncAddonDatePaintFromParents(live, { dateDisplay: display });
      const ctrl = formControllers.get(live) || getCtrl(live, false);
      if (ctrl) {
        ctrl.readConfig();
        ctrl.updateFooterTotals({ preserveDeliveryPickers: false });
      }
      return true;
    };

    if (applyLive()) return Promise.resolve(true);
    if (bellaEnsureDateInFlight) return bellaEnsureDateInFlight;

    // Form lost date (full Liquid replace) — recover from cart attributes (once).
    // Never recover after the shopper emptied the cart (stale attrs would auto-select date).
    if (bellaIsUserCartEdit() || bellaDeliveryWasJustCleared()) return Promise.resolve(false);
    const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
    bellaEnsureDateInFlight = fetch(cartUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        const live = getForm();
        if (!live || !cart || !cart.attributes) return false;
        if (typeof cart.item_count === 'number' && cart.item_count === 0) {
          return clearBellaCartDeliveryState({ force: true }).then(function () {
            return false;
          });
        }
        const attrs = cart.attributes;
        if (String(attrs['bella-date-source'] || '') !== 'cart') return false;
        const iso = String(attrs['delivery-date'] || '').trim();
        let display = String(attrs['Delivery Date'] || '').trim();
        if (!iso && !display) return false;
        // Only recover when at least one standard line already has / needs a live cart date.
        if (!cartHasVisibleLineDate(live) && !allStandardLinesHaveDates(live)) {
          // Fresh add after empty cart: attrs may still be stale — clear, don't paint.
          return clearBellaCartDeliveryState({ force: true }).then(function () {
            return false;
          });
        }
        if (!iso && display) {
          const parsed = parseAdminDeliveryDate(display);
          if (parsed) live.dataset.deliveryDate = parsed;
        } else {
          live.dataset.deliveryDate = iso;
        }
        if (!display && live.dataset.deliveryDate) {
          display = formatAdminOrderDateParts(live.dataset.deliveryDate).date || '';
        }
        live.dataset.deliveryDateDisplay = display;
        live.dataset.bellaDateSource = 'cart';
        return applyLive();
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        bellaEnsureDateInFlight = null;
      });

    return bellaEnsureDateInFlight;
  }

  window.BellaCartEnsureDateAfterMerge = bellaEnsureCartDateAfterMerge;
  window.BellaCartQuietMergeFromSections = bellaQuietMergeCartFromSections;
  window.BellaCartUpsellBusy = function () {
    return !!(bellaUpsellAtcInFlight || bellaUpsellRebuildInFlight);
  };

  let bellaCartDrawerOpenedTimer = null;
  document.addEventListener('cartdrawer:opened', function () {
    // Debounce — Product Addons + theme both fire this; stacking caused ensure/refresh loops.
    clearTimeout(bellaCartDrawerOpenedTimer);
    bellaCartDrawerOpenedTimer = setTimeout(function () {
      const form = getForm();
      // Don't re-arm restore snapshots right after a shopper delete/qty edit.
      if (bellaIsUserCartEdit()) {
        const formEdit = getForm();
        const emptyEdit =
          !formEdit ||
          formEdit.classList.contains('is-empty') ||
          !formEdit.querySelector('cart-items li[data-line], cart-items li[id^="CartItem-"]');
        if (emptyEdit) {
          maybeClearBellaStateIfCartEmpty({ item_count: 0 }).catch(function () {});
          return;
        }
        refreshBellaForm();
        return;
      }
      // Keep a fresh preorder snapshot while cart is preorder-only (before normal ATC).
      if (form && (cartLooksPreorderOnly(form) || hasPreorderLineEl(form))) {
        armPreorderPreserveForAtc(form).catch(function () {});
      }
      if (isBellaCartMergeLocked() || isBellaDateSaveInFlight(getForm())) {
        bellaEnsureCartDateAfterMerge({ lockMs: 800 }).catch(function () {});
        return;
      }
      bellaEnsureCartDateAfterMerge({ lockMs: 1200 })
        .then(function () {
          refreshBellaForm();
        })
        .catch(function () {
          refreshBellaForm();
        });
    }, 120);
  });

  let bellaCartUpdatedTimer = null;
  document.addEventListener('cart:updated', function (e) {
    const form = getForm();
    // Shopper delete / qty change — never restore old snapshots.
    if (bellaIsUserCartEdit()) {
      maybeClearBellaStateIfCartEmpty(e && e.detail && (e.detail.cart || e.detail)).then(function (cleared) {
        if (!cleared) refreshBellaForm();
      });
      return;
    }

    const detail = e && e.detail;
    const cart = detail && (detail.cart || detail);
    const hasUpsellSnap = !!(bellaUpsellCartSnapshot && bellaUpsellCartSnapshot.length);

    // CRITICAL: during upsell ATC, Product Addons fires cart:updated AFTER we restore.
    // Returning early here missed the wipe and left only the upsell in the drawer.
    if (bellaUpsellAtcInFlight || bellaUpsellRebuildInFlight) {
      if (!hasUpsellSnap) return;
      clearTimeout(bellaCartUpdatedTimer);
      bellaCartUpdatedTimer = setTimeout(function () {
        bellaUpsellGuardRestore({ cart: cart, paintIfRestored: false }).catch(function () {});
      }, 120);
      return;
    }

    if (form && (isBellaDateSaveInFlight(form) || (isBellaCartMergeLocked() && !hasUpsellSnap))) return;
    if (Date.now() < bellaClearDeliveryCooldownUntil) return;

    clearTimeout(bellaCartUpdatedTimer);
    bellaCartUpdatedTimer = setTimeout(function () {
      if (isBellaDateSaveInFlight(getForm())) return;
      if (isBellaCartMergeLocked() && !(bellaUpsellCartSnapshot && bellaUpsellCartSnapshot.length)) return;
      var snap = bellaUpsellCartSnapshot;
      if (snap && snap.length) {
        bellaUpsellGuardRestore({ cart: cart, paintIfRestored: false })
          .then(function (did) {
            if (did) return;
            if (bellaPreorderSnapshot && bellaPreorderSnapshot.length) {
              return bellaRestoreWipedPreorderLines();
            }
            return false;
          })
          .then(function (did) {
            if (did) return;
            maybeClearBellaStateIfCartEmpty(cart).then(function (cleared) {
              if (!cleared) refreshBellaForm();
            });
          })
          .catch(function () {});
        return;
      }
      if (bellaPreorderSnapshot && bellaPreorderSnapshot.length) {
        bellaRestoreWipedPreorderLines()
          .then(function (restored) {
            if (restored) return;
            maybeClearBellaStateIfCartEmpty(cart).then(function (cleared) {
              if (!cleared) refreshBellaForm();
            });
          })
          .catch(function () {});
        return;
      }
      maybeClearBellaStateIfCartEmpty(cart).then(function (cleared) {
        if (!cleared) refreshBellaForm();
      });
    }, 350);
  });

  let bellaAjaxAddedTimer = null;
  document.addEventListener('ajaxProduct:added', function () {
    // Upsell quiet-append owns this flow — never refresh sections here.
    if (bellaUpsellAtcInFlight) return;
    const form = getForm();
    if (!form || isBellaDateSaveInFlight(form)) return;
    clearTimeout(bellaAjaxAddedTimer);
    bellaAjaxAddedTimer = setTimeout(function () {
      // If cart had / has preorder lines, block writes and restore if a normal ATC wiped them.
      if (
        form.dataset.bellaPreservePreorder === '1' ||
        hasPreorderLineEl(form) ||
        bellaPreorderSnapshot ||
        (bellaUpsellCartSnapshot && bellaUpsellCartSnapshot.length)
      ) {
        blockBellaCartWrites(5000);
        var snap = bellaUpsellCartSnapshot;
        var restorePromise = snap && snap.length
          ? bellaRestoreMissingSnapshotLines(snap, { blockMs: 5000 })
          : bellaRestoreWipedPreorderLines();
        restorePromise
          .then(function (restored) {
            // No section refresh — that reintroduces Addons wipe / empty flash.
            if (restored) bellaUpdateDrawerCountAndMoney(null);
          })
          .catch(function () {});
      }
      bellaEnsureCartDateAfterMerge({ lockMs: 2000 })
        .then(function (ok) {
          if (ok) return syncAddonDatesFromParents(getForm(), { paintOnly: true });
          if (!isBellaCartMergeLocked() && !bellaCartWritesBlocked()) {
            return clearInheritedStandardLineDates(getForm(), { paintOnly: true });
          }
        })
        .catch(function () {});
    }, 250);
  });

  const host = getMiniCartHost();
  if (host) {
    let moTimer;
    const mo = new MutationObserver(() => {
      if (bellaQuietDomDepth > 0) return;
      if (bellaUpsellAtcInFlight) return;
      if (isBellaCartMergeLocked() || isBellaDateSaveInFlight(getForm())) return;
      clearTimeout(moTimer);
      // Longer debounce — Product Addons mutates the drawer often; 30ms refreshed into a loop.
      moTimer = setTimeout(function () {
        if (bellaQuietDomDepth > 0) return;
        if (isBellaCartMergeLocked() || isBellaDateSaveInFlight(getForm())) return;
        refreshBellaForm();
      }, 250);
    });
    mo.observe(host, { childList: true, subtree: false });
  }

  // Remove the checkmark raw symbol (✓ or ✔) from the app's free shipping message in a translation-safe way
  (function() {
    const checkmarkObserver = new MutationObserver(() => {
      const lead = document.querySelector('.decoded-llp-free-shipping__unlocked-lead');
      if (lead && (lead.textContent.includes('✓') || lead.textContent.includes('✔'))) {
        checkmarkObserver.disconnect();
        lead.textContent = lead.textContent.replace(/[✓✔]/g, '').trim();
        checkmarkObserver.observe(document.body, { childList: true, subtree: true });
      }
    });
    checkmarkObserver.observe(document.body, { childList: true, subtree: true });
  })();

  // Fix the sticky media gallery position when the app's custom cart drawer is open (body is fixed)
  (function() {
    function updateGalleryPosition() {
      const isAppDrawerOpen = document.body.classList.contains('decoded-llp-drawer-open');
      const gallery = document.querySelector('.product__media-gallery');
      if (gallery) {
        if (isAppDrawerOpen) {
          const wrapper = gallery.closest('.product__media-wrapper');
          if (wrapper) {
            requestAnimationFrame(() => {
              if (!document.body.classList.contains('decoded-llp-drawer-open')) return;
              const wrapperRect = wrapper.getBoundingClientRect();
              const maxOffset = Math.max(0, wrapper.clientHeight - gallery.clientHeight);
              const offset = Math.min(maxOffset, Math.max(0, -wrapperRect.top));
              gallery.style.transform = 'translateY(' + offset + 'px)';
            });
          }
        } else {
          gallery.style.transform = '';
        }
      }
    }

    const galleryObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateGalleryPosition();
        }
      });
    });

    galleryObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('resize', updateGalleryPosition);
  })();
})();
